// Lanzadores de las herramientas de desarrollo (editores offline) accesibles desde la barra
// de dev-tools de la pantalla de login. Extraído de AuthUI.ts (era un god-object): estos
// editores no tienen relación con el flujo de autenticación y sólo dependen del contentLayer
// del host y de poder volver a la pantalla de login al salir.
import Phaser from 'phaser';
import type {
  MapData,
  MapNPCData,
  MapLightData,
  MapDecoObjectData,
  MapTransladoData,
  MapAreaData,
  AreaRespawnZone,
  MapRect,
  MapFishingSpot,
} from '@shared/types/map';
import { prepareMapForSave, loadMapWithBorders } from '@shared/maps/mapUtils';
import { FISH_SPECIES } from '@shared/data/fish';
import { isDecorationKey, getDecorationAssetUrl } from '../../util/decorationAssets';
import { loadMap1Raw } from '../../boot/mapData';
import { TILE_SIZE, VIEW_TILES_X, VIEW_TILES_Y, getAssetUrl } from '@shared/constants';
import {
  CHARACTER_CLASSES,
  CHARACTER_RACES,
  type CharacterRaceId as PassiveRaceId,
} from '@shared/types/character';
import { createCheckbox, createSlider } from '../formControls';
import { SOUNDS } from '@shared/data/sounds';
import { skinScrollable } from '../customScrollbar';
import { MapEditorScene } from '../../scenes/MapEditorScene';
import { PassiveTreeEditorScene } from '../../scenes/PassiveTreeEditorScene';
import { SurfaceIndexerScene } from '../../scenes/SurfaceIndexerScene';
import { WorldBuilderView } from '../../scenes/worldBuilder/WorldBuilderView';
import { HouseCollisionView } from '../../scenes/houseEditor/HouseCollisionView';
import { ParticleEditorScene, type EditorAnchorMode } from '../../scenes/ParticleEditorScene';
import { PARTICLE_PRESETS } from '../../scenes/particleEditor/presets';
import { listFx, saveFx, deleteFx } from '../../scenes/particleEditor/catalogClient';
import { v1ToV2, v2ToV1 } from '../../scenes/particleEditor/v1v2Convert';
import {
  createDefaultEffectDef,
  PARTICLE_FRAME_NAMES,
  type ParticleEffectDef,
} from '../../systems/ParticleSystem';
import type { SurfaceDefinition, SurfaceIndex } from '@shared/types/surface';
import {
  SpriteFrameEditorScene,
  type FrameAdjustment,
  type SpriteSheetConfig,
  type CanvasCropInfo,
} from '../../scenes/SpriteFrameEditorScene';
import type {
  PassiveTreeData,
  PassiveNode,
  PassiveNodeType,
  PassiveStatType,
  PassiveStatModifier,
} from '@shared/types/passiveTree';
import { EditorDock } from '../editor/EditorDock';

/**
 * Contrato mínimo que DevToolsLauncher necesita de su host (AuthUI): el contenedor DOM donde
 * montar cada editor y la forma de volver a la pantalla de login al salir del editor.
 */
export interface DevToolsHost {
  readonly contentLayer: HTMLDivElement;
  hasLoginCallback(): boolean;
  returnToLogin(): void;
}

export class DevToolsLauncher {
  constructor(private readonly host: DevToolsHost) {}

  openWorldBuilder(): void {
    if (!this.host.hasLoginCallback()) {
      return;
    }
    this.host.contentLayer.innerHTML = '';
    new WorldBuilderView({
      host: this.host.contentLayer,
      onExit: () => {
        this.host.returnToLogin();
      },
    });
  }

  /**
   * Abre el editor de "Bloqueos de casas" (modo offline): muestra cada casa indexada
   * armada y permite pintar por tile su colisión, guardándola en el sidecar
   * `shared/world/houseCollision.json` vía el endpoint DEV `__dev/houses`.
   * Ver client/src/scenes/houseEditor/HouseCollisionView.ts.
   */
  openHouseCollisionEditor(): void {
    if (!this.host.hasLoginCallback()) {
      return;
    }
    this.host.contentLayer.innerHTML = '';
    new HouseCollisionView({
      host: this.host.contentLayer,
      onExit: () => {
        this.host.returnToLogin();
      },
    });
  }

  /**
   * Abre el Editor de Mapas (modo offline, sin conexión al servidor).
   * Permite cargar un mapa, seleccionar tiles y agregar NPCs / items,
   * generando un JSON para pegar en el archivo de mapa.
   */
  openMapEditor(): void {
    // Debe existir el callback original de login para poder volver
    if (!this.host.hasLoginCallback()) {
      return;
    }

    this.host.contentLayer.innerHTML = '';

    // Contenedor principal del editor (pantalla completa, como el juego)
    const container = document.createElement('div');
    container.style.background = '#222';
    container.style.padding = '0';
    container.style.borderRadius = '0';
    container.style.width = '100vw';
    container.style.height = '100vh';
    container.style.display = 'flex';
    container.style.flexDirection = 'column';
    container.style.position = 'fixed';
    container.style.inset = '0';
    container.style.overflow = 'hidden';
    container.style.color = '#eee';

    this.host.contentLayer.appendChild(container);

    // Contenedor donde vivirá el canvas de Phaser del editor de mapas
    const phaserContainer = document.createElement('div');
    phaserContainer.id = 'map-editor-phaser-container';
    phaserContainer.style.position = 'absolute';
    phaserContainer.style.inset = '0';
    phaserContainer.style.zIndex = '0';
    // Mismo tamaño que el juego para mantener la misma escala
    phaserContainer.style.width = '1280px';
    phaserContainer.style.height = '960px';
    phaserContainer.style.left = '50%';
    phaserContainer.style.top = '50%';
    phaserContainer.style.transform = 'translate(-50%, -50%)';
    container.appendChild(phaserContainer);

    // Dock lateral colapsable que aloja las secciones NPCs/Items, Áreas y Superficies.
    // Cada panel se registra más abajo con editorDock.addSection(...).
    const editorDock = new EditorDock(container);

    // Estado básico del editor
    type MapItemPlacement = { itemId: string; x: number; y: number; quantity: number };

    // Mapa actual (se carga sólo cuando el usuario selecciona uno)
    let currentMap: MapData | null = null;
    let npcPlacements: MapNPCData[] = [];
    let itemPlacements: MapItemPlacement[] = [];
    let lightPlacements: MapLightData[] = [];
    let decoObjectPlacements: MapDecoObjectData[] = [];
    let transladoPlacements: MapTransladoData[] = [];
    let areaPlacements: MapAreaData[] = [];
    let fishingSpotPlacements: MapFishingSpot[] = [];
    let selectedFishSpotId: string | null = null;
    let fishingDrawActive = false;
    // La setea la sección "Cardúmenes" para completar el dibujo del área (stop + refresh).
    let onFishingRectDone: (() => void) | null = null;
    let selectedTile = { x: 0, y: 0 };
    let editorGame: Phaser.Game | null = null;

    // Pre-llenado del formulario de translados al seleccionar un tile. Placeholder no-op
    // hasta que se construyen los inputs de la sección Translados (evita TDZ, porque
    // refreshSelectedTileLabel() se invoca antes de que esos inputs existan).
    let refreshTransladoForm: () => void = () => {};

    const npcTemplates: { id: string; name: string }[] = [
      { id: 'npc_001', name: 'Guardián del Pueblo' },
      { id: 'npc_002', name: 'Comerciante' },
      { id: 'npc_003', name: 'Mentor' },
      { id: 'npc_004', name: 'NPC de Pruebas' },
    ];

    const itemTemplates: { id: string; name: string }[] = [
      { id: 'weapon_sword_001', name: 'Espada de Hierro' },
      { id: 'armor_001', name: 'Armadura de Cuero' },
      { id: 'boots_001', name: 'Botas de Cuero' },
      { id: 'shield_001', name: 'Mizarodera' },
    ];

    // Función para construir el JSON del mapa (definida temprano para usarla en el botón)
    const buildMapJson = (): string => {
      // Si aún no hay mapa seleccionado, generamos un JSON mínimo
      if (!currentMap) {
        return JSON.stringify(
          {
            npcs: npcPlacements,
            items: itemPlacements,
            lights: lightPlacements,
          },
          null,
          2
        );
      }

      // Preparar el mapa para guardar (remueve los bordes generados automáticamente)
      const cleanMap = prepareMapForSave(currentMap);
      const data: MapData = {
        ...cleanMap,
        npcs: npcPlacements,
        lights: lightPlacements.length > 0 ? lightPlacements : undefined,
        decoObjects: decoObjectPlacements.length > 0 ? decoObjectPlacements : undefined,
        translados: transladoPlacements.length > 0 ? transladoPlacements : undefined,
        areas: areaPlacements.length > 0 ? areaPlacements : undefined,
        fishingSpots: fishingSpotPlacements.length > 0 ? fishingSpotPlacements : undefined,
      };
      return JSON.stringify(data, null, 2);
    };

    // ---- Botones en la esquina superior derecha ----
    const buttonsContainer = document.createElement('div');
    buttonsContainer.style.position = 'absolute';
    buttonsContainer.style.top = '16px';
    buttonsContainer.style.right = '16px';
    buttonsContainer.style.zIndex = '10';
    buttonsContainer.style.display = 'flex';
    buttonsContainer.style.gap = '8px';
    
    const backButton = document.createElement('button');
    backButton.textContent = 'Volver al login';
    backButton.style.cursor = 'pointer';
    backButton.style.padding = '6px 12px';
    backButton.onclick = () => {
      // Destruir Phaser antes de volver
      if (editorGame) {
        editorGame.destroy(true);
        editorGame = null;
      }
      MapEditorScene.onTileSelected = null;
      MapEditorScene.onSurfaceDeselected = null;
      MapEditorScene.onAreaRectDrawn = null;
      // Volver a la pantalla de login / registro
      this.host.returnToLogin();
    };
    
    const copyJsonButton = document.createElement('button');
    copyJsonButton.innerHTML = '<strong>Copiar JSON</strong>';
    copyJsonButton.style.cursor = 'pointer';
    copyJsonButton.style.padding = '6px 12px';
    copyJsonButton.onclick = async () => {
      const json = buildMapJson();
      try {
        await navigator.clipboard.writeText(json);
        // Feedback visual opcional
        const originalText = copyJsonButton.innerHTML;
        copyJsonButton.innerHTML = '<strong>¡Copiado!</strong>';
        setTimeout(() => {
          copyJsonButton.innerHTML = originalText;
        }, 1000);
      } catch {
        // Ignorar errores de clipboard (por permisos)
      }
    };
    
    buttonsContainer.appendChild(copyJsonButton);
    buttonsContainer.appendChild(backButton);
    container.appendChild(buttonsContainer);

    // ---- Selección de mapa (ventana flotante ligera en la esquina superior izquierda) ----
    const mapRow = document.createElement('div');
    mapRow.style.position = 'absolute';
    mapRow.style.top = '16px';
    mapRow.style.left = '16px';
    mapRow.style.background = 'rgba(0,0,0,0.6)';
    mapRow.style.padding = '6px 8px';
    mapRow.style.borderRadius = '4px';
    mapRow.style.display = 'flex';
    mapRow.style.alignItems = 'center';
    mapRow.style.gap = '8px';
    mapRow.style.zIndex = '10';

    const mapLabel = document.createElement('span');
    mapLabel.textContent = 'Mapa:';

    const mapSelect = document.createElement('select');

    // Opción placeholder: obliga a elegir un mapa antes de editar
    const placeholderOption = document.createElement('option');
    placeholderOption.value = '';
    placeholderOption.textContent = 'Selecciona un mapa...';
    mapSelect.appendChild(placeholderOption);

    // Por ahora sólo hay map1, pero dejamos la estructura lista
    const option1 = document.createElement('option');
    option1.value = 'map1';
    option1.textContent = 'map1';
    mapSelect.appendChild(option1);
    mapSelect.value = '';

    // Prevenir que el select capture las flechas (para que funcionen en el editor)
    mapSelect.onkeydown = (e: KeyboardEvent) => {
      // Si es una flecha, prevenir el comportamiento por defecto y dejar que Phaser lo maneje
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        e.stopPropagation();
        // Quitar el foco del select para que Phaser pueda capturar las teclas
        mapSelect.blur();
        // Enfocar el canvas de Phaser si existe
        const phaserCanvas = document.querySelector('#map-editor-phaser-container canvas');
        if (phaserCanvas) {
          (phaserCanvas as HTMLElement).focus();
        }
      }
    };

    mapSelect.onchange = async () => {
      const value = mapSelect.value;
      if (!value) {
        // Sin mapa seleccionado - destruir Phaser si existe
        if (editorGame) {
          editorGame.destroy(true);
          editorGame = null;
        }
        MapEditorScene.onTileSelected = null;
        MapEditorScene.onSurfaceDeselected = null;
        MapEditorScene.onAreaRectDrawn = null;
        currentMap = null;
        npcPlacements = [];
        itemPlacements = [];
        lightPlacements = [];
        decoObjectPlacements = [];
        transladoPlacements = [];
        areaPlacements = [];
        fishingSpotPlacements = [];
        selectedFishSpotId = null;
        selectedTile = { x: 0, y: 0 };
        refreshSelectedTileLabel();
        refreshPlacementsView();
        refreshTileInfo();
        return;
      }

      if (value === 'map1') {
        // Forzar re-horneado del terreno del minimapa al cambiar de mapa
        MapEditorScene.minimapDirty = true;

        // map1.json se fetchea en runtime (ya no se bundlea); memoizado → instantáneo tras el boot.
        const map1Raw = await loadMap1Raw();
        currentMap = JSON.parse(JSON.stringify(loadMapWithBorders(map1Raw))) as MapData;
        npcPlacements = currentMap.npcs ? [...currentMap.npcs] : [];
        itemPlacements = [];
        lightPlacements = currentMap.lights ? [...currentMap.lights] : [];
        decoObjectPlacements = currentMap.decoObjects ? [...currentMap.decoObjects] : [];
        transladoPlacements = currentMap.translados ? [...currentMap.translados] : [];
        areaPlacements = currentMap.areas ? JSON.parse(JSON.stringify(currentMap.areas)) : [];
        fishingSpotPlacements = currentMap.fishingSpots
          ? JSON.parse(JSON.stringify(currentMap.fishingSpots))
          : [];
        selectedTile = { x: 50, y: 50 };
        refreshSelectedTileLabel();
        refreshPlacementsView();
        refreshTileInfo();
        updateToggleBlockButton();

        // Conectar callback de selección de tile
        MapEditorScene.onTileSelected = (x: number, y: number) => {
          selectedTile = { x, y };
          refreshSelectedTileLabel();
          refreshPlacementsView();
          refreshTileInfo();
        };
        
        // Conectar callback de deselección de superficie (click derecho o ESC en la escena)
        MapEditorScene.onSurfaceDeselected = () => {
          selectedSurfaceId = null;
          selectedSurfaceInfo.textContent = 'Ninguna superficie seleccionada';
          selectedSurfaceInfo.style.color = '#888';
          placeSurfaceButton.disabled = true;
          placeSurfaceButton.style.opacity = '0.5';
          
          // Limpiar preview de imagen
          clearSurfacePreviewImage();
          
          // Deseleccionar visualmente en el listado
          surfaceListContainer.querySelectorAll('.surface-selected').forEach(el => {
            (el as HTMLElement).classList.remove('surface-selected');
            (el as HTMLElement).style.background = '#222';
            (el as HTMLElement).style.borderColor = '#333';
          });
        };

        // Destruir Phaser anterior si existe para recargar assets correctamente
        if (editorGame) {
          editorGame.destroy(true);
          editorGame = null;
        }

        // Actualizar la propiedad estática ANTES de crear el juego
        // para que preload() cargue los assets correctos
        MapEditorScene.currentMapData = currentMap;

        // Crear Phaser.Game para el editor con el mapData correcto
        const editorConfig: Phaser.Types.Core.GameConfig = {
          type: Phaser.AUTO,
          parent: 'map-editor-phaser-container',
          backgroundColor: '#1b1b1b',
          scale: {
            mode: Phaser.Scale.FIT,
            width: VIEW_TILES_X * TILE_SIZE,
            height: VIEW_TILES_Y * TILE_SIZE,
            autoCenter: Phaser.Scale.CENTER_BOTH,
          },
          render: {
            pixelArt: true, // Renderizado nítido para pixel art
            antialias: false, // Sin antialiasing para mejor nitidez
            roundPixels: true,
          },
          physics: {
            default: 'arcade',
          },
          disableContextMenu: true,
          scene: [MapEditorScene],
        };

        editorGame = new Phaser.Game(editorConfig);
        // Iniciar la escena (el mapData ya está en la propiedad estática)
        editorGame.scene.start('MapEditorScene');
        
        // Esperar a que la escena esté lista y luego actualizar NPCs e Items y toggles
        setTimeout(() => {
          const scene = editorGame?.scene.getScene('MapEditorScene') as MapEditorScene;
          if (scene) {
            scene.updateNPCsAndItems(npcPlacements, itemPlacements);
            initializeToggles();
            refreshMinimap();
            // Actualizar lista de superficies cuando la escena esté lista
            refreshSurfaceList();
          }
        }, 500);
      }
    };

    mapRow.append(mapLabel, mapSelect);
    container.appendChild(mapRow);

    // Botón para toggle de bloqueo del tile
    const toggleBlockButton = document.createElement('button');
    toggleBlockButton.style.position = 'absolute';
    toggleBlockButton.style.bottom = '48px';
    toggleBlockButton.style.left = '16px';
    toggleBlockButton.style.width = '40px';
    toggleBlockButton.style.height = '40px';
    toggleBlockButton.style.borderRadius = '4px';
    toggleBlockButton.style.border = 'none';
    toggleBlockButton.style.background = 'rgba(0,0,0,0.6)';
    toggleBlockButton.style.fontSize = '20px';
    toggleBlockButton.style.cursor = 'pointer';
    toggleBlockButton.style.zIndex = '10';
    toggleBlockButton.style.display = 'none'; // Oculto hasta que haya un mapa cargado
    container.appendChild(toggleBlockButton);

    const updateToggleBlockButton = (): void => {
      if (!currentMap) {
        toggleBlockButton.style.display = 'none';
        return;
      }
      toggleBlockButton.style.display = 'block';
      
      // Verificar si el tile está bloqueado (ahora se lee de layers)
      const tileX = selectedTile.x;
      const tileY = selectedTile.y;
      let isBlocked = false;
      
      for (const layer of currentMap.layers) {
        for (const tile of layer.tiles) {
          if (tile.x === tileX && tile.y === tileY && tile.blocked) {
            isBlocked = true;
            break;
          }
        }
        if (isBlocked) break;
      }
      
      // Cambiar emoji según el estado
      toggleBlockButton.textContent = isBlocked ? '🚫' : '✅';
    };

    toggleBlockButton.onclick = () => {
      if (!currentMap || !editorGame) return;
      
      const scene = editorGame.scene.getScene('MapEditorScene') as MapEditorScene;
      if (scene) {
        const newBlockedState = scene.toggleTileBlocked(selectedTile.x, selectedTile.y);
        updateToggleBlockButton();
        refreshTileInfo(); // Actualizar información del tile
      }
    };

    // FPS display - abajo a la izquierda, fuera del canvas de Phaser
    const fpsLabel = document.createElement('div');
    fpsLabel.id = 'map-editor-fps';
    fpsLabel.style.position = 'absolute';
    fpsLabel.style.bottom = '16px';
    fpsLabel.style.left = '16px';
    fpsLabel.style.padding = '4px 8px';
    fpsLabel.style.borderRadius = '4px';
    fpsLabel.style.background = 'rgba(0,0,0,0.6)';
    fpsLabel.style.fontSize = '11px';
    fpsLabel.style.color = '#ffffff';
    fpsLabel.style.fontFamily = 'monospace';
    fpsLabel.style.zIndex = '100'; // Más alto que el phaserContainer (z-index: 0)
    fpsLabel.style.pointerEvents = 'none'; // No interceptar clicks del canvas
    fpsLabel.textContent = '0';
    container.appendChild(fpsLabel);

    // Botones de toggle en la parte inferior derecha
    const toggleButtonsContainer = document.createElement('div');
    toggleButtonsContainer.style.position = 'absolute';
    toggleButtonsContainer.style.bottom = '16px';
    toggleButtonsContainer.style.right = '16px';
    toggleButtonsContainer.style.display = 'flex';
    toggleButtonsContainer.style.flexDirection = 'column';
    toggleButtonsContainer.style.gap = '8px';
    toggleButtonsContainer.style.zIndex = '10';
    container.appendChild(toggleButtonsContainer);

    // Botón de Grilla
    const gridToggleButton = document.createElement('button');
    gridToggleButton.textContent = '❌';
    gridToggleButton.style.border = 'none';
    gridToggleButton.style.background = 'transparent';
    gridToggleButton.style.fontSize = '20px';
    gridToggleButton.style.cursor = 'pointer';
    gridToggleButton.style.padding = '0';
    gridToggleButton.style.margin = '0';
    gridToggleButton.style.display = 'flex';
    gridToggleButton.style.alignItems = 'center';
    gridToggleButton.style.gap = '6px';
    gridToggleButton.style.color = '#eee';

    const gridLabel = document.createElement('span');
    gridLabel.textContent = 'Grilla';
    gridLabel.style.fontSize = '13px';
    gridToggleButton.appendChild(gridLabel);

    let showGrid = false;
    gridToggleButton.onclick = () => {
      showGrid = !showGrid;
      gridToggleButton.textContent = showGrid ? '✅' : '❌';
      gridToggleButton.appendChild(gridLabel);
      if (editorGame) {
        const scene = editorGame.scene.getScene('MapEditorScene') as MapEditorScene;
        if (scene) {
          scene.setShowGrid(showGrid);
        }
      }
    };
    toggleButtonsContainer.appendChild(gridToggleButton);

    // Botón de Bloqueos
    const blockedToggleButton = document.createElement('button');
    blockedToggleButton.textContent = '✅';
    blockedToggleButton.style.border = 'none';
    blockedToggleButton.style.background = 'transparent';
    blockedToggleButton.style.fontSize = '20px';
    blockedToggleButton.style.cursor = 'pointer';
    blockedToggleButton.style.padding = '0';
    blockedToggleButton.style.margin = '0';
    blockedToggleButton.style.display = 'flex';
    blockedToggleButton.style.alignItems = 'center';
    blockedToggleButton.style.gap = '6px';
    blockedToggleButton.style.color = '#eee';

    const blockedLabel = document.createElement('span');
    blockedLabel.textContent = 'Bloqueos';
    blockedLabel.style.fontSize = '13px';
    blockedToggleButton.appendChild(blockedLabel);

    let showBlocked = true;
    blockedToggleButton.onclick = () => {
      showBlocked = !showBlocked;
      blockedToggleButton.textContent = showBlocked ? '✅' : '❌';
      blockedToggleButton.appendChild(blockedLabel);
      if (editorGame) {
        const scene = editorGame.scene.getScene('MapEditorScene') as MapEditorScene;
        if (scene) {
          scene.setShowBlockedTiles(showBlocked);
        }
      }
    };
    toggleButtonsContainer.appendChild(blockedToggleButton);

    // Botón de Bloqueos de Mapa (en la derecha con los otros toggles)
    const mapBordersToggleButton = document.createElement('button');
    mapBordersToggleButton.textContent = '❌';
    mapBordersToggleButton.style.border = 'none';
    mapBordersToggleButton.style.background = 'transparent';
    mapBordersToggleButton.style.fontSize = '20px';
    mapBordersToggleButton.style.cursor = 'pointer';
    mapBordersToggleButton.style.padding = '0';
    mapBordersToggleButton.style.margin = '0';
    mapBordersToggleButton.style.display = 'flex';
    mapBordersToggleButton.style.alignItems = 'center';
    mapBordersToggleButton.style.gap = '6px';
    mapBordersToggleButton.style.color = '#eee';

    const mapBordersLabel = document.createElement('span');
    mapBordersLabel.textContent = 'Bloqueos de mapa';
    mapBordersLabel.style.fontSize = '13px';
    mapBordersToggleButton.appendChild(mapBordersLabel);

    let showMapBorders = false;
    mapBordersToggleButton.onclick = () => {
      showMapBorders = !showMapBorders;
      mapBordersToggleButton.textContent = showMapBorders ? '✅' : '❌';
      mapBordersToggleButton.appendChild(mapBordersLabel);
      if (editorGame) {
        const scene = editorGame.scene.getScene('MapEditorScene') as MapEditorScene;
        if (scene) {
          scene.setShowMapBorders(showMapBorders);
        }
      }
    };
    toggleButtonsContainer.appendChild(mapBordersToggleButton);

    // Inicializar estado de los toggles en la escena cuando se crea
    const initializeToggles = () => {
      if (editorGame) {
        const scene = editorGame.scene.getScene('MapEditorScene') as MapEditorScene;
        if (scene) {
          scene.setShowGrid(showGrid);
          scene.setShowBlockedTiles(showBlocked);
          scene.setShowMapBorders(showMapBorders);
        }
      }
    };

    // ---- Minimap (reconstruido desde cero) ----
    const minimapContainer = document.createElement('div');
    minimapContainer.style.position = 'absolute';
    minimapContainer.style.top = '80px';
    minimapContainer.style.right = '16px';
    minimapContainer.style.width = '300px';
    minimapContainer.style.height = '300px';
    minimapContainer.style.background = 'rgba(0, 0, 0, 0.7)';
    minimapContainer.style.border = '2px solid #555';
    minimapContainer.style.borderRadius = '8px';
    minimapContainer.style.zIndex = '10';
    minimapContainer.style.userSelect = 'none';
    container.appendChild(minimapContainer);

    // Header del minimap (para mover)
    const minimapHeader = document.createElement('div');
    minimapHeader.textContent = 'Minimapa';
    minimapHeader.style.padding = '4px 8px';
    minimapHeader.style.background = 'rgba(0, 0, 0, 0.5)';
    minimapHeader.style.borderBottom = '1px solid #555';
    minimapHeader.style.fontSize = '12px';
    minimapHeader.style.cursor = `url(${getAssetUrl('cursors/drag.cur')}), move`;

    // Canvas del minimap
    const minimapCanvas = document.createElement('canvas');
    minimapCanvas.width = 300;
    minimapCanvas.height = 300;
    minimapCanvas.style.width = '100%';
    minimapCanvas.style.height = 'calc(100% - 25px)';
    minimapCanvas.style.display = 'block';
    minimapCanvas.style.cursor = 'pointer';

    // Contenedor para el canvas (para el padding)
    const minimapCanvasContainer = document.createElement('div');
    minimapCanvasContainer.style.padding = '4px';
    minimapCanvasContainer.style.height = 'calc(100% - 25px)';
    minimapCanvasContainer.appendChild(minimapCanvas);
    minimapContainer.appendChild(minimapHeader);
    minimapContainer.appendChild(minimapCanvasContainer);

    const ctx = minimapCanvas.getContext('2d')!;
    
    // Estado del minimapa
    let isMinimapDragging = false;
    let minimapDragOffset = { x: 0, y: 0 };
    let isMinimapClicking = false;
    
    // Canvas offscreen donde se "hornea" el terreno del minimapa UNA sola vez por
    // cambio de mapa/edición (no en cada frame). Con mundos de ~390k tiles, iterar
    // los tiles por frame congelaba el editor (y bloqueaba la carga de superficies).
    // El frame del minimapa ahora solo blittea este canvas + entidades + viewport.
    const terrainCanvas = document.createElement('canvas');
    terrainCanvas.width = minimapCanvas.width;
    terrainCanvas.height = minimapCanvas.height;
    const terrainCtx = terrainCanvas.getContext('2d')!;
    // Throttle del horneado: aunque el mapa quede "dirty" durante un arrastre de
    // pintado, no re-horneamos más de ~2.5 veces/seg.
    let lastMinimapBakeAt = 0;

    // Color del minimapa por familia de sprite (barato: un fillRect por tile, sin
    // drawImage). Suficiente para un minimapa de 1024² tiles a ~300px.
    const minimapColorForSprite = (sprite: string, layerName: string): string => {
      if (sprite.startsWith('pattern2')) return '#4a7c3a'; // grass
      if (sprite.startsWith('pattern4')) return '#c2b280'; // sand
      if (sprite.startsWith('pattern6')) return '#e3ebf2'; // snow
      if (sprite.startsWith('pattern5')) return '#9b7e4e'; // path
      if (sprite.startsWith('pattern7') || sprite.startsWith('pattern3')) return '#4a4750'; // dungeon1/2 (piedra oscura)
      if (sprite.startsWith('water')) return '#3a6ea5'; // water
      if (layerName === 'roof' || layerName === 'layer4_roof') return '#8a5a3a';
      return '#5a5a5a';
    };

    // Hacer el minimap movible (solo el header)
    minimapHeader.addEventListener('mousedown', (e) => {
      isMinimapDragging = true;
      const rect = minimapContainer.getBoundingClientRect();
      minimapDragOffset.x = e.clientX - rect.left;
      minimapDragOffset.y = e.clientY - rect.top;
      e.preventDefault();
    });

    window.addEventListener('mousemove', (e) => {
      if (isMinimapDragging) {
        const parentRect = container.getBoundingClientRect();
        let newLeft = e.clientX - minimapDragOffset.x - parentRect.left;
        let newTop = e.clientY - minimapDragOffset.y - parentRect.top;
        newLeft = Math.max(0, Math.min(newLeft, parentRect.width - minimapContainer.offsetWidth));
        newTop = Math.max(0, Math.min(newTop, parentRect.height - minimapContainer.offsetHeight));
        minimapContainer.style.left = 'auto';
        minimapContainer.style.right = 'auto';
        minimapContainer.style.top = `${newTop}px`;
        minimapContainer.style.left = `${newLeft}px`;
      }
    });

    window.addEventListener('mouseup', () => {
      isMinimapDragging = false;
      if (isMinimapClicking) {
        isMinimapClicking = false;
      }
    });

    // Función para mover la cámara desde el minimapa
    const moveCameraFromMinimap = (x: number, y: number) => {
      if (!currentMap || !editorGame) return;
      
      const scene = editorGame.scene.getScene('MapEditorScene') as MapEditorScene;
      if (!scene) return;
      
      const cam = scene.cameras.main;
      const canvasWidth = minimapCanvas.width;
      const canvasHeight = minimapCanvas.height;
      const padding = 8;
      const drawWidth = canvasWidth - padding * 2;
      const drawHeight = canvasHeight - padding * 2;
      const mapWidth = (currentMap.bounds.maxX - currentMap.bounds.minX + 1) * TILE_SIZE;
      const mapHeight = (currentMap.bounds.maxY - currentMap.bounds.minY + 1) * TILE_SIZE;
      // Escala FIJA (igual que en renderMinimap)
      const baseScaleX = drawWidth / mapWidth;
      const baseScaleY = drawHeight / mapHeight;
      const baseScale = Math.min(baseScaleX, baseScaleY);
      const scaledWidth = mapWidth * baseScale;
      const scaledHeight = mapHeight * baseScale;
      const offsetX = (canvasWidth - scaledWidth) / 2;
      const offsetY = (canvasHeight - scaledHeight) / 2;
      
      if (x < offsetX || x > offsetX + scaledWidth || y < offsetY || y > offsetY + scaledHeight) {
        return;
      }
      
      const relativeX = (x - offsetX) / baseScale;
      const relativeY = (y - offsetY) / baseScale;
      let worldX = relativeX + currentMap.bounds.minX * TILE_SIZE;
      let worldY = relativeY + currentMap.bounds.minY * TILE_SIZE;
      
      const worldMinX = currentMap.bounds.minX * TILE_SIZE;
      const worldMaxX = (currentMap.bounds.maxX + 1) * TILE_SIZE;
      const worldMinY = currentMap.bounds.minY * TILE_SIZE;
      const worldMaxY = (currentMap.bounds.maxY + 1) * TILE_SIZE;
      const viewHalfWidth = cam.width / (2 * cam.zoom);
      const viewHalfHeight = cam.height / (2 * cam.zoom);
      
      worldX = Math.max(worldMinX + viewHalfWidth, Math.min(worldMaxX - viewHalfWidth, worldX));
      worldY = Math.max(worldMinY + viewHalfHeight, Math.min(worldMaxY - viewHalfHeight, worldY));
      
      cam.centerOn(worldX, worldY);
      
      const tileX = Math.floor(worldX / TILE_SIZE);
      const tileY = Math.floor(worldY / TILE_SIZE);
      const clampedX = Math.max(currentMap.bounds.minX, Math.min(currentMap.bounds.maxX, tileX));
      const clampedY = Math.max(currentMap.bounds.minY, Math.min(currentMap.bounds.maxY, tileY));
      
      selectedTile = { x: clampedX, y: clampedY };
      refreshSelectedTileLabel();
      refreshPlacementsView();
      refreshTileInfo();
      if (MapEditorScene.onTileSelected) {
        MapEditorScene.onTileSelected(clampedX, clampedY);
      }
    };

    // Click y arrastre en el minimapa
    minimapCanvas.addEventListener('mousedown', (e) => {
      if (!currentMap) return;
      const rect = minimapCanvas.getBoundingClientRect();
      // Escalar coordenadas del click al tamaño interno del canvas
      const scaleX = minimapCanvas.width / rect.width;
      const scaleY = minimapCanvas.height / rect.height;
      const x = (e.clientX - rect.left) * scaleX;
      const y = (e.clientY - rect.top) * scaleY;
      
      isMinimapClicking = true;
      moveCameraFromMinimap(x, y);
    });

    minimapCanvas.addEventListener('mousemove', (e) => {
      if (isMinimapClicking) {
        const rect = minimapCanvas.getBoundingClientRect();
        // Escalar coordenadas del click al tamaño interno del canvas
        const scaleX = minimapCanvas.width / rect.width;
        const scaleY = minimapCanvas.height / rect.height;
        const x = (e.clientX - rect.left) * scaleX;
        const y = (e.clientY - rect.top) * scaleY;
        moveCameraFromMinimap(x, y);
      }
    });

    minimapCanvas.addEventListener('mouseup', () => {
      isMinimapClicking = false;
    });

    // Hornea el terreno completo del mapa en el canvas offscreen (1 fillRect por
    // tile, coloreado por familia de sprite). Caro (~390k tiles) pero se llama solo
    // cuando el mapa cambió, no por frame.
    const bakeMinimapTerrain = (
      offsetX: number,
      offsetY: number,
      baseScale: number,
      scaledWidth: number,
      scaledHeight: number
    ): void => {
      terrainCtx.clearRect(0, 0, terrainCanvas.width, terrainCanvas.height);
      terrainCtx.fillStyle = '#2a2a2a';
      terrainCtx.fillRect(offsetX, offsetY, scaledWidth, scaledHeight);
      if (!currentMap) return;

      const size = Math.max(1, TILE_SIZE * baseScale);
      for (const layer of currentMap.layers) {
        // Saltar capas internas (bordes, colisión) - no van en el minimapa.
        if (layer.name.startsWith('_')) continue;
        if (!layer.tiles || layer.tiles.length === 0) continue;

        for (const tile of layer.tiles) {
          if (!tile.sprite) continue;
          const mx = offsetX + (tile.x - currentMap.bounds.minX) * TILE_SIZE * baseScale;
          const my = offsetY + (tile.y - currentMap.bounds.minY) * TILE_SIZE * baseScale;
          // Árboles/decoraciones: verde marcado; superficies: color por bioma.
          terrainCtx.fillStyle = isDecorationKey(tile.sprite)
            ? '#3fae3f'
            : minimapColorForSprite(tile.sprite, layer.name);
          terrainCtx.fillRect(mx, my, size, size);
        }
      }
    };

    // Función para renderizar el minimapa
    const renderMinimap = () => {
      if (!currentMap) return;

      const canvasWidth = minimapCanvas.width;
      const canvasHeight = minimapCanvas.height;
      const padding = 8;
      const drawWidth = canvasWidth - padding * 2;
      const drawHeight = canvasHeight - padding * 2;
      const mapWidth = (currentMap.bounds.maxX - currentMap.bounds.minX + 1) * TILE_SIZE;
      const mapHeight = (currentMap.bounds.maxY - currentMap.bounds.minY + 1) * TILE_SIZE;
      
      // Escala FIJA para que el mapa siempre se vea completo
      const baseScaleX = drawWidth / mapWidth;
      const baseScaleY = drawHeight / mapHeight;
      const baseScale = Math.min(baseScaleX, baseScaleY);
      const scaledWidth = mapWidth * baseScale;
      const scaledHeight = mapHeight * baseScale;
      
      // Offset fijo: mapa centrado en el canvas
      const offsetX = (canvasWidth - scaledWidth) / 2;
      const offsetY = (canvasHeight - scaledHeight) / 2;
      
      // Obtener datos de la cámara para el viewport
      let viewCenterX = 0;
      let viewCenterY = 0;
      let viewWorldWidth = 0;
      let viewWorldHeight = 0;
      
      if (editorGame) {
        const scene = editorGame.scene.getScene('MapEditorScene') as MapEditorScene;
        if (scene) {
          const cam = scene.cameras.main;
          // Usar el punto central de la cámara (midPoint) que es lo que se mantiene fijo al hacer zoom
          viewWorldWidth = cam.width / cam.zoom;
          viewWorldHeight = cam.height / cam.zoom;
          viewCenterX = cam.midPoint.x;
          viewCenterY = cam.midPoint.y;
        }
      }

      // Re-hornear el terreno solo si el mapa cambió (carga/edición), con throttle
      // (~2.5 bakes/seg como techo, aunque se pinte arrastrando). El resto de los
      // frames el minimapa es barato: blit del terreno + entidades + viewport.
      const nowTs = performance.now();
      if (MapEditorScene.minimapDirty && nowTs - lastMinimapBakeAt > 400) {
        bakeMinimapTerrain(offsetX, offsetY, baseScale, scaledWidth, scaledHeight);
        MapEditorScene.minimapDirty = false;
        lastMinimapBakeAt = nowTs;
      }

      // Limpiar canvas y blittear el terreno horneado.
      ctx.clearRect(0, 0, canvasWidth, canvasHeight);
      ctx.fillStyle = '#1b1b1b';
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);
      ctx.drawImage(terrainCanvas, 0, 0);

      // Dibujar NPCs
      const allNPCs = [...(currentMap.npcs || []), ...npcPlacements];
      for (const npc of allNPCs) {
        const x = offsetX + (npc.x - currentMap.bounds.minX + 0.5) * TILE_SIZE * baseScale;
        const y = offsetY + (npc.y - currentMap.bounds.minY + 0.5) * TILE_SIZE * baseScale;
        ctx.fillStyle = '#3fa9f5';
        ctx.beginPath();
        ctx.arc(x, y, 1.5, 0, Math.PI * 2);
        ctx.fill();
      }

      // Dibujar items
      for (const item of itemPlacements) {
        const x = offsetX + (item.x - currentMap.bounds.minX) * TILE_SIZE * baseScale;
        const y = offsetY + (item.y - currentMap.bounds.minY) * TILE_SIZE * baseScale;
        ctx.fillStyle = '#ffcc00';
        ctx.beginPath();
        ctx.arc(x, y, 2, 0, Math.PI * 2);
        ctx.fill();
      }

      // Dibujar área visible usando el centro del viewport (midPoint de Phaser)
      if (editorGame && viewWorldWidth > 0 && viewWorldHeight > 0) {
        const mapOriginX = currentMap.bounds.minX * TILE_SIZE;
        const mapOriginY = currentMap.bounds.minY * TILE_SIZE;
        
        // Convertir el centro del viewport a coordenadas del minimapa
        const viewCenterMinimapX = offsetX + (viewCenterX - mapOriginX) * baseScale;
        const viewCenterMinimapY = offsetY + (viewCenterY - mapOriginY) * baseScale;
        
        // Tamaño del viewport en el minimapa
        const viewWidth = viewWorldWidth * baseScale;
        const viewHeight = viewWorldHeight * baseScale;
        
        // Dibujar desde el centro (restar la mitad del tamaño)
        const viewX = viewCenterMinimapX - viewWidth / 2;
        const viewY = viewCenterMinimapY - viewHeight / 2;
        
        // Dibujar el rectángulo del área visible
        ctx.strokeStyle = '#ffff00';
        ctx.lineWidth = 2;
        ctx.strokeRect(viewX, viewY, viewWidth, viewHeight);
      }
    };

    // Renderizar minimapa inicialmente (marca el terreno para re-hornear).
    const refreshMinimap = (): void => {
      MapEditorScene.minimapDirty = true;
      renderMinimap();
    };

    // Actualizar minimapa frecuentemente (cada frame si es posible, o al menos cada 100ms)
    const minimapUpdateInterval = setInterval(() => {
      renderMinimap();
    }, 100);

    // Limpiar intervalo + dock lateral cuando se cierre el editor
    const originalBackButtonOnclick = backButton.onclick;
    backButton.onclick = () => {
      clearInterval(minimapUpdateInterval);
      editorDock.destroy();
      if (originalBackButtonOnclick) originalBackButtonOnclick.call(backButton, {} as MouseEvent);
    };

    // ---- Ventana de información del tile seleccionado ----
    const tileInfoPanel = document.createElement('div');
    tileInfoPanel.style.position = 'absolute';
    tileInfoPanel.style.top = '52px';
    tileInfoPanel.style.left = '16px';
    tileInfoPanel.style.background = 'rgba(0,0,0,0.6)';
    tileInfoPanel.style.padding = '8px 12px';
    tileInfoPanel.style.borderRadius = '8px';
    tileInfoPanel.style.fontSize = '12px';
    tileInfoPanel.style.zIndex = '10';
    tileInfoPanel.style.minWidth = '250px';
    tileInfoPanel.style.maxWidth = '350px';
    tileInfoPanel.style.color = '#eee';
    container.appendChild(tileInfoPanel);

    const refreshTileInfo = (): void => {
      if (!currentMap) {
        tileInfoPanel.innerHTML = '<div style="color: #999;">Selecciona un mapa</div>';
        return;
      }

      const tileX = selectedTile.x;
      const tileY = selectedTile.y;
      const info: string[] = [];

      // Buscar gráficos en todas las capas
      const graphicsInLayers: Array<{ layer: string; sprite: string }> = [];
      for (const layer of currentMap.layers) {
        for (const tile of layer.tiles) {
          if (tile.x === tileX && tile.y === tileY) {
            graphicsInLayers.push({
              layer: layer.name,
              sprite: tile.sprite,
            });
          }
        }
      }

      // Buscar bloqueos (ahora se lee de layers)
      const blockedInfo: string[] = [];
      for (const layer of currentMap.layers) {
        for (const tile of layer.tiles) {
          if (tile.x === tileX && tile.y === tileY && tile.blocked) {
            blockedInfo.push(layer.name);
          }
        }
      }

      // Buscar NPCs (solo el primero si hay varios)
      let npcAtTile: { templateId: string } | null = null;
      if (currentMap.npcs) {
        for (const npc of currentMap.npcs) {
          if (npc.x === tileX && npc.y === tileY) {
            npcAtTile = { templateId: npc.templateId };
            break; // Solo el primero
          }
        }
      }
      // También buscar en npcPlacements (los que se agregan en el editor)
      if (!npcAtTile) {
        for (const npc of npcPlacements) {
          if (npc.x === tileX && npc.y === tileY) {
            npcAtTile = { templateId: npc.templateId };
            break; // Solo el primero
          }
        }
      }

      // Buscar Items (solo el primero si hay varios)
      let itemAtTile: { itemId: string; quantity: number } | null = null;
      for (const item of itemPlacements) {
        if (item.x === tileX && item.y === tileY) {
          itemAtTile = { itemId: item.itemId, quantity: item.quantity };
          break; // Solo el primero
        }
      }

      // Construir HTML
      let html = `<div style="font-weight: bold; margin-bottom: 6px; color: #fff;">Tile (${tileX}, ${tileY})</div>`;

      // Bloqueos (primero si está bloqueado)
      if (blockedInfo.length > 0) {
        html += '<div style="margin-top: 6px;"><strong>✓ Bloqueado</strong> <span style="color: #4caf50;">✓</span></div>';
      }

      // Gráficos (solo mostrar si hay)
      if (graphicsInLayers.length > 0) {
        html += '<div style="margin-top: 6px;"><strong>Gráficos:</strong></div>';
        for (const g of graphicsInLayers) {
          html += `<div style="margin-left: 12px; color: #ccc;">• ${g.sprite} <span style="color: #888;">[${g.layer}]</span></div>`;
        }
      }

      // NPC (solo uno, sin bullet, con prefijo)
      if (npcAtTile) {
        // Buscar el nombre del NPC en npcTemplates
        const npcTemplate = npcTemplates.find(t => t.id === npcAtTile!.templateId);
        const npcName = npcTemplate ? npcTemplate.name : npcAtTile.templateId;
        html += `<div style="margin-top: 6px; color: #3fa9f5;">NPC: ${npcName} (${npcAtTile.templateId})</div>`;
      }

      // Item (solo uno, sin bullet, con prefijo)
      if (itemAtTile) {
        // Buscar el nombre del item en itemTemplates
        const itemTemplate = itemTemplates.find(t => t.id === itemAtTile!.itemId);
        const itemName = itemTemplate ? itemTemplate.name : itemAtTile.itemId;
        html += `<div style="margin-top: 6px; color: #ffcc00;">Item: ${itemName} - ${itemAtTile.quantity} (${itemAtTile.itemId})</div>`;
      }

      // Translado en este tile
      const transladoAtTile = transladoPlacements.find((t) => t.x === tileX && t.y === tileY);
      if (transladoAtTile) {
        html += `<div style="margin-top: 6px; color: #ffd700;">Translado → Map ${transladoAtTile.destMapId} (${transladoAtTile.destX},${transladoAtTile.destY})`;
        if (transladoAtTile.sprite) html += ` · sprite: ${transladoAtTile.sprite}`;
        const anim = transladoAtTile.arrivalAnimationId ?? 1;
        html += ` · anim: ${anim}`;
        if (transladoAtTile.randomizeArrival) html += ' · 🎲 random';
        html += '</div>';
      }

      tileInfoPanel.innerHTML = html;
    };

    // El "Tile seleccionado" ya no se muestra - eliminado según solicitud del usuario
    const refreshSelectedTileLabel = (): void => {
      // Solo actualizar la información del tile y el botón, sin mostrar el label
      refreshTileInfo();
      updateToggleBlockButton(); // Actualizar el botón cuando cambia el tile seleccionado
      refreshTransladoForm(); // Cargar datos del translado del tile (si existe) en la sección
    };
    refreshSelectedTileLabel();

    // La vista del mapa ahora la gestiona MapEditorScene (Phaser). Esta función queda como no-op.
    const renderMapView = (): void => {
      // El mapa se dibuja en MapEditorScene; aquí no hacemos nada visual.
    };

    // ---- Sección "NPCs / Items" del dock lateral ----
    // El cuerpo (panelContent) se construye igual que antes; el dock se encarga del
    // marco, header, colapso y modo flotante/pin.
    const panelContent = document.createElement('div');
    panelContent.style.display = 'flex';
    panelContent.style.flex = '1';
    panelContent.style.overflow = 'auto';
    skinScrollable(panelContent);
    panelContent.style.padding = '8px';
    panelContent.style.gap = '12px';

    editorDock.addSection({
      id: 'npcs',
      title: 'NPCs / Items',
      body: panelContent,
      preferredWidth: 360,
    });

    // ---- Columnas: NPCs e Items ----
    const npcColumn = document.createElement('div');
    npcColumn.style.flex = '1';
    npcColumn.style.display = 'flex';
    npcColumn.style.flexDirection = 'column';

    const npcTitle = document.createElement('div');
    npcTitle.textContent = 'NPCs';
    npcTitle.style.marginBottom = '4px';
    npcTitle.style.fontWeight = 'bold';

    const npcSelect = document.createElement('select');
    npcSelect.size = 6;
    npcSelect.style.width = '100%';

    npcTemplates.forEach((tpl) => {
      const opt = document.createElement('option');
      opt.value = tpl.id;
      opt.textContent = tpl.id; // Sólo ID, sin más info
      npcSelect.appendChild(opt);
    });

    // ---- Override de respawn por área (para mobs creados dentro de un área) ----
    const respawnBox = document.createElement('div');
    respawnBox.style.marginTop = '6px';
    respawnBox.style.padding = '4px';
    respawnBox.style.border = '1px solid #444';
    respawnBox.style.borderRadius = '4px';
    respawnBox.style.fontSize = '11px';

    const respawnCb = document.createElement('input');
    respawnCb.type = 'checkbox';
    const respawnCbLabel = document.createElement('label');
    respawnCbLabel.append(respawnCb, document.createTextNode(' Respawn en área'));
    respawnCbLabel.style.display = 'flex';
    respawnCbLabel.style.alignItems = 'center';
    respawnCbLabel.style.gap = '4px';

    const respawnTimeInput = document.createElement('input');
    respawnTimeInput.type = 'number';
    respawnTimeInput.min = '0';
    respawnTimeInput.value = '30000';
    respawnTimeInput.style.width = '90px';
    const respawnTimeRow = document.createElement('div');
    respawnTimeRow.style.display = 'flex';
    respawnTimeRow.style.alignItems = 'center';
    respawnTimeRow.style.gap = '4px';
    respawnTimeRow.style.marginTop = '3px';
    respawnTimeRow.append(document.createTextNode('Tiempo (ms):'), respawnTimeInput);

    const respawnAreaSelect = document.createElement('select');
    respawnAreaSelect.style.width = '100%';
    respawnAreaSelect.style.marginTop = '3px';
    const refreshRespawnAreaOptions = (): void => {
      const prev = respawnAreaSelect.value;
      respawnAreaSelect.innerHTML = '';
      const none = document.createElement('option');
      none.value = '';
      none.textContent = '(área del tile)';
      respawnAreaSelect.appendChild(none);
      for (const a of areaPlacements) {
        const opt = document.createElement('option');
        opt.value = a.id;
        opt.textContent = a.name ? `${a.name} (${a.id})` : a.id;
        respawnAreaSelect.appendChild(opt);
      }
      respawnAreaSelect.value = prev;
    };
    respawnAreaSelect.onmousedown = refreshRespawnAreaOptions;

    const respawnZonesInput = document.createElement('input');
    respawnZonesInput.type = 'text';
    respawnZonesInput.placeholder = 'zonas (rz_a, rz_b) — vacío = todo el área';
    respawnZonesInput.style.width = '100%';
    respawnZonesInput.style.marginTop = '3px';

    respawnBox.append(respawnCbLabel, respawnTimeRow, respawnAreaSelect, respawnZonesInput);

    const npcInvokeButton = document.createElement('button');
    npcInvokeButton.textContent = 'Invocar';
    npcInvokeButton.style.marginTop = '4px';
    npcInvokeButton.style.cursor = 'pointer';

    npcInvokeButton.onclick = () => {
      const tplId = npcSelect.value;
      if (!tplId) return;
      const placement: MapNPCData = {
        templateId: tplId,
        x: selectedTile.x,
        y: selectedTile.y,
      };
      if (respawnCb.checked) {
        placement.respawn = true;
        const t = parseInt(respawnTimeInput.value, 10);
        if (!Number.isNaN(t) && t > 0) placement.respawnTime = t;
        if (respawnAreaSelect.value) placement.respawnAreaId = respawnAreaSelect.value;
        const zones = respawnZonesInput.value
          .split(',')
          .map((z) => z.trim())
          .filter((z) => z.length > 0);
        if (zones.length > 0) placement.respawnZoneIds = zones;
      }
      npcPlacements.push(placement);
      refreshPlacementsView();
      // Actualizar visualización en Phaser
      if (editorGame) {
        const scene = editorGame.scene.getScene('MapEditorScene') as MapEditorScene;
        if (scene) {
          scene.updateNPCsAndItems(npcPlacements, itemPlacements);
        }
      }
      // Actualizar información del tile
      refreshTileInfo();
    };

    npcColumn.append(npcTitle, npcSelect, respawnBox, npcInvokeButton);

    const itemColumn = document.createElement('div');
    itemColumn.style.flex = '1';
    itemColumn.style.display = 'flex';
    itemColumn.style.flexDirection = 'column';

    const itemTitle = document.createElement('div');
    itemTitle.textContent = 'Items';
    itemTitle.style.marginBottom = '4px';
    itemTitle.style.fontWeight = 'bold';

    const itemSelect = document.createElement('select');
    itemSelect.size = 6;
    itemSelect.style.width = '100%';

    itemTemplates.forEach((tpl) => {
      const opt = document.createElement('option');
      opt.value = tpl.id;
      opt.textContent = tpl.id;
      itemSelect.appendChild(opt);
    });

    const itemInvokeButton = document.createElement('button');
    itemInvokeButton.textContent = 'Invocar';
    itemInvokeButton.style.marginTop = '4px';
    itemInvokeButton.style.cursor = 'pointer';

    itemInvokeButton.onclick = () => {
      const itemId = itemSelect.value;
      if (!itemId) return;
      itemPlacements.push({
        itemId,
        x: selectedTile.x,
        y: selectedTile.y,
        quantity: 1,
      });
      refreshPlacementsView();
      // Actualizar visualización en Phaser
      if (editorGame) {
        const scene = editorGame.scene.getScene('MapEditorScene') as MapEditorScene;
        if (scene) {
          scene.updateNPCsAndItems(npcPlacements, itemPlacements);
        }
      }
      // Actualizar información del tile
      refreshTileInfo();
    };

    itemColumn.append(itemTitle, itemSelect, itemInvokeButton);

    // ---- Columna de Luces ----
    const lightColumn = document.createElement('div');
    lightColumn.style.flex = '1';
    lightColumn.style.display = 'flex';
    lightColumn.style.flexDirection = 'column';

    const lightTitle = document.createElement('div');
    lightTitle.textContent = 'Luces';
    lightTitle.style.marginBottom = '4px';
    lightTitle.style.fontWeight = 'bold';

    // Configuración de luz
    const lightConfigContainer = document.createElement('div');
    lightConfigContainer.style.display = 'flex';
    lightConfigContainer.style.flexDirection = 'column';
    lightConfigContainer.style.gap = '4px';
    lightConfigContainer.style.marginBottom = '8px';
    lightConfigContainer.style.fontSize = '11px';

    // Radio
    const radiusRow = document.createElement('div');
    radiusRow.style.display = 'flex';
    radiusRow.style.alignItems = 'center';
    radiusRow.style.gap = '4px';
    const radiusLabel = document.createElement('label');
    radiusLabel.textContent = 'Radio:';
    radiusLabel.style.width = '60px';
    const radiusInput = document.createElement('input');
    radiusInput.type = 'number';
    radiusInput.value = '120';
    radiusInput.min = '32';
    radiusInput.max = '500';
    radiusInput.style.width = '60px';
    radiusInput.style.background = '#222';
    radiusInput.style.border = '1px solid #444';
    radiusInput.style.color = '#fff';
    radiusInput.style.padding = '2px 4px';
    radiusRow.append(radiusLabel, radiusInput);

    // Intensidad
    const intensityRow = document.createElement('div');
    intensityRow.style.display = 'flex';
    intensityRow.style.alignItems = 'center';
    intensityRow.style.gap = '4px';
    const intensityLabel = document.createElement('label');
    intensityLabel.textContent = 'Intensidad:';
    intensityLabel.style.width = '60px';
    const intensityInput = document.createElement('input');
    intensityInput.type = 'number';
    intensityInput.value = '0.9';
    intensityInput.min = '0.1';
    intensityInput.max = '1.0';
    intensityInput.step = '0.1';
    intensityInput.style.width = '60px';
    intensityInput.style.background = '#222';
    intensityInput.style.border = '1px solid #444';
    intensityInput.style.color = '#fff';
    intensityInput.style.padding = '2px 4px';
    intensityRow.append(intensityLabel, intensityInput);

    // Color
    const colorRow = document.createElement('div');
    colorRow.style.display = 'flex';
    colorRow.style.alignItems = 'center';
    colorRow.style.gap = '4px';
    const colorLabel = document.createElement('label');
    colorLabel.textContent = 'Color:';
    colorLabel.style.width = '60px';
    const colorInput = document.createElement('input');
    colorInput.type = 'color';
    colorInput.value = '#FFAA44';
    colorInput.style.width = '60px';
    colorInput.style.height = '24px';
    colorInput.style.border = '1px solid #444';
    colorInput.style.cursor = 'pointer';
    colorRow.append(colorLabel, colorInput);

    // Flicker
    const flickerRow = document.createElement('div');
    flickerRow.style.display = 'flex';
    flickerRow.style.alignItems = 'center';
    flickerRow.style.gap = '4px';
    const flickerLabel = document.createElement('label');
    flickerLabel.textContent = 'Parpadeo:';
    flickerLabel.style.width = '60px';
    const flickerCheckbox = document.createElement('input');
    flickerCheckbox.type = 'checkbox';
    flickerCheckbox.checked = true;
    flickerRow.append(flickerLabel, flickerCheckbox);

    lightConfigContainer.append(radiusRow, intensityRow, colorRow, flickerRow);

    // Botón para colocar luz
    const placeLightButton = document.createElement('button');
    placeLightButton.textContent = 'Colocar Luz';
    placeLightButton.style.marginTop = '4px';
    placeLightButton.style.cursor = 'pointer';
    placeLightButton.style.background = '#FFAA44';
    placeLightButton.style.color = '#000';
    placeLightButton.style.border = 'none';
    placeLightButton.style.padding = '6px 12px';
    placeLightButton.style.borderRadius = '4px';
    placeLightButton.style.fontWeight = 'bold';

    placeLightButton.onclick = () => {
      if (!currentMap) return;
      
      // Convertir color hex a formato 0xRRGGBB
      const hexColor = colorInput.value.replace('#', '0x');
      
      const newLight: MapLightData = {
        id: `light_${Date.now().toString(36)}_${Math.random().toString(36).substring(2, 6)}`,
        x: selectedTile.x,
        y: selectedTile.y,
        radius: parseInt(radiusInput.value, 10),
        intensity: parseFloat(intensityInput.value),
        color: hexColor,
        flicker: flickerCheckbox.checked,
      };
      
      lightPlacements.push(newLight);
      
      // Actualizar el mapa
      if (currentMap) {
        currentMap.lights = [...lightPlacements];
      }
      
      // Actualizar visualización en Phaser
      if (editorGame) {
        const scene = editorGame.scene.getScene('MapEditorScene') as MapEditorScene;
        if (scene) {
          scene.addLight(newLight);
        }
      }
      
      refreshPlacementsView();
      refreshTileInfo();
    };

    // Botón para eliminar luz en el tile seleccionado
    const removeLightButton = document.createElement('button');
    removeLightButton.textContent = 'Eliminar Luz';
    removeLightButton.style.marginTop = '4px';
    removeLightButton.style.cursor = 'pointer';
    removeLightButton.style.background = '#aa4444';
    removeLightButton.style.color = '#fff';
    removeLightButton.style.border = 'none';
    removeLightButton.style.padding = '6px 12px';
    removeLightButton.style.borderRadius = '4px';

    removeLightButton.onclick = () => {
      if (!currentMap) return;
      
      // Buscar luz en la posición seleccionada
      const lightIndex = lightPlacements.findIndex(l => l.x === selectedTile.x && l.y === selectedTile.y);
      if (lightIndex < 0) {
        return;
      }
      
      const lightId = lightPlacements[lightIndex].id;
      lightPlacements.splice(lightIndex, 1);
      
      // Actualizar el mapa
      if (currentMap) {
        currentMap.lights = [...lightPlacements];
      }
      
      // Actualizar visualización en Phaser
      if (editorGame) {
        const scene = editorGame.scene.getScene('MapEditorScene') as MapEditorScene;
        if (scene) {
          scene.removeLight(lightId);
        }
      }
      
      refreshPlacementsView();
      refreshTileInfo();
    };

    // Info de luces en el tile
    const lightInfo = document.createElement('div');
    lightInfo.style.marginTop = '8px';
    lightInfo.style.fontSize = '10px';
    lightInfo.style.color = '#888';
    lightInfo.textContent = 'Selecciona un tile para colocar una luz';

    // ---- Deco-object: FOGATA (animación en loop + luz acoplada) ----
    // Reutiliza los inputs de luz de arriba (radius/intensity/color/flicker) para la luz acoplada.
    // Coloca un decoObject con animationId 25 (fogata). Ver docs/MAP_FORMAT.md.
    const placeFogataButton = document.createElement('button');
    placeFogataButton.textContent = '🔥 Colocar Fogata';
    placeFogataButton.style.marginTop = '8px';
    placeFogataButton.style.cursor = 'pointer';
    placeFogataButton.style.background = '#FF7A2A';
    placeFogataButton.style.color = '#000';
    placeFogataButton.style.border = 'none';
    placeFogataButton.style.padding = '6px 12px';
    placeFogataButton.style.borderRadius = '4px';
    placeFogataButton.style.fontWeight = 'bold';
    placeFogataButton.onclick = (): void => {
      if (!currentMap) return;
      const deco: MapDecoObjectData = {
        id: `deco_${Date.now().toString(36)}_${Math.random().toString(36).substring(2, 6)}`,
        x: selectedTile.x,
        y: selectedTile.y,
        animationId: 25,
        light: {
          radius: parseInt(radiusInput.value, 10),
          intensity: parseFloat(intensityInput.value),
          color: colorInput.value.replace('#', '0x'),
          flicker: flickerCheckbox.checked,
        },
      };
      decoObjectPlacements.push(deco);
      currentMap.decoObjects = [...decoObjectPlacements];
      if (editorGame) {
        const scene = editorGame.scene.getScene('MapEditorScene') as MapEditorScene;
        scene?.addDecoMarker(deco.id, deco.x, deco.y, '#FF7A2A');
      }
      refreshPlacementsView();
      refreshTileInfo();
    };

    const removeFogataButton = document.createElement('button');
    removeFogataButton.textContent = 'Eliminar Deco';
    removeFogataButton.style.marginTop = '4px';
    removeFogataButton.style.cursor = 'pointer';
    removeFogataButton.style.background = '#aa4444';
    removeFogataButton.style.color = '#fff';
    removeFogataButton.style.border = 'none';
    removeFogataButton.style.padding = '6px 12px';
    removeFogataButton.style.borderRadius = '4px';
    removeFogataButton.onclick = (): void => {
      if (!currentMap) return;
      const idx = decoObjectPlacements.findIndex((d) => d.x === selectedTile.x && d.y === selectedTile.y);
      if (idx < 0) return;
      const decoId = decoObjectPlacements[idx].id;
      decoObjectPlacements.splice(idx, 1);
      currentMap.decoObjects = [...decoObjectPlacements];
      if (editorGame) {
        const scene = editorGame.scene.getScene('MapEditorScene') as MapEditorScene;
        scene?.removeDecoMarker(decoId);
      }
      refreshPlacementsView();
      refreshTileInfo();
    };

    lightColumn.append(
      lightTitle,
      lightConfigContainer,
      placeLightButton,
      removeLightButton,
      lightInfo,
      placeFogataButton,
      removeFogataButton
    );

    // ---- Sección "Translados" del dock lateral ----
    const transladoColumn = document.createElement('div');
    transladoColumn.style.display = 'flex';
    transladoColumn.style.flexDirection = 'column';
    transladoColumn.style.flex = '1';
    transladoColumn.style.overflowY = 'auto';
    skinScrollable(transladoColumn);
    transladoColumn.style.padding = '8px';
    transladoColumn.style.gap = '6px';

    editorDock.addSection({
      id: 'translados',
      title: '🌀 Translados',
      body: transladoColumn,
      preferredWidth: 300,
    });

    const transladoTitle = document.createElement('div');
    transladoTitle.textContent = 'Translados';
    transladoTitle.style.marginBottom = '4px';
    transladoTitle.style.fontWeight = 'bold';

    const transladoConfig = document.createElement('div');
    transladoConfig.style.display = 'flex';
    transladoConfig.style.flexDirection = 'column';
    transladoConfig.style.gap = '4px';
    transladoConfig.style.marginBottom = '8px';
    transladoConfig.style.fontSize = '11px';

    const makeNumRow = (labelText: string, defaultValue: string): { row: HTMLDivElement; input: HTMLInputElement } => {
      const row = document.createElement('div');
      row.style.display = 'flex';
      row.style.alignItems = 'center';
      row.style.gap = '4px';
      const label = document.createElement('label');
      label.textContent = labelText;
      label.style.width = '70px';
      const input = document.createElement('input');
      input.type = 'number';
      input.value = defaultValue;
      input.style.width = '70px';
      input.style.background = '#222';
      input.style.border = '1px solid #444';
      input.style.color = '#fff';
      input.style.padding = '2px 4px';
      row.append(label, input);
      return { row, input };
    };

    const destMapRow = makeNumRow('Dest Map:', '1');
    const destXRow = makeNumRow('Dest X:', '50');
    const destYRow = makeNumRow('Dest Y:', '50');
    const animRow = makeNumRow('Anim ID:', '1');
    animRow.input.min = '1';

    // Sprite del tile (texto libre — surface id del assets-index, p.ej. "surface_warp_01")
    const spriteRow = document.createElement('div');
    spriteRow.style.display = 'flex';
    spriteRow.style.alignItems = 'center';
    spriteRow.style.gap = '4px';
    const spriteLabel = document.createElement('label');
    spriteLabel.textContent = 'Sprite:';
    spriteLabel.style.width = '70px';
    const spriteInput = document.createElement('input');
    spriteInput.type = 'text';
    spriteInput.placeholder = '(opcional)';
    spriteInput.style.flex = '1';
    spriteInput.style.background = '#222';
    spriteInput.style.border = '1px solid #444';
    spriteInput.style.color = '#fff';
    spriteInput.style.padding = '2px 4px';
    spriteRow.append(spriteLabel, spriteInput);

    // Checkbox: random llegada (anti-camp)
    const randomRow = document.createElement('div');
    randomRow.style.display = 'flex';
    randomRow.style.alignItems = 'center';
    randomRow.style.gap = '4px';
    const randomLabel = document.createElement('label');
    randomLabel.textContent = 'Random:';
    randomLabel.style.width = '70px';
    randomLabel.title = 'Si está activo, la llegada se reparte aleatoriamente entre el tile destino y sus 8 vecinos (anti-camp).';
    const randomCheckbox = document.createElement('input');
    randomCheckbox.type = 'checkbox';
    randomCheckbox.checked = false;
    randomCheckbox.title = randomLabel.title;
    randomRow.append(randomLabel, randomCheckbox);

    // Condición de facción (ver docs/FACTIONS.md): exigir tener facción o una
    // facción específica para usar el translado.
    const factionRow = document.createElement('div');
    factionRow.style.display = 'flex';
    factionRow.style.gap = '4px';
    const factionLabel = document.createElement('label');
    factionLabel.textContent = 'Facción:';
    factionLabel.style.width = '70px';
    factionLabel.title = 'Restringe quién puede usar el translado por facción.';
    const transladoFactionSelect = document.createElement('select');
    for (const opt of [
      { value: '', label: 'Cualquiera' },
      { value: 'any', label: 'Con facción (no newbie)' },
      { value: 'aresgard', label: 'Solo Aresgard' },
      { value: 'elvaria', label: 'Solo Elvaria' },
      { value: 'rebel', label: 'Solo Rebeldes' },
    ]) {
      const o = document.createElement('option');
      o.value = opt.value;
      o.textContent = opt.label;
      transladoFactionSelect.appendChild(o);
    }
    factionRow.append(factionLabel, transladoFactionSelect);

    transladoConfig.append(destMapRow.row, destXRow.row, destYRow.row, animRow.row, spriteRow, randomRow, factionRow);

    const placeTransladoButton = document.createElement('button');
    placeTransladoButton.textContent = 'Crear Translado';
    placeTransladoButton.style.marginTop = '4px';
    placeTransladoButton.style.cursor = 'pointer';
    placeTransladoButton.style.background = '#ffd700';
    placeTransladoButton.style.color = '#000';
    placeTransladoButton.style.border = 'none';
    placeTransladoButton.style.padding = '6px 12px';
    placeTransladoButton.style.borderRadius = '4px';
    placeTransladoButton.style.fontWeight = 'bold';

    placeTransladoButton.onclick = () => {
      if (!currentMap) return;
      const destMapId = parseInt(destMapRow.input.value, 10);
      const destX = parseInt(destXRow.input.value, 10);
      const destY = parseInt(destYRow.input.value, 10);
      const arrival = parseInt(animRow.input.value, 10) || 1;
      const sprite = spriteInput.value.trim() || undefined;

      // Validar loop trivial (mismo mapa, mismo tile)
      const currentMapId = Number(currentMap.id);
      if (destMapId === currentMapId && destX === selectedTile.x && destY === selectedTile.y) {
        alert('No se puede crear un translado que apunta a sí mismo (mismo mapa + mismo tile).');
        return;
      }

      // Si ya hay translado en este tile, reemplazarlo (mismo id)
      const existing = transladoPlacements.find((t) => t.x === selectedTile.x && t.y === selectedTile.y);
      const id =
        existing?.id ?? `translado_${Date.now().toString(36)}_${Math.random().toString(36).substring(2, 6)}`;

      const factionSel = transladoFactionSelect.value;
      const newT: MapTransladoData = {
        id,
        x: selectedTile.x,
        y: selectedTile.y,
        destMapId,
        destX,
        destY,
        sprite,
        arrivalAnimationId: arrival,
        randomizeArrival: randomCheckbox.checked || undefined,
        requiresFaction: factionSel === 'any' ? true : undefined,
        requiredFaction:
          factionSel && factionSel !== 'any' ? factionSel : undefined,
      };

      // Update local list
      const idx = transladoPlacements.findIndex((t) => t.id === id);
      if (idx >= 0) transladoPlacements[idx] = newT;
      else transladoPlacements.push(newT);

      if (currentMap) currentMap.translados = [...transladoPlacements];

      // Render en Phaser
      if (editorGame) {
        const scene = editorGame.scene.getScene('MapEditorScene') as MapEditorScene;
        if (scene) scene.addTranslado(newT);
      }

      refreshPlacementsView();
      refreshTileInfo();
      refreshTransladoForm();
    };

    const removeTransladoButton = document.createElement('button');
    removeTransladoButton.textContent = 'Eliminar Translado';
    removeTransladoButton.style.marginTop = '4px';
    removeTransladoButton.style.cursor = 'pointer';
    removeTransladoButton.style.background = '#aa4444';
    removeTransladoButton.style.color = '#fff';
    removeTransladoButton.style.border = 'none';
    removeTransladoButton.style.padding = '6px 12px';
    removeTransladoButton.style.borderRadius = '4px';

    removeTransladoButton.onclick = () => {
      if (!currentMap) return;
      const idx = transladoPlacements.findIndex((t) => t.x === selectedTile.x && t.y === selectedTile.y);
      if (idx < 0) return;
      const id = transladoPlacements[idx].id;
      transladoPlacements.splice(idx, 1);
      if (currentMap) currentMap.translados = [...transladoPlacements];
      if (editorGame) {
        const scene = editorGame.scene.getScene('MapEditorScene') as MapEditorScene;
        if (scene) scene.removeTranslado(id);
      }
      refreshPlacementsView();
      refreshTileInfo();
      refreshTransladoForm();
    };

    // Línea de estado: indica si el tile seleccionado ya tiene translado.
    const transladoStatus = document.createElement('div');
    transladoStatus.style.marginTop = '6px';
    transladoStatus.style.fontSize = '11px';
    transladoStatus.style.fontWeight = 'bold';

    const transladoInfo = document.createElement('div');
    transladoInfo.style.marginTop = '8px';
    transladoInfo.style.fontSize = '10px';
    transladoInfo.style.color = '#888';
    transladoInfo.textContent = 'Selecciona un tile y completá destino + sprite + animación. Anim 1 = entrada estándar.';

    transladoColumn.append(
      transladoTitle,
      transladoStatus,
      transladoConfig,
      placeTransladoButton,
      removeTransladoButton,
      transladoInfo
    );

    // Carga la info del translado del tile seleccionado (si existe) en el formulario y ajusta
    // el label del botón / la línea de estado. Reasigna el placeholder declarado arriba.
    refreshTransladoForm = (): void => {
      const existing = transladoPlacements.find(
        (t) => t.x === selectedTile.x && t.y === selectedTile.y
      );
      if (existing) {
        destMapRow.input.value = String(existing.destMapId);
        destXRow.input.value = String(existing.destX);
        destYRow.input.value = String(existing.destY);
        animRow.input.value = String(existing.arrivalAnimationId ?? 1);
        spriteInput.value = existing.sprite ?? '';
        randomCheckbox.checked = !!existing.randomizeArrival;
        transladoFactionSelect.value = existing.requiredFaction
          ? existing.requiredFaction
          : existing.requiresFaction
            ? 'any'
            : '';
        placeTransladoButton.textContent = 'Actualizar Translado';
        removeTransladoButton.style.display = '';
        transladoStatus.style.color = '#ffd700';
        transladoStatus.textContent = `Translado en (${selectedTile.x},${selectedTile.y}) → M${existing.destMapId} (${existing.destX},${existing.destY})`;
      } else {
        transladoFactionSelect.value = '';
        placeTransladoButton.textContent = 'Crear Translado';
        removeTransladoButton.style.display = 'none';
        transladoStatus.style.color = '#888';
        transladoStatus.textContent = `Tile (${selectedTile.x},${selectedTile.y}) sin translado`;
      }
    };
    refreshTransladoForm();

    panelContent.append(npcColumn, itemColumn, lightColumn);

    const refreshPlacementsView = (): void => {
      // Ya no necesitamos actualizar ningún textarea, solo mantener la función por compatibilidad
      buildMapJson();
      // Actualizar minimap cuando cambian los NPCs o items
      refreshMinimap();
    };

    // Inicializar JSON sin mapa seleccionado
    refreshPlacementsView();

    // =====================================================================
    // ---- Sección "Áreas" del dock lateral ----
    // =====================================================================
    const areaPanelContent = document.createElement('div');
    areaPanelContent.style.display = 'flex';
    areaPanelContent.style.flexDirection = 'column';
    areaPanelContent.style.flex = '1';
    areaPanelContent.style.overflowY = 'auto';
    skinScrollable(areaPanelContent);
    areaPanelContent.style.padding = '8px';
    areaPanelContent.style.gap = '6px';

    editorDock.addSection({
      id: 'areas',
      title: '🗺️ Áreas',
      body: areaPanelContent,
      preferredWidth: 300,
    });

    // ---- Estado del editor de áreas ----
    let selectedAreaId: string | null = null;
    let selectedZoneId: string | null = null;
    let drawTarget: 'area' | 'zone' | null = null;

    const genAreaId = (): string =>
      `area_${Date.now().toString(36)}_${Math.floor(Math.random() * 1e4).toString(36)}`;
    const genZoneId = (): string =>
      `rz_${Date.now().toString(36)}_${Math.floor(Math.random() * 1e4).toString(36)}`;

    const getSelectedArea = (): MapAreaData | null =>
      areaPlacements.find((a) => a.id === selectedAreaId) ?? null;

    const pushAreasToScene = (): void => {
      if (currentMap) currentMap.areas = areaPlacements.length > 0 ? areaPlacements : undefined;
      if (editorGame) {
        const scene = editorGame.scene.getScene('MapEditorScene') as MapEditorScene;
        if (scene) scene.updateAreas(areaPlacements);
      }
    };

    // Helpers de UI
    const areaFieldRow = (labelText: string, el: HTMLElement): HTMLDivElement => {
      const row = document.createElement('div');
      row.style.display = 'flex';
      row.style.alignItems = 'center';
      row.style.gap = '6px';
      const lab = document.createElement('label');
      lab.textContent = labelText;
      lab.style.fontSize = '11px';
      lab.style.minWidth = '92px';
      row.append(lab, el);
      return row;
    };
    const areaCheckbox = (
      labelText: string,
      checked: boolean,
      onChange: (v: boolean) => void
    ): HTMLDivElement => {
      const cb = document.createElement('input');
      cb.type = 'checkbox';
      cb.checked = checked;
      cb.onchange = (): void => onChange(cb.checked);
      return areaFieldRow(labelText, cb);
    };

    // Lista de áreas
    const areaSelect = document.createElement('select');
    areaSelect.size = 4;
    areaSelect.style.width = '100%';
    areaSelect.onchange = (): void => {
      selectedAreaId = areaSelect.value || null;
      selectedZoneId = null;
      stopDrawing();
      renderAreaInspector();
    };

    const refreshAreaList = (): void => {
      areaSelect.innerHTML = '';
      for (const a of areaPlacements) {
        const opt = document.createElement('option');
        opt.value = a.id;
        opt.textContent = a.name ? `${a.name} (${a.id})` : a.id;
        areaSelect.appendChild(opt);
      }
      if (selectedAreaId) areaSelect.value = selectedAreaId;
    };

    const areaButtonsRow = document.createElement('div');
    areaButtonsRow.style.display = 'flex';
    areaButtonsRow.style.gap = '6px';
    const newAreaBtn = document.createElement('button');
    newAreaBtn.textContent = '+ Área';
    newAreaBtn.style.cursor = 'pointer';
    newAreaBtn.onclick = (): void => {
      const area: MapAreaData = {
        id: genAreaId(),
        name: 'Nueva área',
        rects: [],
        safe: false,
        tradeEnabled: true,
        dropItemsOnDeath: true,
        expBonusEligible: true,
        goldBonusEligible: true,
        questBonusEligible: true,
        respawnZones: [],
      };
      areaPlacements.push(area);
      selectedAreaId = area.id;
      selectedZoneId = null;
      refreshAreaList();
      renderAreaInspector();
      pushAreasToScene();
    };
    const delAreaBtn = document.createElement('button');
    delAreaBtn.textContent = '🗑 Área';
    delAreaBtn.style.cursor = 'pointer';
    delAreaBtn.onclick = (): void => {
      if (!selectedAreaId) return;
      const idx = areaPlacements.findIndex((a) => a.id === selectedAreaId);
      if (idx >= 0) areaPlacements.splice(idx, 1);
      selectedAreaId = null;
      selectedZoneId = null;
      stopDrawing();
      refreshAreaList();
      renderAreaInspector();
      pushAreasToScene();
    };
    areaButtonsRow.append(newAreaBtn, delAreaBtn);

    // Toggle mostrar/ocultar áreas
    let showAreasFlag = true;
    const showAreasRow = areaCheckbox('Mostrar áreas', true, (v) => {
      showAreasFlag = v;
      if (editorGame) {
        const scene = editorGame.scene.getScene('MapEditorScene') as MapEditorScene;
        if (scene) scene.setShowAreas(showAreasFlag);
      }
    });

    // Inspector (se reconstruye al seleccionar área)
    const areaInspector = document.createElement('div');
    areaInspector.style.display = 'flex';
    areaInspector.style.flexDirection = 'column';
    areaInspector.style.gap = '4px';
    areaInspector.style.borderTop = '1px solid #444';
    areaInspector.style.paddingTop = '6px';

    const drawAreaBtn = document.createElement('button');
    const drawZoneBtn = document.createElement('button');
    const stopDrawing = (): void => {
      drawTarget = null;
      if (editorGame) {
        const scene = editorGame.scene.getScene('MapEditorScene') as MapEditorScene;
        if (scene) scene.setAreaDrawMode(false);
      }
      drawAreaBtn.textContent = '✏️ Dibujar rect (área)';
      drawAreaBtn.style.background = '';
      drawZoneBtn.textContent = '✏️ Dibujar rect (zona)';
      drawZoneBtn.style.background = '';
    };
    const startDrawing = (target: 'area' | 'zone'): void => {
      drawTarget = target;
      if (editorGame) {
        const scene = editorGame.scene.getScene('MapEditorScene') as MapEditorScene;
        if (scene) scene.setAreaDrawMode(true);
      }
      drawAreaBtn.style.background = target === 'area' ? '#2a6' : '';
      drawAreaBtn.textContent = target === 'area' ? '■ Dibujando (área)' : '✏️ Dibujar rect (área)';
      drawZoneBtn.style.background = target === 'zone' ? '#2a6' : '';
      drawZoneBtn.textContent = target === 'zone' ? '■ Dibujando (zona)' : '✏️ Dibujar rect (zona)';
    };

    // Rect dibujado → si estamos dibujando un CARDUMEN, es su área; si no, va al área/zona.
    MapEditorScene.onAreaRectDrawn = (rect: MapRect): void => {
      if (fishingDrawActive && selectedFishSpotId) {
        const spot = fishingSpotPlacements.find((s) => s.id === selectedFishSpotId);
        if (spot) spot.rect = rect;
        fishingDrawActive = false;
        onFishingRectDone?.();
        return;
      }
      const area = getSelectedArea();
      if (!area) return;
      if (drawTarget === 'zone' && selectedZoneId) {
        const zone = (area.respawnZones ?? []).find((z) => z.id === selectedZoneId);
        if (zone) zone.rects.push(rect);
      } else {
        area.rects.push(rect);
      }
      pushAreasToScene();
      renderAreaInspector();
    };

    const parseCsv = (s: string): string[] =>
      s.split(',').map((x) => x.trim()).filter((x) => x.length > 0);

    function renderAreaInspector(): void {
      areaInspector.innerHTML = '';
      const area = getSelectedArea();
      if (!area) {
        const hint = document.createElement('div');
        hint.style.fontSize = '11px';
        hint.style.color = '#888';
        hint.textContent = 'Creá o seleccioná un área para editarla.';
        areaInspector.appendChild(hint);
        return;
      }

      // Nombre
      const nameInput = document.createElement('input');
      nameInput.type = 'text';
      nameInput.value = area.name ?? '';
      nameInput.style.flex = '1';
      nameInput.oninput = (): void => {
        area.name = nameInput.value || undefined;
        refreshAreaList();
        pushAreasToScene();
      };
      areaInspector.appendChild(areaFieldRow('Nombre', nameInput));

      // Flags
      areaInspector.appendChild(
        areaCheckbox('Segura (no PvP)', area.safe === true, (v) => { area.safe = v; pushAreasToScene(); })
      );
      areaInspector.appendChild(
        areaCheckbox('Comercio', area.tradeEnabled !== false, (v) => { area.tradeEnabled = v; })
      );
      areaInspector.appendChild(
        areaCheckbox('Drop al morir', area.dropItemsOnDeath !== false, (v) => { area.dropItemsOnDeath = v; })
      );
      areaInspector.appendChild(
        areaCheckbox('Embarcable (muelle)', area.embarkable === true, (v) => { area.embarkable = v || undefined; })
      );
      areaInspector.appendChild(
        areaCheckbox('Bonus exp', area.expBonusEligible !== false, (v) => { area.expBonusEligible = v; })
      );
      areaInspector.appendChild(
        areaCheckbox('Bonus oro', area.goldBonusEligible !== false, (v) => { area.goldBonusEligible = v; })
      );
      areaInspector.appendChild(
        areaCheckbox('Bonus quest', area.questBonusEligible !== false, (v) => { area.questBonusEligible = v; })
      );
      areaInspector.appendChild(
        areaCheckbox('Ocultar minimapa', area.hideMinimap === true, (v) => { area.hideMinimap = v; pushAreasToScene(); })
      );
      areaInspector.appendChild(
        areaCheckbox('Interior (oscurecer afuera)', area.interior === true, (v) => { area.interior = v; pushAreasToScene(); })
      );

      // Facción dueña del área (PvP entre enemigos + "Sal de aquí, traidor" +
      // regla de drop al morir). Ver docs/FACTIONS.md.
      const factionSelect = document.createElement('select');
      factionSelect.style.flex = '1';
      for (const opt of [
        { value: '', label: 'Ninguna' },
        { value: 'aresgard', label: 'Aresgard (roja)' },
        { value: 'elvaria', label: 'Elvaria (azul)' },
      ]) {
        const o = document.createElement('option');
        o.value = opt.value;
        o.textContent = opt.label;
        factionSelect.appendChild(o);
      }
      factionSelect.value = area.factionId ?? '';
      factionSelect.onchange = (): void => {
        area.factionId = factionSelect.value || undefined;
        pushAreasToScene();
      };
      areaInspector.appendChild(areaFieldRow('Facción dueña', factionSelect));

      // ===== Audio de la zona (música + enter/leave + ruidos ambientes) =====
      const audioTitle = document.createElement('div');
      audioTitle.textContent = 'Audio de la zona';
      audioTitle.style.fontSize = '11px';
      audioTitle.style.fontWeight = 'bold';
      audioTitle.style.marginTop = '4px';
      areaInspector.appendChild(audioTitle);

      // Datalist compartido con TODOS los sonidos del registro (autocompletar por id).
      const soundListId = 'aureum-area-sound-ids';
      if (!document.getElementById(soundListId)) {
        const dl = document.createElement('datalist');
        dl.id = soundListId;
        for (const def of Object.values(SOUNDS)) {
          const o = document.createElement('option');
          o.value = def.id;
          o.textContent = def.label ?? def.id;
          dl.appendChild(o);
        }
        areaInspector.appendChild(dl);
      }

      const mkSoundInput = (
        value: string | undefined,
        placeholder: string,
        onset: (v: string | undefined) => void
      ): HTMLInputElement => {
        const inp = document.createElement('input');
        inp.type = 'text';
        inp.value = value ?? '';
        inp.style.flex = '1';
        inp.placeholder = placeholder;
        inp.setAttribute('list', soundListId);
        inp.oninput = (): void => onset(inp.value.trim() || undefined);
        return inp;
      };

      // Música
      const musicInput = mkSoundInput(area.musicId, 'musicId (ej. music_city)', (v) => {
        area.musicId = v;
      });
      areaInspector.appendChild(areaFieldRow('Música', musicInput));

      // Loop de ambiente continuo (lluvia/mar/viento…)
      const ambientLoopInput = mkSoundInput(area.ambientLoopId, 'ambient loop (ej. ambient_ocean)', (v) => {
        area.ambientLoopId = v;
      });
      areaInspector.appendChild(areaFieldRow('Ambiente (loop)', ambientLoopInput));

      // Reverb de "cueva" (0..1): mix wet del reverb de zona para dungeons/interiores.
      const reverbRow = document.createElement('div');
      reverbRow.style.display = 'flex';
      reverbRow.style.alignItems = 'center';
      reverbRow.style.gap = '6px';
      reverbRow.style.flex = '1';
      const reverbInput = document.createElement('input');
      reverbInput.type = 'range';
      reverbInput.min = '0';
      reverbInput.max = '1';
      reverbInput.step = '0.05';
      reverbInput.style.flex = '1';
      reverbInput.value = String(area.reverbIntensity ?? 0);
      const reverbVal = document.createElement('span');
      reverbVal.style.width = '32px';
      reverbVal.style.textAlign = 'right';
      reverbVal.textContent = (area.reverbIntensity ?? 0).toFixed(2);
      reverbInput.oninput = (): void => {
        const v = parseFloat(reverbInput.value);
        reverbVal.textContent = v.toFixed(2);
        area.reverbIntensity = v > 0 ? v : undefined;
      };
      reverbRow.append(reverbInput, reverbVal);
      areaInspector.appendChild(areaFieldRow('Reverb (cueva)', reverbRow));

      // SFX al entrar / salir
      const enterInput = mkSoundInput(area.enterSound, 'sonido al entrar', (v) => {
        area.enterSound = v;
      });
      areaInspector.appendChild(areaFieldRow('SFX entrar', enterInput));

      const leaveInput = mkSoundInput(area.leaveSound, 'sonido al salir', (v) => {
        area.leaveSound = v;
      });
      areaInspector.appendChild(areaFieldRow('SFX salir', leaveInput));

      // Ruidos ambientes: ids (coma) + intervalo [minMs, maxMs].
      const ambientIdsInput = document.createElement('input');
      ambientIdsInput.type = 'text';
      ambientIdsInput.style.flex = '1';
      ambientIdsInput.placeholder = 'ids separados por coma';
      ambientIdsInput.setAttribute('list', soundListId);
      ambientIdsInput.value = (area.ambientSounds?.ids ?? []).join(', ');

      const ambientMin = document.createElement('input');
      ambientMin.type = 'number';
      ambientMin.min = '200';
      ambientMin.style.width = '70px';
      ambientMin.placeholder = 'min ms';
      ambientMin.value = String(area.ambientSounds?.minMs ?? 8000);

      const ambientMax = document.createElement('input');
      ambientMax.type = 'number';
      ambientMax.min = '200';
      ambientMax.style.width = '70px';
      ambientMax.placeholder = 'max ms';
      ambientMax.value = String(area.ambientSounds?.maxMs ?? 20000);

      const syncAmbient = (): void => {
        const ids = ambientIdsInput.value
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
        if (ids.length === 0) {
          area.ambientSounds = undefined;
          return;
        }
        const min = parseInt(ambientMin.value, 10);
        const max = parseInt(ambientMax.value, 10);
        area.ambientSounds = {
          ids,
          minMs: Number.isNaN(min) ? 8000 : min,
          maxMs: Number.isNaN(max) ? 20000 : Math.max(min || 0, max),
        };
      };
      ambientIdsInput.oninput = syncAmbient;
      ambientMin.oninput = syncAmbient;
      ambientMax.oninput = syncAmbient;

      areaInspector.appendChild(areaFieldRow('Ambiente (ids)', ambientIdsInput));
      const ambientRangeRow = document.createElement('div');
      ambientRangeRow.style.display = 'flex';
      ambientRangeRow.style.gap = '6px';
      ambientRangeRow.append(ambientMin, ambientMax);
      areaInspector.appendChild(areaFieldRow('Intervalo', ambientRangeRow));

      // Requisitos de entrada
      const entryTitle = document.createElement('div');
      entryTitle.textContent = 'Entrada (requisitos)';
      entryTitle.style.fontSize = '11px';
      entryTitle.style.fontWeight = 'bold';
      entryTitle.style.marginTop = '4px';
      areaInspector.appendChild(entryTitle);

      const ensureEntry = (): NonNullable<MapAreaData['entry']> => {
        if (!area.entry) area.entry = {};
        return area.entry;
      };
      const cleanupEntry = (): void => {
        const e = area.entry;
        if (
          e &&
          !e.itemId &&
          (!e.classes || e.classes.length === 0) &&
          (!e.races || e.races.length === 0) &&
          e.timeStartHour === undefined &&
          e.timeEndHour === undefined
        ) {
          area.entry = undefined;
        }
      };

      const itemInput = document.createElement('input');
      itemInput.type = 'text';
      itemInput.value = area.entry?.itemId ?? '';
      itemInput.style.flex = '1';
      itemInput.placeholder = 'itemId';
      itemInput.oninput = (): void => {
        ensureEntry().itemId = itemInput.value || undefined;
        cleanupEntry();
      };
      areaInspector.appendChild(areaFieldRow('Item', itemInput));

      const qtyInput = document.createElement('input');
      qtyInput.type = 'number';
      qtyInput.min = '1';
      qtyInput.value = String(area.entry?.itemQuantity ?? 1);
      qtyInput.style.width = '70px';
      qtyInput.oninput = (): void => { ensureEntry().itemQuantity = parseInt(qtyInput.value, 10) || 1; };
      areaInspector.appendChild(areaFieldRow('Cantidad', qtyInput));

      const classesInput = document.createElement('input');
      classesInput.type = 'text';
      classesInput.value = (area.entry?.classes ?? []).join(', ');
      classesInput.style.flex = '1';
      classesInput.placeholder = CHARACTER_CLASSES.map((c) => c.id).join(',');
      classesInput.oninput = (): void => {
        const arr = parseCsv(classesInput.value);
        ensureEntry().classes =
          arr.length > 0 ? (arr as NonNullable<MapAreaData['entry']>['classes']) : undefined;
        cleanupEntry();
      };
      areaInspector.appendChild(areaFieldRow('Clases', classesInput));

      const racesInput = document.createElement('input');
      racesInput.type = 'text';
      racesInput.value = (area.entry?.races ?? []).join(', ');
      racesInput.style.flex = '1';
      racesInput.placeholder = CHARACTER_RACES.map((r) => r.id).join(',');
      racesInput.oninput = (): void => {
        const arr = parseCsv(racesInput.value);
        ensureEntry().races = arr.length > 0 ? (arr as NonNullable<MapAreaData['entry']>['races']) : undefined;
        cleanupEntry();
      };
      areaInspector.appendChild(areaFieldRow('Razas', racesInput));

      const timeStart = document.createElement('input');
      timeStart.type = 'number';
      timeStart.min = '0';
      timeStart.max = '23';
      timeStart.style.width = '60px';
      timeStart.value = area.entry?.timeStartHour !== undefined ? String(area.entry.timeStartHour) : '';
      const timeEnd = document.createElement('input');
      timeEnd.type = 'number';
      timeEnd.min = '0';
      timeEnd.max = '23';
      timeEnd.style.width = '60px';
      timeEnd.value = area.entry?.timeEndHour !== undefined ? String(area.entry.timeEndHour) : '';
      const applyTime = (): void => {
        const s = timeStart.value === '' ? undefined : Math.max(0, Math.min(23, parseInt(timeStart.value, 10)));
        const e = timeEnd.value === '' ? undefined : Math.max(0, Math.min(23, parseInt(timeEnd.value, 10)));
        const entry = ensureEntry();
        entry.timeStartHour = Number.isNaN(s as number) ? undefined : s;
        entry.timeEndHour = Number.isNaN(e as number) ? undefined : e;
        cleanupEntry();
      };
      timeStart.oninput = applyTime;
      timeEnd.oninput = applyTime;
      const timeRow = document.createElement('div');
      timeRow.style.display = 'flex';
      timeRow.style.alignItems = 'center';
      timeRow.style.gap = '6px';
      const timeLab = document.createElement('label');
      timeLab.textContent = 'Horario';
      timeLab.style.fontSize = '11px';
      timeLab.style.minWidth = '92px';
      const dash = document.createElement('span');
      dash.textContent = '–';
      timeRow.append(timeLab, timeStart, dash, timeEnd);
      areaInspector.appendChild(timeRow);

      // Rectángulos del área
      const rectsInfo = document.createElement('div');
      rectsInfo.style.fontSize = '11px';
      rectsInfo.style.color = '#aaa';
      rectsInfo.style.marginTop = '4px';
      rectsInfo.textContent = `Rectángulos: ${area.rects.length}`;
      areaInspector.appendChild(rectsInfo);

      drawAreaBtn.style.cursor = 'pointer';
      drawAreaBtn.onclick = (): void => {
        if (drawTarget === 'area') stopDrawing();
        else startDrawing('area');
      };
      areaInspector.appendChild(drawAreaBtn);

      const clearRectsBtn = document.createElement('button');
      clearRectsBtn.textContent = 'Limpiar rects';
      clearRectsBtn.style.cursor = 'pointer';
      clearRectsBtn.onclick = (): void => {
        area.rects = [];
        pushAreasToScene();
        renderAreaInspector();
      };
      areaInspector.appendChild(clearRectsBtn);

      // Zonas de respawn
      const zonesTitle = document.createElement('div');
      zonesTitle.textContent = 'Zonas de respawn';
      zonesTitle.style.fontSize = '11px';
      zonesTitle.style.fontWeight = 'bold';
      zonesTitle.style.marginTop = '6px';
      areaInspector.appendChild(zonesTitle);

      const zoneSelect = document.createElement('select');
      zoneSelect.size = 3;
      zoneSelect.style.width = '100%';
      for (const z of area.respawnZones ?? []) {
        const opt = document.createElement('option');
        opt.value = z.id;
        opt.textContent = `${z.name ?? z.id} (${z.rects.length} rect)`;
        zoneSelect.appendChild(opt);
      }
      if (selectedZoneId) zoneSelect.value = selectedZoneId;
      zoneSelect.onchange = (): void => {
        selectedZoneId = zoneSelect.value || null;
        if (drawTarget === 'zone') stopDrawing();
      };
      areaInspector.appendChild(zoneSelect);

      const zoneBtns = document.createElement('div');
      zoneBtns.style.display = 'flex';
      zoneBtns.style.gap = '6px';
      const newZoneBtn = document.createElement('button');
      newZoneBtn.textContent = '+ Zona';
      newZoneBtn.style.cursor = 'pointer';
      newZoneBtn.onclick = (): void => {
        if (!area.respawnZones) area.respawnZones = [];
        const zone: AreaRespawnZone = { id: genZoneId(), name: 'Zona', rects: [] };
        area.respawnZones.push(zone);
        selectedZoneId = zone.id;
        pushAreasToScene();
        renderAreaInspector();
      };
      const delZoneBtn = document.createElement('button');
      delZoneBtn.textContent = '🗑 Zona';
      delZoneBtn.style.cursor = 'pointer';
      delZoneBtn.onclick = (): void => {
        if (!selectedZoneId || !area.respawnZones) return;
        const i = area.respawnZones.findIndex((z) => z.id === selectedZoneId);
        if (i >= 0) area.respawnZones.splice(i, 1);
        selectedZoneId = null;
        if (drawTarget === 'zone') stopDrawing();
        pushAreasToScene();
        renderAreaInspector();
      };
      zoneBtns.append(newZoneBtn, delZoneBtn);
      areaInspector.appendChild(zoneBtns);

      const selectedZone = (area.respawnZones ?? []).find((z) => z.id === selectedZoneId) ?? null;
      if (selectedZone) {
        const zoneNameInput = document.createElement('input');
        zoneNameInput.type = 'text';
        zoneNameInput.value = selectedZone.name ?? '';
        zoneNameInput.style.flex = '1';
        zoneNameInput.oninput = (): void => {
          selectedZone.name = zoneNameInput.value || undefined;
        };
        areaInspector.appendChild(areaFieldRow('Nombre zona', zoneNameInput));

        drawZoneBtn.style.cursor = 'pointer';
        drawZoneBtn.onclick = (): void => {
          if (drawTarget === 'zone') stopDrawing();
          else startDrawing('zone');
        };
        areaInspector.appendChild(drawZoneBtn);
      }
    }

    areaPanelContent.append(areaSelect, areaButtonsRow, showAreasRow, areaInspector);
    renderAreaInspector();

    // =====================================================================
    // ---- Sección "Cardúmenes" del dock lateral (pesca) ----
    // =====================================================================
    const fishPanelContent = document.createElement('div');
    fishPanelContent.style.display = 'flex';
    fishPanelContent.style.flexDirection = 'column';
    fishPanelContent.style.flex = '1';
    fishPanelContent.style.overflowY = 'auto';
    skinScrollable(fishPanelContent);
    fishPanelContent.style.padding = '8px';
    fishPanelContent.style.gap = '6px';
    editorDock.addSection({
      id: 'fishing',
      title: '🎣 Cardúmenes',
      body: fishPanelContent,
      preferredWidth: 300,
    });

    const genFishId = (): string =>
      `fishspot_${Date.now().toString(36)}_${Math.floor(Math.random() * 1e4).toString(36)}`;
    const getSelectedFish = (): MapFishingSpot | null =>
      fishingSpotPlacements.find((s) => s.id === selectedFishSpotId) ?? null;

    const pushFishingToScene = (): void => {
      if (currentMap) {
        currentMap.fishingSpots = fishingSpotPlacements.length > 0 ? fishingSpotPlacements : undefined;
      }
      if (editorGame) {
        const scene = editorGame.scene.getScene('MapEditorScene') as MapEditorScene;
        if (scene) scene.updateFishingSpots(fishingSpotPlacements);
      }
    };

    const fishSelect = document.createElement('select');
    fishSelect.size = 4;
    fishSelect.style.width = '100%';
    const refreshFishList = (): void => {
      fishSelect.innerHTML = '';
      for (const s of fishingSpotPlacements) {
        const opt = document.createElement('option');
        const sp = FISH_SPECIES.find((f) => `fish_node_${f.species}` === s.typeId);
        opt.value = s.id;
        opt.textContent = `${sp?.label ?? s.typeId} [${s.rect.w}×${s.rect.h}]`;
        fishSelect.appendChild(opt);
      }
      if (selectedFishSpotId) fishSelect.value = selectedFishSpotId;
    };

    // Inspector: dropdown de especie + dibujar área.
    const fishInspector = document.createElement('div');
    fishInspector.style.display = 'flex';
    fishInspector.style.flexDirection = 'column';
    fishInspector.style.gap = '4px';
    fishInspector.style.borderTop = '1px solid #444';
    fishInspector.style.paddingTop = '6px';

    const drawFishBtn = document.createElement('button');
    const stopFishingDraw = (): void => {
      fishingDrawActive = false;
      if (editorGame) {
        const scene = editorGame.scene.getScene('MapEditorScene') as MapEditorScene;
        if (scene) scene.setAreaDrawMode(false);
      }
      drawFishBtn.textContent = '✏️ Dibujar área';
      drawFishBtn.style.background = '';
    };
    const refreshFishingUI = (): void => {
      refreshFishList();
      renderFishInspector();
    };
    onFishingRectDone = (): void => {
      stopFishingDraw();
      pushFishingToScene();
      refreshFishingUI();
    };

    function renderFishInspector(): void {
      fishInspector.innerHTML = '';
      const spot = getSelectedFish();
      if (!spot) {
        const hint = document.createElement('div');
        hint.textContent = 'Creá un cardumen y dibujá su área de agua.';
        hint.style.fontSize = '11px';
        hint.style.opacity = '0.7';
        fishInspector.append(hint);
        return;
      }
      const speciesSel = document.createElement('select');
      speciesSel.style.width = '100%';
      for (const f of FISH_SPECIES) {
        const opt = document.createElement('option');
        opt.value = `fish_node_${f.species}`;
        opt.textContent = `${f.label} (nivel ${f.fishLevel})`;
        speciesSel.appendChild(opt);
      }
      speciesSel.value = spot.typeId;
      speciesSel.onchange = (): void => {
        spot.typeId = speciesSel.value;
        refreshFishList();
        pushFishingToScene();
      };
      drawFishBtn.textContent = fishingDrawActive ? '■ Dibujando…' : '✏️ Dibujar área';
      drawFishBtn.style.cursor = 'pointer';
      drawFishBtn.onclick = (): void => {
        if (fishingDrawActive) {
          stopFishingDraw();
        } else {
          fishingDrawActive = true;
          if (editorGame) {
            const scene = editorGame.scene.getScene('MapEditorScene') as MapEditorScene;
            if (scene) scene.setAreaDrawMode(true);
          }
          drawFishBtn.textContent = '■ Dibujando…';
          drawFishBtn.style.background = '#2a6';
        }
      };
      const rectInfo = document.createElement('div');
      rectInfo.style.fontSize = '11px';
      rectInfo.style.opacity = '0.8';
      rectInfo.textContent = `Área: ${spot.rect.w}×${spot.rect.h} en (${spot.rect.x}, ${spot.rect.y})`;
      fishInspector.append(fishFieldRow('Pez', speciesSel), drawFishBtn, rectInfo);
    }

    const fishFieldRow = (labelText: string, el: HTMLElement): HTMLDivElement => {
      const row = document.createElement('div');
      row.style.display = 'flex';
      row.style.alignItems = 'center';
      row.style.gap = '6px';
      const lab = document.createElement('label');
      lab.textContent = labelText;
      lab.style.fontSize = '11px';
      lab.style.minWidth = '40px';
      row.append(lab, el);
      return row;
    };

    fishSelect.onchange = (): void => {
      selectedFishSpotId = fishSelect.value || null;
      stopFishingDraw();
      renderFishInspector();
    };

    const fishButtonsRow = document.createElement('div');
    fishButtonsRow.style.display = 'flex';
    fishButtonsRow.style.gap = '6px';
    const newFishBtn = document.createElement('button');
    newFishBtn.textContent = '+ Cardumen';
    newFishBtn.style.cursor = 'pointer';
    newFishBtn.onclick = (): void => {
      const spot: MapFishingSpot = {
        id: genFishId(),
        typeId: `fish_node_${FISH_SPECIES[0].species}`,
        rect: { x: selectedTile.x, y: selectedTile.y, w: 3, h: 3 },
      };
      fishingSpotPlacements.push(spot);
      selectedFishSpotId = spot.id;
      refreshFishList();
      renderFishInspector();
      pushFishingToScene();
    };
    const delFishBtn = document.createElement('button');
    delFishBtn.textContent = '🗑 Cardumen';
    delFishBtn.style.cursor = 'pointer';
    delFishBtn.onclick = (): void => {
      if (!selectedFishSpotId) return;
      const idx = fishingSpotPlacements.findIndex((s) => s.id === selectedFishSpotId);
      if (idx >= 0) fishingSpotPlacements.splice(idx, 1);
      selectedFishSpotId = null;
      stopFishingDraw();
      refreshFishList();
      renderFishInspector();
      pushFishingToScene();
    };
    fishButtonsRow.append(newFishBtn, delFishBtn);

    fishPanelContent.append(fishSelect, fishButtonsRow, fishInspector);
    renderFishInspector();

    // ---- Sección "Superficies" del dock lateral ----
    const surfacePanelContent = document.createElement('div');
    surfacePanelContent.style.display = 'flex';
    surfacePanelContent.style.flexDirection = 'column';
    surfacePanelContent.style.flex = '1';
    surfacePanelContent.style.overflowY = 'auto';
    skinScrollable(surfacePanelContent);
    surfacePanelContent.style.overflowX = 'hidden';
    surfacePanelContent.style.padding = '8px';
    surfacePanelContent.style.gap = '8px';

    editorDock.addSection({
      id: 'surfaces',
      title: '🎨 Superficies',
      body: surfacePanelContent,
      preferredWidth: 600,
    });

    // Selector de capa
    const layerRow = document.createElement('div');
    layerRow.style.display = 'flex';
    layerRow.style.alignItems = 'center';
    layerRow.style.gap = '8px';
    
    const layerLabel = document.createElement('span');
    layerLabel.textContent = 'Capa:';
    layerLabel.style.fontSize = '12px';
    layerLabel.style.minWidth = '40px';
    
    const layerSelect = document.createElement('select');
    layerSelect.style.flex = '1';
    layerSelect.style.padding = '4px';
    layerSelect.style.background = '#222';
    layerSelect.style.color = '#eee';
    layerSelect.style.border = '1px solid #444';
    layerSelect.style.borderRadius = '4px';
    
    const layers = [
      { id: 'layer1_ground', name: '1 - Suelo (base)', shortcut: '1' },
      { id: 'layer2_floor_objects', name: '2 - Objetos bajos', shortcut: '2' },
      { id: 'layer3_solid', name: '3 - Objetos sólidos', shortcut: '3' },
      { id: 'layer4_roof', name: '4 - Sobre personaje', shortcut: '4' },
    ];
    
    layers.forEach(layer => {
      const opt = document.createElement('option');
      opt.value = layer.id;
      opt.textContent = layer.name;
      layerSelect.appendChild(opt);
    });
    
    // Evento para cuando cambie la capa
    layerSelect.onchange = () => {
      if (selectedSurfaceId && editorGame) {
        const scene = editorGame.scene.getScene('MapEditorScene') as MapEditorScene;
        if (scene) {
          scene.setSelectedSurface(selectedSurfaceId, layerSelect.value);
        }
      }
    };
    
    layerRow.append(layerLabel, layerSelect);
    surfacePanelContent.appendChild(layerRow);
    
    // Estado de superficie seleccionada
    let selectedSurfaceId: string | null = null;
    
    // Contenedor principal con lista a la izquierda y preview a la derecha
    const surfaceListPreviewContainer = document.createElement('div');
    surfaceListPreviewContainer.style.display = 'flex';
    surfaceListPreviewContainer.style.flexDirection = 'row';
    surfaceListPreviewContainer.style.flex = '1';
    surfaceListPreviewContainer.style.gap = '8px';
    surfaceListPreviewContainer.style.minHeight = '0'; // Para que flex funcione correctamente
    
    // Selector de superficies agrupadas por tipo (lista a la izquierda)
    const surfaceListContainer = document.createElement('div');
    surfaceListContainer.style.flex = '1';
    surfaceListContainer.style.minWidth = '0';
    surfaceListContainer.style.overflowY = 'auto';
    skinScrollable(surfaceListContainer);
    surfaceListContainer.style.overflowX = 'hidden';
    surfaceListContainer.style.display = 'flex';
    surfaceListContainer.style.flexDirection = 'column';
    surfaceListContainer.style.gap = '0';
    surfaceListContainer.style.background = '#1a1a1a';
    surfaceListContainer.style.border = '1px solid #333';
    surfaceListContainer.style.borderRadius = '4px';
    surfaceListContainer.style.padding = '4px';
    surfaceListContainer.innerHTML = '<div style="color: #666; font-size: 11px; padding: 8px;">Carga un mapa para ver superficies</div>';
    
    // Área de preview de la superficie seleccionada (preview a la derecha)
    const surfacePreviewContainer = document.createElement('div');
    surfacePreviewContainer.style.width = '240px';
    surfacePreviewContainer.style.flexShrink = '0';
    surfacePreviewContainer.style.minHeight = '200px';
    surfacePreviewContainer.style.background = '#0a0a0a';
    surfacePreviewContainer.style.border = '1px solid #333';
    surfacePreviewContainer.style.borderRadius = '4px';
    surfacePreviewContainer.style.display = 'flex';
    surfacePreviewContainer.style.alignItems = 'center';
    surfacePreviewContainer.style.justifyContent = 'center';
    surfacePreviewContainer.style.overflow = 'hidden';
    surfacePreviewContainer.style.position = 'relative';
    
    const surfacePreviewImage = document.createElement('img');
    surfacePreviewImage.style.maxWidth = '100%';
    surfacePreviewImage.style.maxHeight = '100%';
    surfacePreviewImage.style.objectFit = 'contain';
    surfacePreviewImage.style.imageRendering = 'pixelated';
    surfacePreviewImage.style.display = 'none';
    
    const surfacePreviewPlaceholder = document.createElement('div');
    surfacePreviewPlaceholder.style.color = '#444';
    surfacePreviewPlaceholder.style.fontSize = '11px';
    surfacePreviewPlaceholder.style.textAlign = 'center';
    surfacePreviewPlaceholder.innerHTML = '📷<br>Selecciona una superficie<br>para ver preview';
    
    const surfacePreviewError = document.createElement('div');
    surfacePreviewError.style.color = '#f55';
    surfacePreviewError.style.fontSize = '10px';
    surfacePreviewError.style.position = 'absolute';
    surfacePreviewError.style.bottom = '4px';
    surfacePreviewError.style.left = '4px';
    surfacePreviewError.style.right = '4px';
    surfacePreviewError.style.textAlign = 'center';
    surfacePreviewError.style.display = 'none';
    
    surfacePreviewContainer.append(surfacePreviewImage, surfacePreviewPlaceholder, surfacePreviewError);
    
    // Agregar lista y preview al contenedor horizontal
    surfaceListPreviewContainer.appendChild(surfaceListContainer);
    surfaceListPreviewContainer.appendChild(surfacePreviewContainer);
    surfacePanelContent.appendChild(surfaceListPreviewContainer);
    
    // Información de superficie seleccionada con botón de deseleccionar
    const selectedSurfaceRow = document.createElement('div');
    selectedSurfaceRow.style.display = 'flex';
    selectedSurfaceRow.style.alignItems = 'center';
    selectedSurfaceRow.style.gap = '8px';
    
    const selectedSurfaceInfo = document.createElement('div');
    selectedSurfaceInfo.style.flex = '1';
    selectedSurfaceInfo.style.padding = '6px';
    selectedSurfaceInfo.style.background = '#1a1a1a';
    selectedSurfaceInfo.style.borderRadius = '4px';
    selectedSurfaceInfo.style.fontSize = '11px';
    selectedSurfaceInfo.style.color = '#888';
    selectedSurfaceInfo.textContent = 'Ninguna superficie seleccionada';
    
    const deselectButton = document.createElement('button');
    deselectButton.textContent = '✕';
    deselectButton.style.padding = '4px 8px';
    deselectButton.style.cursor = 'pointer';
    deselectButton.style.background = '#333';
    deselectButton.style.color = '#fff';
    deselectButton.style.border = 'none';
    deselectButton.style.borderRadius = '4px';
    deselectButton.style.fontSize = '12px';
    deselectButton.title = 'Deseleccionar superficie';
    
    // El onclick del deselectButton se define más abajo, después de definir clearSurfacePreviewImage
    
    selectedSurfaceRow.append(selectedSurfaceInfo, deselectButton);
    surfacePanelContent.appendChild(selectedSurfaceRow);
    
    // Función para actualizar el preview de la superficie
    const updateSurfacePreviewImage = (surfaceId: string, surface: SurfaceDefinition) => {
      // Construir la URL del atlas: dungeons/… → world/dungeons/; pattern1..8 y
      // floors1..3 → world/floors/; resto → pattern/. (Igual que surfaceAtlasUrl.)
      const atlasUrl = surface.atlasFile.startsWith('dungeons/')
        ? `/assets/world/${surface.atlasFile}`
        : /^(pattern[1-8]|floors[1-3])\.png$/i.test(surface.atlasFile)
          ? `/assets/world/floors/${surface.atlasFile}`
          : `/assets/pattern/${surface.atlasFile}`;
      
      // Calcular las dimensiones del recorte
      const tileSize = 32; // TILE_SIZE
      const tilesX = surface.tilesX || 1;
      const tilesY = surface.tilesY || 1;
      const cropX = surface.gridX * tileSize;
      const cropY = surface.gridY * tileSize;
      const cropWidth = tilesX * tileSize;
      const cropHeight = tilesY * tileSize;
      
      // Cargar la imagen del atlas y recortarla
      const atlasImg = new Image();
      atlasImg.crossOrigin = 'anonymous';
      atlasImg.onload = () => {
        // Crear un canvas para recortar
        const canvas = document.createElement('canvas');
        canvas.width = cropWidth;
        canvas.height = cropHeight;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(
            atlasImg,
            cropX, cropY, cropWidth, cropHeight,
            0, 0, cropWidth, cropHeight
          );
          
          // Mostrar el preview
          surfacePreviewImage.src = canvas.toDataURL();
          surfacePreviewImage.style.display = 'block';
          surfacePreviewPlaceholder.style.display = 'none';
          surfacePreviewError.style.display = 'none';
          
          // Ajustar altura del contenedor según el tamaño
          const aspectRatio = cropHeight / cropWidth;
          const containerWidth = surfacePreviewContainer.clientWidth - 20;
          let previewHeight = Math.min(280, containerWidth * aspectRatio);
          previewHeight = Math.max(80, previewHeight);
          surfacePreviewContainer.style.minHeight = `${previewHeight + 20}px`;
        }
      };
      atlasImg.onerror = () => {
        surfacePreviewImage.style.display = 'none';
        surfacePreviewPlaceholder.style.display = 'block';
        surfacePreviewPlaceholder.innerHTML = '❌<br>Error cargando atlas';
        surfacePreviewError.textContent = `No se pudo cargar: ${atlasUrl}`;
        surfacePreviewError.style.display = 'block';
      };
      atlasImg.src = atlasUrl;
    };
    
    // Función para limpiar el preview
    const clearSurfacePreviewImage = () => {
      surfacePreviewImage.style.display = 'none';
      surfacePreviewImage.src = '';
      surfacePreviewPlaceholder.style.display = 'block';
      surfacePreviewPlaceholder.innerHTML = '📷<br>Selecciona una superficie<br>para ver preview';
      surfacePreviewError.style.display = 'none';
      surfacePreviewContainer.style.minHeight = '120px';
    };
    
    // Ahora definimos el onclick del deselectButton (después de tener clearSurfacePreviewImage)
    deselectButton.onclick = () => {
      selectedSurfaceId = null;
      selectedSurfaceInfo.textContent = 'Ninguna superficie seleccionada';
      selectedSurfaceInfo.style.color = '#888';
      placeSurfaceButton.disabled = true;
      placeSurfaceButton.style.opacity = '0.5';
      
      // Limpiar preview de imagen
      clearSurfacePreviewImage();
      
      // Deseleccionar visualmente
      surfaceListContainer.querySelectorAll('.surface-selected').forEach(el => {
        (el as HTMLElement).classList.remove('surface-selected');
        (el as HTMLElement).style.background = '#222';
        (el as HTMLElement).style.borderLeft = '2px solid transparent';
      });
      
      // Limpiar preview en la escena
      if (editorGame) {
        const scene = editorGame.scene.getScene('MapEditorScene') as MapEditorScene;
        if (scene) {
          scene.setSelectedSurface(null, layerSelect.value);
        }
      }
    };
    
    // Botón para colocar superficie
    const placeSurfaceButton = document.createElement('button');
    placeSurfaceButton.textContent = '📍 Colocar en tile seleccionado';
    placeSurfaceButton.style.padding = '8px';
    placeSurfaceButton.style.cursor = 'pointer';
    placeSurfaceButton.style.background = '#2d6a4f';
    placeSurfaceButton.style.color = '#fff';
    placeSurfaceButton.style.border = 'none';
    placeSurfaceButton.style.borderRadius = '4px';
    placeSurfaceButton.disabled = true;
    placeSurfaceButton.style.opacity = '0.5';
    
    placeSurfaceButton.onclick = () => {
      if (!selectedSurfaceId || !currentMap || !editorGame) return;
      
      const scene = editorGame.scene.getScene('MapEditorScene') as MapEditorScene;
      if (scene) {
        const layerName = layerSelect.value;
        scene.placeTile(selectedTile.x, selectedTile.y, selectedSurfaceId, layerName);
        refreshTileInfo();
        refreshMinimap();
      }
    };
    
    surfacePanelContent.appendChild(placeSurfaceButton);
    
    // Botón para eliminar tile
    const removeTileButton = document.createElement('button');
    removeTileButton.textContent = '🗑️ Eliminar tile de esta capa';
    removeTileButton.style.padding = '8px';
    removeTileButton.style.cursor = 'pointer';
    removeTileButton.style.background = '#8b0000';
    removeTileButton.style.color = '#fff';
    removeTileButton.style.border = 'none';
    removeTileButton.style.borderRadius = '4px';
    
    removeTileButton.onclick = () => {
      if (!currentMap || !editorGame) return;
      
      const scene = editorGame.scene.getScene('MapEditorScene') as MapEditorScene;
      if (scene) {
        const layerName = layerSelect.value;
        scene.removeTile(selectedTile.x, selectedTile.y, layerName);
        refreshTileInfo();
        refreshMinimap();
      }
    };
    
    surfacePanelContent.appendChild(removeTileButton);
    
    // Función para actualizar la lista de superficies y decoraciones
    const refreshSurfaceList = (): void => {
      if (!editorGame) {
        surfaceListContainer.innerHTML = '<div style="color: #666; font-size: 11px;">Carga un mapa para ver superficies</div>';
        return;
      }
      
      const scene = editorGame.scene.getScene('MapEditorScene') as MapEditorScene;
      if (!scene) return;
      
      const surfacesByTypeAndSubtype = scene.getSurfacesByTypeAndSubtype();
      const decorationIndex = scene.getDecorationIndex();
      const types = Object.keys(surfacesByTypeAndSubtype);
      const hasDecorations = decorationIndex && Object.keys(decorationIndex).length > 0;
      
      if (types.length === 0 && !hasDecorations) {
        surfaceListContainer.innerHTML = '<div style="color: #666; font-size: 11px;">No hay superficies indexadas.<br>Usa el indexador 🎨 para crear.</div>';
        return;
      }
      
      surfaceListContainer.innerHTML = '';

      // Helper: item de decoración clickeable (mismo path de selección que las superficies).
      const makeDecoItem = (id: string, deco: { name: string; type: string }): HTMLElement => {
        const decoItem = document.createElement('div');
        decoItem.style.cssText = 'padding:6px 8px;background:#222;font-size:11px;cursor:pointer;border-left:2px solid transparent;transition:all 0.15s;user-select:none;';
        decoItem.textContent = `🚪 ${deco.name}`;
        decoItem.title = `ID: ${id}\nTipo: ${deco.type}`;
        decoItem.onclick = () => {
          surfaceListContainer.querySelectorAll('.surface-selected').forEach(el => {
            (el as HTMLElement).classList.remove('surface-selected');
            (el as HTMLElement).style.background = '#222';
            (el as HTMLElement).style.borderLeft = '2px solid transparent';
          });
          decoItem.classList.add('surface-selected');
          decoItem.style.background = '#4a3a1a';
          decoItem.style.borderLeft = '2px solid #fbbf24';
          selectedSurfaceId = id;
          selectedSurfaceInfo.innerHTML = `<strong style="color: #fbbf24;">🚪 ${deco.name}</strong><br>ID: ${id}`;
          placeSurfaceButton.disabled = false;
          placeSurfaceButton.style.opacity = '1';
          updateDecorationPreviewImage(id);
          if (editorGame) {
            const sc = editorGame.scene.getScene('MapEditorScene') as MapEditorScene;
            if (sc) sc.setSelectedSurface(id, layerSelect.value);
          }
        };
        decoItem.onmouseenter = () => { if (!decoItem.classList.contains('surface-selected')) decoItem.style.background = '#2a2a2a'; };
        decoItem.onmouseleave = () => { if (!decoItem.classList.contains('surface-selected')) decoItem.style.background = '#222'; };
        return decoItem;
      };

      // Categoría "Dungeons" (lazy): anida los subtipos de dungeon en 2 niveles.
      let dungeonCategoryBody: HTMLElement | null = null;
      const ensureDungeonCategory = (): HTMLElement => {
        if (dungeonCategoryBody) return dungeonCategoryBody;
        const cat = document.createElement('div');
        const catTitle = document.createElement('div');
        let catExpanded = false;
        catTitle.innerHTML = `<span style="display:inline-block;width:12px;text-align:center;">▶</span> 🏰 Dungeons`;
        catTitle.style.cssText = 'font-weight:bold;font-size:13px;color:#f0abfc;padding:8px 6px 4px 6px;background:#0a0a0a;user-select:none;cursor:pointer;border-bottom:1px solid #333;margin-top:6px;';
        const body = document.createElement('div');
        body.style.display = 'none';
        body.style.paddingLeft = '10px';
        catTitle.onclick = () => {
          catExpanded = !catExpanded;
          const a = catTitle.querySelector('span');
          body.style.display = catExpanded ? 'block' : 'none';
          if (a) a.textContent = catExpanded ? '▼' : '▶';
        };
        cat.appendChild(catTitle);
        cat.appendChild(body);
        surfaceListContainer.appendChild(cat);
        dungeonCategoryBody = body;
        return body;
      };

      // === Lista vertical con todas las superficies agrupadas por subtipo ===
      types.forEach(type => {
        const subtypes = Object.keys(surfacesByTypeAndSubtype[type]);
        
        subtypes.forEach(subtype => {
          // Contenedor para el grupo de superficies de este subtipo
          const subtypeGroupContainer = document.createElement('div');
          subtypeGroupContainer.className = 'subtype-group';
          
          // Título de subtipo (toggleable, colapsado por defecto)
          const subtypeTitle = document.createElement('div');
          const subtypeLabel = subtype.charAt(0).toUpperCase() + subtype.slice(1);
          const subtypeIcon = type === 'dungeon' ? '🗝️' :
                              subtype === 'grass' ? '🌱' :
                              subtype === 'sand' ? '🏜️' :
                              subtype === 'path' ? '🛤️' :
                              subtype === 'snow' ? '❄️' : '🎨';
          
          // Estado colapsado por defecto
          let isExpanded = false;
          const toggleArrow = '▶'; // Flecha hacia la derecha (colapsado)
          subtypeTitle.innerHTML = `<span style="display: inline-block; width: 12px; text-align: center;">${toggleArrow}</span> ${subtypeIcon} ${subtypeLabel}`;
          subtypeTitle.style.fontWeight = 'bold';
          subtypeTitle.style.fontSize = '12px';
          subtypeTitle.style.color = type === 'dungeon' ? '#e9d5ff' :
                                      subtype === 'grass' ? '#86efac' :
                                      subtype === 'sand' ? '#fcd34d' :
                                      subtype === 'path' ? '#c084fc' :
                                      subtype === 'snow' ? '#93c5fd' : '#94a3b8';
          subtypeTitle.style.padding = '8px 6px 4px 6px';
          subtypeTitle.style.background = '#0a0a0a';
          subtypeTitle.style.userSelect = 'none';
          subtypeTitle.style.cursor = 'pointer';
          subtypeTitle.style.borderBottom = '1px solid #333';
          subtypeTitle.style.marginTop = '4px';
          subtypeTitle.onmouseenter = () => {
            subtypeTitle.style.background = '#1a1a1a';
          };
          subtypeTitle.onmouseleave = () => {
            subtypeTitle.style.background = '#0a0a0a';
          };
          
          // Contenedor para las superficies (oculto por defecto)
          const surfacesContainer = document.createElement('div');
          surfacesContainer.style.display = 'none'; // Colapsado por defecto
          
          // Función para toggle
          subtypeTitle.onclick = () => {
            isExpanded = !isExpanded;
            const arrowSpan = subtypeTitle.querySelector('span');
            if (isExpanded) {
              surfacesContainer.style.display = 'block';
              if (arrowSpan) arrowSpan.textContent = '▼'; // Flecha hacia abajo (expandido)
            } else {
              surfacesContainer.style.display = 'none';
              if (arrowSpan) arrowSpan.textContent = '▶'; // Flecha hacia la derecha (colapsado)
            }
          };
          
          subtypeGroupContainer.appendChild(subtypeTitle);
          
          // Lista de superficies para este subtipo
          surfacesByTypeAndSubtype[type][subtype].forEach(({ id, surface }) => {
            const surfaceItem = document.createElement('div');
            surfaceItem.style.padding = '6px 8px';
            surfaceItem.style.background = '#222';
            surfaceItem.style.fontSize = '11px';
            surfaceItem.style.cursor = 'pointer';
            surfaceItem.style.borderLeft = '2px solid transparent';
            surfaceItem.style.transition = 'all 0.15s';
            surfaceItem.style.userSelect = 'none';
            
            const tilesX = surface.tilesX || 1;
            const tilesY = surface.tilesY || 1;
            const sizeLabel = (tilesX > 1 || tilesY > 1) ? ` [${tilesX}x${tilesY}]` : '';
            surfaceItem.textContent = `${surface.name}${sizeLabel}`;
            surfaceItem.title = `ID: ${id}\nTipo: ${surface.type}${surface.subtype ? ` / ${surface.subtype}` : ''}\nAtlas: ${surface.atlasFile}\nGrid: (${surface.gridX}, ${surface.gridY})`;
            
            surfaceItem.onclick = () => {
              // Deseleccionar anteriores
              surfaceListContainer.querySelectorAll('.surface-selected').forEach(el => {
                (el as HTMLElement).classList.remove('surface-selected');
                (el as HTMLElement).style.background = '#222';
                (el as HTMLElement).style.borderLeft = '2px solid transparent';
              });
              
              // Seleccionar este
              surfaceItem.classList.add('surface-selected');
              surfaceItem.style.background = '#1a4a3a';
              surfaceItem.style.borderLeft = '2px solid #4ade80';
              
              selectedSurfaceId = id;
              selectedSurfaceInfo.innerHTML = `<strong style="color: #4ade80;">${surface.name}</strong><br>ID: ${id}${sizeLabel}`;
              placeSurfaceButton.disabled = false;
              placeSurfaceButton.style.opacity = '1';
              
              // Actualizar preview de imagen en el panel
              updateSurfacePreviewImage(id, surface);
              
              // Actualizar preview en la escena
              if (editorGame) {
                const scene = editorGame.scene.getScene('MapEditorScene') as MapEditorScene;
                if (scene) {
                  scene.setSelectedSurface(id, layerSelect.value);
                }
              }
            };
            
            surfaceItem.onmouseenter = () => {
              if (!surfaceItem.classList.contains('surface-selected')) {
                surfaceItem.style.background = '#2a2a2a';
              }
            };
            
            surfaceItem.onmouseleave = () => {
              if (!surfaceItem.classList.contains('surface-selected')) {
                surfaceItem.style.background = '#222';
              }
            };
            
            surfacesContainer.appendChild(surfaceItem);
          });
          
          // Dungeons: sumar las ENTRADAS (decoraciones) dentro del grupo del dungeon.
          if (type === 'dungeon' && decorationIndex) {
            Object.entries(decorationIndex)
              .filter(([, d]) => (d as { type?: string; subtype?: string }).type === 'entrance'
                && (d as { subtype?: string }).subtype === subtype)
              .forEach(([decoId, deco]) =>
                surfacesContainer.appendChild(makeDecoItem(decoId, deco as { name: string; type: string })));
          }

          subtypeGroupContainer.appendChild(surfacesContainer);
          // Dungeons van anidados bajo la categoría "Dungeons"; el resto, planos.
          if (type === 'dungeon') {
            ensureDungeonCategory().appendChild(subtypeGroupContainer);
          } else {
            surfaceListContainer.appendChild(subtypeGroupContainer);
          }
        });
      });
      
      // === Decoraciones (unificadas como superficies) ===
      if (hasDecorations && decorationIndex) {
        // Agrupar decoraciones por tipo
        const decosByType: Record<string, Array<{ id: string; deco: { name: string; type: string } }>> = {};
        
        Object.entries(decorationIndex).forEach(([id, deco]) => {
          // Las entradas de dungeon (con subtype) ya se muestran dentro de su grupo de dungeon.
          if ((deco as { type?: string; subtype?: string }).type === 'entrance'
            && (deco as { subtype?: string }).subtype) return;
          const type = deco.type || 'misc';
          if (!decosByType[type]) {
            decosByType[type] = [];
          }
          decosByType[type].push({ id, deco });
        });
        
        Object.entries(decosByType).forEach(([type, decos]) => {
          // Casas: anidar por subtype (cada casa = un grupo colapsable con su colección
          // de partes), igual que el patrón de "Dungeons". Así la paleta queda agrupada
          // "por casa" en vez de una lista plana con las 14 partes mezcladas.
          if (type === 'house') {
            const catContainer = document.createElement('div');
            const catTitle = document.createElement('div');
            let catExpanded = false;
            catTitle.innerHTML = `<span style="display:inline-block;width:12px;text-align:center;">▶</span> 🏠 Casas`;
            catTitle.style.cssText = 'font-weight:bold;font-size:13px;color:#fbbf24;padding:8px 6px 4px 6px;background:#0a0a0a;user-select:none;cursor:pointer;border-bottom:1px solid #333;margin-top:6px;';
            const catBody = document.createElement('div');
            catBody.style.display = 'none';
            catBody.style.paddingLeft = '10px';
            catTitle.onclick = () => {
              catExpanded = !catExpanded;
              const a = catTitle.querySelector('span');
              catBody.style.display = catExpanded ? 'block' : 'none';
              if (a) a.textContent = catExpanded ? '▼' : '▶';
            };
            catContainer.appendChild(catTitle);
            catContainer.appendChild(catBody);

            // Agrupar las partes por subtype (houseN)
            const bySubtype: Record<string, Array<{ id: string; deco: { name: string; type: string } }>> = {};
            decos.forEach(({ id, deco }) => {
              const sub = (deco as { type?: string; subtype?: string }).subtype || 'otras';
              if (!bySubtype[sub]) bySubtype[sub] = [];
              bySubtype[sub].push({ id, deco });
            });

            Object.keys(bySubtype).sort().forEach((sub) => {
              const subContainer = document.createElement('div');
              const subTitle = document.createElement('div');
              let subExpanded = false;
              const label = sub.charAt(0).toUpperCase() + sub.slice(1);
              subTitle.innerHTML = `<span style="display:inline-block;width:12px;text-align:center;">▶</span> 🏠 ${label}`;
              subTitle.style.cssText = 'font-weight:bold;font-size:12px;color:#fcd34d;padding:6px 6px 4px 6px;background:#0a0a0a;user-select:none;cursor:pointer;border-bottom:1px solid #333;margin-top:4px;';
              const subBody = document.createElement('div');
              subBody.style.display = 'none';
              subTitle.onclick = () => {
                subExpanded = !subExpanded;
                const a = subTitle.querySelector('span');
                subBody.style.display = subExpanded ? 'block' : 'none';
                if (a) a.textContent = subExpanded ? '▼' : '▶';
              };
              // Item "casa entera": estampa todas las partes en sus capas + la colisión
              // de la huella de un solo click en el mapa (en vez de parte por parte).
              const wholeItem = document.createElement('div');
              wholeItem.style.cssText = 'padding:6px 8px;background:#2a2410;font-size:11px;cursor:pointer;border-left:2px solid transparent;transition:all 0.15s;user-select:none;font-weight:bold;color:#fde68a;';
              wholeItem.textContent = '🏠 Colocar casa entera';
              wholeItem.title = `Estampa ${label} completa (cuerpo + techo + colisión) con un click en el mapa`;
              wholeItem.onclick = () => {
                surfaceListContainer.querySelectorAll('.surface-selected').forEach(el => {
                  (el as HTMLElement).classList.remove('surface-selected');
                  (el as HTMLElement).style.background = '#222';
                  (el as HTMLElement).style.borderLeft = '2px solid transparent';
                });
                wholeItem.classList.add('surface-selected');
                wholeItem.style.background = '#4a3a1a';
                wholeItem.style.borderLeft = '2px solid #fbbf24';
                selectedSurfaceId = null;
                selectedSurfaceInfo.innerHTML = `<strong style="color:#fbbf24;">🏠 ${label} (entera)</strong><br>Clickeá en el mapa para colocarla`;
                placeSurfaceButton.disabled = true;
                placeSurfaceButton.style.opacity = '0.5';
                if (editorGame) {
                  const sc = editorGame.scene.getScene('MapEditorScene') as MapEditorScene;
                  if (sc) sc.setSelectedHouse(sub);
                }
              };
              wholeItem.onmouseenter = () => { if (!wholeItem.classList.contains('surface-selected')) wholeItem.style.background = '#3a3015'; };
              wholeItem.onmouseleave = () => { if (!wholeItem.classList.contains('surface-selected')) wholeItem.style.background = '#2a2410'; };
              subBody.appendChild(wholeItem);

              bySubtype[sub].forEach(({ id, deco }) => subBody.appendChild(makeDecoItem(id, deco)));
              subContainer.appendChild(subTitle);
              subContainer.appendChild(subBody);
              catBody.appendChild(subContainer);
            });

            surfaceListContainer.appendChild(catContainer);
            return;
          }

          // Contenedor para el grupo de decoraciones
          const decoGroupContainer = document.createElement('div');
          decoGroupContainer.className = 'deco-group';
          
          // Título de tipo de decoración (toggleable, colapsado por defecto)
          const decoTitle = document.createElement('div');
          let isDecoExpanded = false;
          const decoToggleArrow = '▶'; // Flecha hacia la derecha (colapsado)
          decoTitle.innerHTML = `<span style="display: inline-block; width: 12px; text-align: center;">${decoToggleArrow}</span> 🌳 ${type.charAt(0).toUpperCase() + type.slice(1)}`;
          decoTitle.style.fontWeight = 'bold';
          decoTitle.style.fontSize = '12px';
          decoTitle.style.color = '#fbbf24';
          decoTitle.style.padding = '8px 6px 4px 6px';
          decoTitle.style.background = '#0a0a0a';
          decoTitle.style.userSelect = 'none';
          decoTitle.style.cursor = 'pointer';
          decoTitle.style.borderBottom = '1px solid #333';
          decoTitle.style.marginTop = '4px';
          decoTitle.onmouseenter = () => {
            decoTitle.style.background = '#1a1a1a';
          };
          decoTitle.onmouseleave = () => {
            decoTitle.style.background = '#0a0a0a';
          };
          
          // Contenedor para las decoraciones (oculto por defecto)
          const decosContainer = document.createElement('div');
          decosContainer.style.display = 'none'; // Colapsado por defecto
          
          // Función para toggle
          decoTitle.onclick = () => {
            isDecoExpanded = !isDecoExpanded;
            const arrowSpan = decoTitle.querySelector('span');
            if (isDecoExpanded) {
              decosContainer.style.display = 'block';
              if (arrowSpan) arrowSpan.textContent = '▼'; // Flecha hacia abajo (expandido)
            } else {
              decosContainer.style.display = 'none';
              if (arrowSpan) arrowSpan.textContent = '▶'; // Flecha hacia la derecha (colapsado)
            }
          };
          
          decoGroupContainer.appendChild(decoTitle);
          
          // Lista de decoraciones para este tipo
          decos.forEach(({ id, deco }) => {
            const decoItem = document.createElement('div');
            decoItem.style.padding = '6px 8px';
            decoItem.style.background = '#222';
            decoItem.style.fontSize = '11px';
            decoItem.style.cursor = 'pointer';
            decoItem.style.borderLeft = '2px solid transparent';
            decoItem.style.transition = 'all 0.15s';
            decoItem.style.userSelect = 'none';
            
            decoItem.textContent = deco.name;
            decoItem.title = `ID: ${id}\nTipo: ${deco.type}`;
            
            decoItem.onclick = () => {
              // Deseleccionar anteriores
              surfaceListContainer.querySelectorAll('.surface-selected').forEach(el => {
                (el as HTMLElement).classList.remove('surface-selected');
                (el as HTMLElement).style.background = '#222';
                (el as HTMLElement).style.borderLeft = '2px solid transparent';
              });
              
              // Seleccionar este
              decoItem.classList.add('surface-selected');
              decoItem.style.background = '#4a3a1a';
              decoItem.style.borderLeft = '2px solid #fbbf24';
              
              selectedSurfaceId = id;
              selectedSurfaceInfo.innerHTML = `<strong style="color: #fbbf24;">🌳 ${deco.name}</strong><br>ID: ${id}`;
              placeSurfaceButton.disabled = false;
              placeSurfaceButton.style.opacity = '1';
              
              // Actualizar preview de imagen con la decoración
              updateDecorationPreviewImage(id);
              
              // Actualizar preview en la escena
              if (editorGame) {
                const scene = editorGame.scene.getScene('MapEditorScene') as MapEditorScene;
                if (scene) {
                  scene.setSelectedSurface(id, layerSelect.value);
                }
              }
            };
            
            decoItem.onmouseenter = () => {
              if (!decoItem.classList.contains('surface-selected')) {
                decoItem.style.background = '#2a2a2a';
              }
            };
            
            decoItem.onmouseleave = () => {
              if (!decoItem.classList.contains('surface-selected')) {
                decoItem.style.background = '#222';
              }
            };
            
            decosContainer.appendChild(decoItem);
          });
          
          decoGroupContainer.appendChild(decosContainer);
          surfaceListContainer.appendChild(decoGroupContainer);
        });
      }
    };
    
    // Función para mostrar preview de decoración
    const updateDecorationPreviewImage = (decorationId: string): void => {
      surfacePreviewError.style.display = 'none';
      surfacePreviewPlaceholder.style.display = 'none';
      surfacePreviewImage.style.display = 'none';
      
      const imgUrl = getDecorationAssetUrl(decorationId);
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        surfacePreviewImage.src = imgUrl;
        surfacePreviewImage.style.display = 'block';
        surfacePreviewImage.style.maxWidth = '100%';
        surfacePreviewImage.style.maxHeight = '200px';
        surfacePreviewImage.style.objectFit = 'contain';
      };
      
      img.onerror = () => {
        surfacePreviewError.textContent = `Error al cargar: ${decorationId}.png`;
        surfacePreviewError.style.display = 'block';
      };
      
      img.src = imgUrl;
    };
    
    // Actualizar lista de superficies cuando se carga un mapa
    const originalMapSelectOnchange = mapSelect.onchange;
    mapSelect.onchange = (ev) => {
      if (originalMapSelectOnchange) {
        originalMapSelectOnchange.call(mapSelect, ev as Event);
      }
      // Esperar a que la escena esté lista
      setTimeout(() => {
        refreshSurfaceList();
      }, 500);
    };
    
    // === Listener de teclado para +/- del numpad (cambiar capa) ===
    const handleNumpadLayerChange = (e: KeyboardEvent) => {
      // Solo actuar si hay una superficie seleccionada
      if (!selectedSurfaceId) return;
      
      const currentIndex = layers.findIndex(l => l.id === layerSelect.value);
      let newIndex = currentIndex;
      
      // NumpadAdd o tecla + para subir capa
      if (e.code === 'NumpadAdd' || (e.code === 'Equal' && e.shiftKey)) {
        newIndex = Math.min(currentIndex + 1, layers.length - 1);
      }
      // NumpadSubtract o tecla - para bajar capa
      else if (e.code === 'NumpadSubtract' || e.code === 'Minus') {
        newIndex = Math.max(currentIndex - 1, 0);
      }
      // Teclas numéricas 1-4 para seleccionar capa directamente
      else if (e.code === 'Digit1' || e.code === 'Numpad1') {
        newIndex = 0;
      } else if (e.code === 'Digit2' || e.code === 'Numpad2') {
        newIndex = 1;
      } else if (e.code === 'Digit3' || e.code === 'Numpad3') {
        newIndex = 2;
      } else if (e.code === 'Digit4' || e.code === 'Numpad4') {
        newIndex = 3;
      } else {
        return; // No es una tecla de capa
      }
      
      if (newIndex !== currentIndex) {
        layerSelect.value = layers[newIndex].id;
        layerSelect.dispatchEvent(new Event('change'));
        
        // Feedback visual
        layerSelect.style.boxShadow = '0 0 8px rgba(100, 200, 255, 0.8)';
        setTimeout(() => {
          layerSelect.style.boxShadow = 'none';
        }, 200);
      }
    };
    
    document.addEventListener('keydown', handleNumpadLayerChange);
    
    // Limpiar el listener cuando se sale del editor
    const cleanupNumpadListener = () => {
      document.removeEventListener('keydown', handleNumpadLayerChange);
    };
    
    // Guardar referencia para limpieza
    (container as HTMLElement & { _cleanupNumpad?: () => void })._cleanupNumpad = cleanupNumpadListener;
  }

  /**
   * Abre el Editor del Árbol de Habilidades Pasivas.
   * Permite crear nodos, conectarlos, y exportar/importar JSON.
   */
  openPassiveTreeEditor(): void {
    if (!this.host.hasLoginCallback()) {
      return;
    }

    this.host.contentLayer.innerHTML = '';

    // Contenedor principal del editor (pantalla completa)
    const container = document.createElement('div');
    container.style.background = '#1a1a2e';
    container.style.padding = '0';
    container.style.borderRadius = '0';
    container.style.width = '100vw';
    container.style.height = '100vh';
    container.style.display = 'flex';
    container.style.flexDirection = 'column';
    container.style.position = 'fixed';
    container.style.inset = '0';
    container.style.overflow = 'hidden';
    container.style.color = '#eee';
    container.style.fontFamily = 'system-ui, sans-serif';

    this.host.contentLayer.appendChild(container);

    // Contenedor donde vivirá el canvas de Phaser del editor
    const phaserContainer = document.createElement('div');
    phaserContainer.id = 'passive-tree-editor-phaser-container';
    phaserContainer.style.position = 'absolute';
    phaserContainer.style.inset = '0';
    phaserContainer.style.zIndex = '0';
    container.appendChild(phaserContainer);

    // Variables del editor
    let editorGame: Phaser.Game | null = null;
    let selectedNode: PassiveNode | null = null;

    // ---- Botones en la esquina superior derecha ----
    const buttonsContainer = document.createElement('div');
    buttonsContainer.style.position = 'absolute';
    buttonsContainer.style.top = '16px';
    buttonsContainer.style.right = '16px';
    buttonsContainer.style.zIndex = '10';
    buttonsContainer.style.display = 'flex';
    buttonsContainer.style.gap = '8px';

    const backButton = document.createElement('button');
    backButton.textContent = 'Volver al login';
    backButton.style.cursor = 'pointer';
    backButton.style.padding = '8px 16px';
    backButton.style.borderRadius = '4px';
    backButton.style.border = 'none';
    backButton.style.background = '#444';
    backButton.style.color = '#fff';
    backButton.onclick = () => {
      if (editorGame) {
        editorGame.destroy(true);
        editorGame = null;
      }
      PassiveTreeEditorScene.onTreeChanged = null;
      PassiveTreeEditorScene.onNodeSelected = null;
      this.host.returnToLogin();
    };

    const exportJsonButton = document.createElement('button');
    exportJsonButton.innerHTML = '📋 Copiar JSON';
    exportJsonButton.style.cursor = 'pointer';
    exportJsonButton.style.padding = '8px 16px';
    exportJsonButton.style.borderRadius = '4px';
    exportJsonButton.style.border = 'none';
    exportJsonButton.style.background = '#2d6a4f';
    exportJsonButton.style.color = '#fff';
    exportJsonButton.onclick = async () => {
      if (!editorGame) return;
      const scene = editorGame.scene.getScene('PassiveTreeEditorScene') as PassiveTreeEditorScene;
      if (!scene) return;
      const json = scene.exportToJson();
      try {
        await navigator.clipboard.writeText(json);
        const originalText = exportJsonButton.innerHTML;
        exportJsonButton.innerHTML = '✅ ¡Copiado!';
        setTimeout(() => {
          exportJsonButton.innerHTML = originalText;
        }, 1500);
      } catch {
        // Fallback: mostrar en consola
        console.log('JSON del árbol:', json);
      }
    };

    const importJsonButton = document.createElement('button');
    importJsonButton.innerHTML = '📁 Cargar JSON';
    importJsonButton.style.cursor = 'pointer';
    importJsonButton.style.padding = '8px 16px';
    importJsonButton.style.borderRadius = '4px';
    importJsonButton.style.border = 'none';
    importJsonButton.style.background = '#1d3557';
    importJsonButton.style.color = '#fff';
    importJsonButton.onclick = () => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.json';
      input.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (!file) return;
        try {
          const text = await file.text();
          const data = JSON.parse(text) as PassiveTreeData;
          if (editorGame) {
            const scene = editorGame.scene.getScene('PassiveTreeEditorScene') as PassiveTreeEditorScene;
            if (scene) {
              scene.loadTree(data);
            }
          }
        } catch (err) {
          console.error('Error cargando JSON:', err);
          alert('Error al cargar el archivo JSON');
        }
      };
      input.click();
    };

    const downloadJsonButton = document.createElement('button');
    downloadJsonButton.innerHTML = '💾 Descargar JSON';
    downloadJsonButton.style.cursor = 'pointer';
    downloadJsonButton.style.padding = '8px 16px';
    downloadJsonButton.style.borderRadius = '4px';
    downloadJsonButton.style.border = 'none';
    downloadJsonButton.style.background = '#6930c3';
    downloadJsonButton.style.color = '#fff';
    downloadJsonButton.onclick = () => {
      if (!editorGame) return;
      const scene = editorGame.scene.getScene('PassiveTreeEditorScene') as PassiveTreeEditorScene;
      if (!scene) return;
      const json = scene.exportToJson();
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'passive-tree.json';
      a.click();
      URL.revokeObjectURL(url);
    };

    buttonsContainer.appendChild(importJsonButton);
    buttonsContainer.appendChild(downloadJsonButton);
    buttonsContainer.appendChild(exportJsonButton);
    buttonsContainer.appendChild(backButton);
    container.appendChild(buttonsContainer);

    // ---- Panel de herramientas (izquierda) ----
    const toolsPanel = document.createElement('div');
    toolsPanel.style.position = 'absolute';
    toolsPanel.style.top = '16px';
    toolsPanel.style.left = '16px';
    toolsPanel.style.zIndex = '10';
    toolsPanel.style.background = 'rgba(0, 0, 0, 0.85)';
    toolsPanel.style.padding = '16px';
    toolsPanel.style.borderRadius = '8px';
    toolsPanel.style.width = '220px';
    toolsPanel.style.maxHeight = 'calc(100vh - 32px)';
    toolsPanel.style.overflowY = 'auto';
    skinScrollable(toolsPanel);

    // Título
    const toolsTitle = document.createElement('h3');
    toolsTitle.textContent = '🌳 Editor de Pasivas';
    toolsTitle.style.margin = '0 0 16px 0';
    toolsTitle.style.fontSize = '14px';
    toolsTitle.style.color = '#FFD700';
    toolsPanel.appendChild(toolsTitle);

    // FPS
    const fpsLabel = document.createElement('div');
    fpsLabel.style.fontSize = '10px';
    fpsLabel.style.color = '#888';
    fpsLabel.style.marginBottom = '12px';
    fpsLabel.innerHTML = 'FPS: <span id="passive-tree-editor-fps">--</span>';
    toolsPanel.appendChild(fpsLabel);

    // ---- Modo de edición ----
    const modeSection = document.createElement('div');
    modeSection.style.marginBottom = '16px';

    const modeLabel = document.createElement('div');
    modeLabel.textContent = 'Modo:';
    modeLabel.style.fontSize = '12px';
    modeLabel.style.marginBottom = '8px';
    modeLabel.style.fontWeight = 'bold';
    modeSection.appendChild(modeLabel);

    const modeButtons = document.createElement('div');
    modeButtons.style.display = 'flex';
    modeButtons.style.gap = '4px';

    const createModeButton = (label: string, mode: 'select' | 'connect' | 'delete', emoji: string) => {
      const btn = document.createElement('button');
      btn.innerHTML = `${emoji}`;
      btn.title = label;
      btn.style.flex = '1';
      btn.style.padding = '8px';
      btn.style.cursor = 'pointer';
      btn.style.border = '2px solid #444';
      btn.style.borderRadius = '4px';
      btn.style.background = '#333';
      btn.style.color = '#fff';
      btn.style.fontSize = '14px';
      btn.dataset.mode = mode;
      btn.onclick = () => {
        if (editorGame) {
          const scene = editorGame.scene.getScene('PassiveTreeEditorScene') as PassiveTreeEditorScene;
          if (scene) {
            scene.setMode(mode);
            // Actualizar estilos de botones
            modeButtons.querySelectorAll('button').forEach(b => {
              (b as HTMLButtonElement).style.borderColor = b.dataset.mode === mode ? '#FFD700' : '#444';
              (b as HTMLButtonElement).style.background = b.dataset.mode === mode ? '#444' : '#333';
            });
          }
        }
      };
      return btn;
    };

    const selectModeBtn = createModeButton('Seleccionar/Mover', 'select', '👆');
    selectModeBtn.style.borderColor = '#FFD700';
    selectModeBtn.style.background = '#444';
    const connectModeBtn = createModeButton('Conectar Nodos', 'connect', '🔗');
    const deleteModeBtn = createModeButton('Eliminar', 'delete', '🗑️');

    modeButtons.appendChild(selectModeBtn);
    modeButtons.appendChild(connectModeBtn);
    modeButtons.appendChild(deleteModeBtn);
    modeSection.appendChild(modeButtons);
    toolsPanel.appendChild(modeSection);

    // ---- Crear nodo ----
    const createSection = document.createElement('div');
    createSection.style.marginBottom = '16px';
    createSection.style.padding = '12px';
    createSection.style.background = 'rgba(255,255,255,0.05)';
    createSection.style.borderRadius = '6px';

    const createLabel = document.createElement('div');
    createLabel.textContent = 'Crear Nodo:';
    createLabel.style.fontSize = '12px';
    createLabel.style.marginBottom = '8px';
    createLabel.style.fontWeight = 'bold';
    createSection.appendChild(createLabel);

    // Nombre del nodo
    const nodeNameInput = document.createElement('input');
    nodeNameInput.placeholder = 'Nombre del nodo';
    nodeNameInput.style.width = '100%';
    nodeNameInput.style.marginBottom = '8px';
    nodeNameInput.style.padding = '6px';
    nodeNameInput.style.borderRadius = '4px';
    nodeNameInput.style.border = '1px solid #444';
    nodeNameInput.style.background = '#222';
    nodeNameInput.style.color = '#fff';
    nodeNameInput.style.boxSizing = 'border-box';
    createSection.appendChild(nodeNameInput);

    // Tipo de nodo
    const nodeTypeSelect = document.createElement('select');
    nodeTypeSelect.style.width = '100%';
    nodeTypeSelect.style.marginBottom = '8px';
    nodeTypeSelect.style.padding = '6px';
    nodeTypeSelect.style.borderRadius = '4px';
    nodeTypeSelect.style.border = '1px solid #444';
    nodeTypeSelect.style.background = '#222';
    nodeTypeSelect.style.color = '#fff';
    
    const nodeTypes: { value: PassiveNodeType; label: string }[] = [
      { value: 'small', label: '● Pequeño' },
      { value: 'notable', label: '◉ Notable' },
      { value: 'keystone', label: '★ Keystone' },
    ];
    nodeTypes.forEach(t => {
      const opt = document.createElement('option');
      opt.value = t.value;
      opt.textContent = t.label;
      nodeTypeSelect.appendChild(opt);
    });
    createSection.appendChild(nodeTypeSelect);

    // Afinidad de raza
    const raceAffinitySelect = document.createElement('select');
    raceAffinitySelect.style.width = '100%';
    raceAffinitySelect.style.marginBottom = '8px';
    raceAffinitySelect.style.padding = '6px';
    raceAffinitySelect.style.borderRadius = '4px';
    raceAffinitySelect.style.border = '1px solid #444';
    raceAffinitySelect.style.background = '#222';
    raceAffinitySelect.style.color = '#fff';

    const raceOptions: { value: string; label: string; color: string }[] = [
      { value: '', label: '⚫ Neutral (centro)', color: '#4a4a4a' },
      { value: 'dwarf', label: '⚪ Enano', color: '#888888' },
      { value: 'elf', label: '🟡 Elfo', color: '#FFD700' },
      { value: 'drow', label: '🔵 Elfo Oscuro', color: '#1E3A5F' },
      { value: 'gnome', label: '🩵 Gnomo', color: '#87CEEB' },
      { value: 'human', label: '⬜ Humano', color: '#F5F5F5' },
      { value: 'orc', label: '🟢 Orco', color: '#228B22' },
    ];
    raceOptions.forEach(r => {
      const opt = document.createElement('option');
      opt.value = r.value;
      opt.textContent = r.label;
      raceAffinitySelect.appendChild(opt);
    });
    createSection.appendChild(raceAffinitySelect);

    // Botón crear
    const createNodeBtn = document.createElement('button');
    createNodeBtn.textContent = '+ Crear Nodo';
    createNodeBtn.style.width = '100%';
    createNodeBtn.style.padding = '10px';
    createNodeBtn.style.cursor = 'pointer';
    createNodeBtn.style.border = 'none';
    createNodeBtn.style.borderRadius = '4px';
    createNodeBtn.style.background = '#2d6a4f';
    createNodeBtn.style.color = '#fff';
    createNodeBtn.style.fontWeight = 'bold';
    createNodeBtn.onclick = () => {
      if (editorGame) {
        const scene = editorGame.scene.getScene('PassiveTreeEditorScene') as PassiveTreeEditorScene;
        if (scene) {
          const name = nodeNameInput.value || undefined;
          const type = nodeTypeSelect.value as PassiveNodeType;
          const raceAffinity = raceAffinitySelect.value as PassiveRaceId | 'gnome' | null || null;
          scene.createNode(type, name || '', raceAffinity);
          nodeNameInput.value = '';
        }
      }
    };
    createSection.appendChild(createNodeBtn);
    toolsPanel.appendChild(createSection);

    // ---- Panel de nodo seleccionado ----
    const selectedNodePanel = document.createElement('div');
    selectedNodePanel.style.marginBottom = '16px';
    selectedNodePanel.style.padding = '12px';
    selectedNodePanel.style.background = 'rgba(255,215,0,0.1)';
    selectedNodePanel.style.borderRadius = '6px';
    selectedNodePanel.style.border = '1px solid #FFD700';
    selectedNodePanel.style.display = 'none';

    const selectedNodeTitle = document.createElement('div');
    selectedNodeTitle.textContent = 'Nodo Seleccionado:';
    selectedNodeTitle.style.fontSize = '12px';
    selectedNodeTitle.style.marginBottom = '8px';
    selectedNodeTitle.style.fontWeight = 'bold';
    selectedNodeTitle.style.color = '#FFD700';
    selectedNodePanel.appendChild(selectedNodeTitle);

    const selectedNodeInfo = document.createElement('div');
    selectedNodeInfo.style.fontSize = '11px';
    selectedNodeInfo.style.color = '#ccc';
    selectedNodePanel.appendChild(selectedNodeInfo);

    // Sección de modificadores
    const modifiersSection = document.createElement('div');
    modifiersSection.style.marginTop = '12px';

    const modifiersLabel = document.createElement('div');
    modifiersLabel.textContent = 'Modificadores:';
    modifiersLabel.style.fontSize = '11px';
    modifiersLabel.style.marginBottom = '6px';
    modifiersLabel.style.fontWeight = 'bold';
    modifiersSection.appendChild(modifiersLabel);

    const modifiersList = document.createElement('div');
    modifiersList.style.marginBottom = '8px';
    modifiersList.style.fontSize = '10px';
    modifiersSection.appendChild(modifiersList);

    // Añadir modificador
    const addModifierRow = document.createElement('div');
    addModifierRow.style.display = 'flex';
    addModifierRow.style.gap = '4px';
    addModifierRow.style.marginTop = '8px';

    const modStatSelect = document.createElement('select');
    modStatSelect.style.flex = '1';
    modStatSelect.style.padding = '4px';
    modStatSelect.style.fontSize = '10px';
    modStatSelect.style.borderRadius = '3px';
    modStatSelect.style.border = '1px solid #444';
    modStatSelect.style.background = '#222';
    modStatSelect.style.color = '#fff';

    const statOptions: { value: PassiveStatType; label: string }[] = [
      { value: 'maxHp', label: '+HP' },
      { value: 'maxMana', label: '+Maná' },
      { value: 'physDef', label: '+Def. Física' },
      { value: 'magicDef', label: '+Def. Mágica' },
      { value: 'minDamage', label: '+Daño Mín' },
      { value: 'maxDamage', label: '+Daño Máx' },
      { value: 'critChance', label: '+% Crítico' },
      { value: 'attackSpeed', label: '+% Vel. Ataque' },
      { value: 'moveSpeed', label: '+% Vel. Mov.' },
      { value: 'evasion', label: '+% Evasión' },
      { value: 'regenHp', label: '+Regen HP' },
      { value: 'regenMana', label: '+Regen Maná' },
      { value: 'manaReduction', label: '-% Costo Maná' },
      { value: 'castSpeed', label: '+% Vel. Casteo' },
      { value: 'lifeLeech', label: '+% Robo Vida' },
    ];
    statOptions.forEach(s => {
      const opt = document.createElement('option');
      opt.value = s.value;
      opt.textContent = s.label;
      modStatSelect.appendChild(opt);
    });
    addModifierRow.appendChild(modStatSelect);

    const modValueInput = document.createElement('input');
    modValueInput.type = 'number';
    modValueInput.placeholder = 'Valor';
    modValueInput.style.width = '50px';
    modValueInput.style.padding = '4px';
    modValueInput.style.fontSize = '10px';
    modValueInput.style.borderRadius = '3px';
    modValueInput.style.border = '1px solid #444';
    modValueInput.style.background = '#222';
    modValueInput.style.color = '#fff';
    addModifierRow.appendChild(modValueInput);

    const addModBtn = document.createElement('button');
    addModBtn.textContent = '+';
    addModBtn.style.padding = '4px 8px';
    addModBtn.style.cursor = 'pointer';
    addModBtn.style.border = 'none';
    addModBtn.style.borderRadius = '3px';
    addModBtn.style.background = '#2d6a4f';
    addModBtn.style.color = '#fff';
    addModBtn.onclick = () => {
      if (!selectedNode || !editorGame) return;
      const scene = editorGame.scene.getScene('PassiveTreeEditorScene') as PassiveTreeEditorScene;
      if (!scene) return;
      
      const stat = modStatSelect.value as PassiveStatType;
      const value = parseFloat(modValueInput.value) || 0;
      if (value === 0) return;
      
      const isPercentage = ['critChance', 'attackSpeed', 'moveSpeed', 'evasion', 'manaReduction', 'castSpeed', 'lifeLeech', 'manaLeech'].includes(stat);
      scene.addModifier(selectedNode.id, { stat, value, isPercentage });
      modValueInput.value = '';
    };
    addModifierRow.appendChild(addModBtn);
    modifiersSection.appendChild(addModifierRow);

    selectedNodePanel.appendChild(modifiersSection);

    // Botón eliminar nodo
    const deleteNodeBtn = document.createElement('button');
    deleteNodeBtn.textContent = '🗑️ Eliminar Nodo';
    deleteNodeBtn.style.width = '100%';
    deleteNodeBtn.style.marginTop = '12px';
    deleteNodeBtn.style.padding = '8px';
    deleteNodeBtn.style.cursor = 'pointer';
    deleteNodeBtn.style.border = 'none';
    deleteNodeBtn.style.borderRadius = '4px';
    deleteNodeBtn.style.background = '#c1121f';
    deleteNodeBtn.style.color = '#fff';
    deleteNodeBtn.onclick = () => {
      if (!selectedNode || !editorGame) return;
      if (selectedNode.isStartingPoint) {
        alert('No se pueden eliminar los puntos de inicio');
        return;
      }
      const scene = editorGame.scene.getScene('PassiveTreeEditorScene') as PassiveTreeEditorScene;
      if (scene) {
        scene.deleteNode(selectedNode.id);
      }
    };
    selectedNodePanel.appendChild(deleteNodeBtn);

    toolsPanel.appendChild(selectedNodePanel);

    // ---- Instrucciones ----
    const helpSection = document.createElement('div');
    helpSection.style.marginTop = '16px';
    helpSection.style.padding = '12px';
    helpSection.style.background = 'rgba(255,255,255,0.05)';
    helpSection.style.borderRadius = '6px';
    helpSection.style.fontSize = '10px';
    helpSection.style.color = '#888';
    helpSection.innerHTML = `
      <div style="font-weight:bold; margin-bottom:8px; color:#aaa;">Controles:</div>
      <div>• Click izq: Seleccionar/Mover</div>
      <div>• Click der/medio: Arrastrar cámara</div>
      <div>• Rueda: Zoom</div>
      <div>• ESC: Cancelar acción</div>
      <div>• DEL: Eliminar nodo seleccionado</div>
      <div style="margin-top:8px; font-weight:bold; color:#aaa;">Conexiones:</div>
      <div>• Modo 🔗: Click en nodo 1, luego nodo 2</div>
    `;
    toolsPanel.appendChild(helpSection);

    // Contenedor para centrar vista y checkbox
    const centerViewContainer = document.createElement('div');
    centerViewContainer.style.marginTop = '12px';
    centerViewContainer.style.display = 'flex';
    centerViewContainer.style.flexDirection = 'column';
    centerViewContainer.style.gap = '8px';

    // Botón centrar vista
    const centerViewBtn = document.createElement('button');
    centerViewBtn.textContent = '🎯 Centrar Vista';
    centerViewBtn.style.width = '100%';
    centerViewBtn.style.padding = '8px';
    centerViewBtn.style.cursor = 'pointer';
    centerViewBtn.style.border = 'none';
    centerViewBtn.style.borderRadius = '4px';
    centerViewBtn.style.background = '#444';
    centerViewBtn.style.color = '#fff';
    centerViewBtn.onclick = () => {
      if (editorGame) {
        const scene = editorGame.scene.getScene('PassiveTreeEditorScene') as PassiveTreeEditorScene;
        if (scene) {
          scene.centerView();
        }
      }
    };
    centerViewContainer.appendChild(centerViewBtn);

    // Checkbox para forzar centrado
    const forceCenterWrapper = document.createElement('label');
    forceCenterWrapper.style.display = 'flex';
    forceCenterWrapper.style.alignItems = 'center';
    forceCenterWrapper.style.gap = '6px';
    forceCenterWrapper.style.fontSize = '11px';
    forceCenterWrapper.style.color = '#aaa';
    forceCenterWrapper.style.cursor = 'pointer';

    const forceCenterCheckbox = document.createElement('input');
    forceCenterCheckbox.type = 'checkbox';
    forceCenterCheckbox.style.cursor = 'pointer';
    forceCenterCheckbox.onchange = () => {
      if (editorGame) {
        const scene = editorGame.scene.getScene('PassiveTreeEditorScene') as PassiveTreeEditorScene;
        if (scene) {
          scene.setForceCenterView(forceCenterCheckbox.checked);
        }
      }
    };

    const forceCenterLabel = document.createElement('span');
    forceCenterLabel.textContent = 'Mantener centrado';

    forceCenterWrapper.appendChild(forceCenterCheckbox);
    forceCenterWrapper.appendChild(forceCenterLabel);
    centerViewContainer.appendChild(forceCenterWrapper);

    toolsPanel.appendChild(centerViewContainer);

    container.appendChild(toolsPanel);

    // ---- Estadísticas del árbol (abajo derecha) ----
    const statsPanel = document.createElement('div');
    statsPanel.style.position = 'absolute';
    statsPanel.style.bottom = '16px';
    statsPanel.style.right = '16px';
    statsPanel.style.zIndex = '10';
    statsPanel.style.background = 'rgba(0, 0, 0, 0.7)';
    statsPanel.style.padding = '12px';
    statsPanel.style.borderRadius = '6px';
    statsPanel.style.fontSize = '11px';
    statsPanel.style.color = '#aaa';

    const statsContent = document.createElement('div');
    statsContent.innerHTML = 'Nodos: 6 | Conexiones: 0';
    statsPanel.appendChild(statsContent);
    container.appendChild(statsPanel);

    // Función para actualizar panel de nodo seleccionado
    const updateSelectedNodePanel = (node: PassiveNode | null) => {
      selectedNode = node;
      if (!node) {
        selectedNodePanel.style.display = 'none';
        return;
      }

      selectedNodePanel.style.display = 'block';
      selectedNodeInfo.innerHTML = `
        <div><strong>ID:</strong> ${node.id}</div>
        <div><strong>Nombre:</strong> ${node.name}</div>
        <div><strong>Tipo:</strong> ${node.type}</div>
        <div><strong>Posición:</strong> (${Math.round(node.x)}, ${Math.round(node.y)})</div>
        <div><strong>Conexiones:</strong> ${node.connections.length}</div>
        ${node.isStartingPoint ? '<div style="color:#FFD700;">⭐ Punto de inicio</div>' : ''}
      `;

      // Mostrar modificadores
      modifiersList.innerHTML = '';
      if (node.modifiers.length === 0) {
        modifiersList.innerHTML = '<div style="color:#666;">Sin modificadores</div>';
      } else {
        node.modifiers.forEach((mod: PassiveStatModifier, idx: number) => {
          const modDiv = document.createElement('div');
          modDiv.style.display = 'flex';
          modDiv.style.justifyContent = 'space-between';
          modDiv.style.alignItems = 'center';
          modDiv.style.padding = '2px 0';
          
          const sign = mod.value >= 0 ? '+' : '';
          const suffix = mod.isPercentage ? '%' : '';
          modDiv.innerHTML = `<span style="color:#aaffaa;">${sign}${mod.value}${suffix} ${mod.stat}</span>`;
          
          const removeBtn = document.createElement('button');
          removeBtn.textContent = '×';
          removeBtn.style.padding = '0 4px';
          removeBtn.style.cursor = 'pointer';
          removeBtn.style.border = 'none';
          removeBtn.style.background = '#c1121f';
          removeBtn.style.color = '#fff';
          removeBtn.style.borderRadius = '2px';
          removeBtn.style.fontSize = '10px';
          removeBtn.onclick = () => {
            if (editorGame) {
              const scene = editorGame.scene.getScene('PassiveTreeEditorScene') as PassiveTreeEditorScene;
              if (scene) {
                scene.removeModifier(node.id, idx);
              }
            }
          };
          modDiv.appendChild(removeBtn);
          modifiersList.appendChild(modDiv);
        });
      }

      // Ocultar botón eliminar para puntos de inicio
      deleteNodeBtn.style.display = node.isStartingPoint ? 'none' : 'block';
    };

    // Función para actualizar estadísticas
    const updateStats = (data: PassiveTreeData) => {
      const nodeCount = data.nodes.length;
      let connectionCount = 0;
      const seenConnections = new Set<string>();
      for (const node of data.nodes) {
        for (const connId of node.connections) {
          const key = [node.id, connId].sort().join('|');
          if (!seenConnections.has(key)) {
            seenConnections.add(key);
            connectionCount++;
          }
        }
      }
      statsContent.innerHTML = `Nodos: ${nodeCount} | Conexiones: ${connectionCount}`;
    };

    // Configurar callbacks
    PassiveTreeEditorScene.onNodeSelected = updateSelectedNodePanel;
    PassiveTreeEditorScene.onTreeChanged = updateStats;

    // Crear el juego Phaser
    const editorConfig: Phaser.Types.Core.GameConfig = {
      type: Phaser.AUTO,
      parent: 'passive-tree-editor-phaser-container',
      width: window.innerWidth,
      height: window.innerHeight,
      backgroundColor: '#1a1a2e',
      pixelArt: false,
      antialias: true,
      scene: [PassiveTreeEditorScene],
    };

    editorGame = new Phaser.Game(editorConfig);

    // Manejar resize de ventana
    const handleResize = () => {
      if (editorGame) {
        editorGame.scale.resize(window.innerWidth, window.innerHeight);
      }
    };
    window.addEventListener('resize', handleResize);

    // Limpiar al cerrar
    const cleanup = () => {
      window.removeEventListener('resize', handleResize);
    };

    // Asociar cleanup al botón de volver
    const originalBackClick = backButton.onclick;
    backButton.onclick = () => {
      cleanup();
      if (originalBackClick) {
        (originalBackClick as () => void)();
      }
    };
  }

  /**
   * Abre el Editor de Ajuste de Frames de Sprites.
   * Permite cargar imágenes, ajustar frames y exportar PNG.
   */
  openSpriteFrameEditor(): void {
    if (!this.host.hasLoginCallback()) {
      return;
    }

    this.host.contentLayer.innerHTML = '';

    // Contenedor principal del editor (pantalla completa)
    const container = document.createElement('div');
    container.style.background = '#1a1a2e';
    container.style.padding = '0';
    container.style.borderRadius = '0';
    container.style.width = '100vw';
    container.style.height = '100vh';
    container.style.display = 'flex';
    container.style.flexDirection = 'column';
    container.style.position = 'fixed';
    container.style.inset = '0';
    container.style.overflow = 'hidden';
    container.style.color = '#eee';
    container.style.fontFamily = 'system-ui, sans-serif';

    this.host.contentLayer.appendChild(container);

    // Contenedor donde vivirá el canvas de Phaser del editor
    const phaserContainer = document.createElement('div');
    phaserContainer.id = 'sprite-frame-editor-phaser-container';
    phaserContainer.style.position = 'absolute';
    phaserContainer.style.inset = '0';
    phaserContainer.style.zIndex = '0';
    container.appendChild(phaserContainer);

    // Variables del editor
    let editorGame: Phaser.Game | null = null;
    let currentConfig: SpriteSheetConfig | null = null;
    let selectedFrameIndex: number | null = null;

    // ---- Botones en la esquina superior derecha ----
    const buttonsContainer = document.createElement('div');
    buttonsContainer.style.position = 'absolute';
    buttonsContainer.style.top = '16px';
    buttonsContainer.style.right = '16px';
    buttonsContainer.style.zIndex = '10';
    buttonsContainer.style.display = 'flex';
    buttonsContainer.style.gap = '8px';

    const backButton = document.createElement('button');
    backButton.textContent = 'Volver al login';
    backButton.style.cursor = 'pointer';
    backButton.style.padding = '8px 16px';
    backButton.style.borderRadius = '4px';
    backButton.style.border = 'none';
    backButton.style.background = '#444';
    backButton.style.color = '#fff';
    backButton.onclick = () => {
      if (editorGame) {
        editorGame.destroy(true);
        editorGame = null;
      }
      SpriteFrameEditorScene.onImageLoaded = null;
      SpriteFrameEditorScene.onFrameSelected = null;
      SpriteFrameEditorScene.onConfigChanged = null;
      SpriteFrameEditorScene.onCanvasCropChanged = null;
      this.host.returnToLogin();
    };

    const exportButton = document.createElement('button');
    exportButton.innerHTML = '💾 Exportar PNG';
    exportButton.style.cursor = 'pointer';
    exportButton.style.padding = '8px 16px';
    exportButton.style.borderRadius = '4px';
    exportButton.style.border = 'none';
    exportButton.style.background = '#2d6a4f';
    exportButton.style.color = '#fff';
    exportButton.onclick = () => {
      if (!editorGame) return;
      const scene = editorGame.scene.getScene('SpriteFrameEditorScene') as SpriteFrameEditorScene;
      if (scene) {
        scene.exportAdjustedPNG();
      }
    };

    buttonsContainer.appendChild(exportButton);
    buttonsContainer.appendChild(backButton);
    container.appendChild(buttonsContainer);

    // ---- Panel de herramientas (izquierda) ----
    const toolsPanel = document.createElement('div');
    toolsPanel.style.position = 'absolute';
    toolsPanel.style.top = '16px';
    toolsPanel.style.left = '16px';
    toolsPanel.style.zIndex = '10';
    toolsPanel.style.background = 'rgba(0, 0, 0, 0.85)';
    toolsPanel.style.padding = '16px';
    toolsPanel.style.borderRadius = '8px';
    toolsPanel.style.width = '240px';
    toolsPanel.style.maxHeight = 'calc(100vh - 32px)';
    toolsPanel.style.overflowY = 'auto';
    skinScrollable(toolsPanel);

    // Título
    const toolsTitle = document.createElement('h3');
    toolsTitle.textContent = '🖼️ Editor de Frames';
    toolsTitle.style.margin = '0 0 16px 0';
    toolsTitle.style.fontSize = '14px';
    toolsTitle.style.color = '#FFD700';
    toolsPanel.appendChild(toolsTitle);

    // FPS
    const fpsLabel = document.createElement('div');
    fpsLabel.style.fontSize = '10px';
    fpsLabel.style.color = '#888';
    fpsLabel.style.marginBottom = '12px';
    fpsLabel.innerHTML = 'FPS: <span id="sprite-frame-editor-fps">--</span>';
    toolsPanel.appendChild(fpsLabel);

    // ---- Cargar imagen ----
    const loadSection = document.createElement('div');
    loadSection.style.marginBottom = '16px';

    const loadLabel = document.createElement('div');
    loadLabel.textContent = 'Cargar Imagen:';
    loadLabel.style.fontSize = '12px';
    loadLabel.style.marginBottom = '8px';
    loadLabel.style.fontWeight = 'bold';
    loadSection.appendChild(loadLabel);

    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/png,image/jpeg,image/gif';
    fileInput.style.display = 'none';

    const loadButton = document.createElement('button');
    loadButton.textContent = '📂 Examinar...';
    loadButton.style.width = '100%';
    loadButton.style.padding = '8px';
    loadButton.style.cursor = 'pointer';
    loadButton.style.borderRadius = '4px';
    loadButton.style.border = 'none';
    loadButton.style.background = '#1d3557';
    loadButton.style.color = '#fff';
    loadButton.onclick = () => fileInput.click();

    fileInput.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (event) => {
        const dataUrl = event.target?.result as string;
        if (editorGame) {
          const scene = editorGame.scene.getScene('SpriteFrameEditorScene') as SpriteFrameEditorScene;
          if (scene) {
            scene.loadImageFromDataUrl(dataUrl, file.name);
          }
        }
      };
      reader.readAsDataURL(file);
    };

    loadSection.appendChild(fileInput);
    loadSection.appendChild(loadButton);
    toolsPanel.appendChild(loadSection);

    // ---- Info de imagen ----
    const imageInfoSection = document.createElement('div');
    imageInfoSection.style.marginBottom = '16px';
    imageInfoSection.style.display = 'none';

    const imageInfoLabel = document.createElement('div');
    imageInfoLabel.textContent = 'Imagen:';
    imageInfoLabel.style.fontSize = '12px';
    imageInfoLabel.style.marginBottom = '4px';
    imageInfoLabel.style.fontWeight = 'bold';
    imageInfoSection.appendChild(imageInfoLabel);

    const imageInfoText = document.createElement('div');
    imageInfoText.style.fontSize = '11px';
    imageInfoText.style.color = '#aaa';
    imageInfoSection.appendChild(imageInfoText);

    toolsPanel.appendChild(imageInfoSection);

    // ---- Recorte del Canvas ----
    const canvasCropSection = document.createElement('div');
    canvasCropSection.style.marginBottom = '16px';
    canvasCropSection.style.display = 'none';

    const canvasCropLabel = document.createElement('div');
    canvasCropLabel.textContent = 'Recorte del Canvas:';
    canvasCropLabel.style.fontSize = '12px';
    canvasCropLabel.style.marginBottom = '8px';
    canvasCropLabel.style.fontWeight = 'bold';
    canvasCropSection.appendChild(canvasCropLabel);

    const canvasCropInfo = document.createElement('div');
    canvasCropInfo.style.fontSize = '10px';
    canvasCropInfo.style.color = '#aaa';
    canvasCropInfo.style.marginBottom = '8px';
    canvasCropInfo.textContent = 'Arrastra los handles azules del borde';
    canvasCropSection.appendChild(canvasCropInfo);

    // Grid de inputs para el crop
    const cropGrid = document.createElement('div');
    cropGrid.style.display = 'grid';
    cropGrid.style.gridTemplateColumns = '1fr 1fr';
    cropGrid.style.gap = '6px';
    cropGrid.style.marginBottom = '8px';

    const createCropInput = (labelText: string) => {
      const wrapper = document.createElement('div');
      const label = document.createElement('div');
      label.textContent = labelText;
      label.style.fontSize = '9px';
      label.style.color = '#888';
      label.style.marginBottom = '2px';
      const input = document.createElement('input');
      input.type = 'number';
      input.value = '0';
      input.min = '0';
      input.style.width = '100%';
      input.style.padding = '4px';
      input.style.borderRadius = '4px';
      input.style.border = '1px solid #444';
      input.style.background = '#222';
      input.style.color = '#fff';
      input.style.boxSizing = 'border-box';
      wrapper.appendChild(label);
      wrapper.appendChild(input);
      return { wrapper, input };
    };

    const { wrapper: leftWrapper, input: cropLeftInput } = createCropInput('Izquierda');
    const { wrapper: rightWrapper, input: cropRightInput } = createCropInput('Derecha');
    const { wrapper: topWrapper, input: cropTopInput } = createCropInput('Arriba');
    const { wrapper: bottomWrapper, input: cropBottomInput } = createCropInput('Abajo');

    cropGrid.appendChild(topWrapper);
    cropGrid.appendChild(document.createElement('div')); // Espacio vacío
    cropGrid.appendChild(leftWrapper);
    cropGrid.appendChild(rightWrapper);
    cropGrid.appendChild(document.createElement('div')); // Espacio vacío
    cropGrid.appendChild(bottomWrapper);

    canvasCropSection.appendChild(cropGrid);

    // Tamaño resultante
    const resultSizeLabel = document.createElement('div');
    resultSizeLabel.style.fontSize = '10px';
    resultSizeLabel.style.color = '#4ade80';
    resultSizeLabel.style.marginBottom = '8px';
    resultSizeLabel.textContent = 'Tamaño resultante: -';
    canvasCropSection.appendChild(resultSizeLabel);

    const applyCropButton = document.createElement('button');
    applyCropButton.textContent = 'Aplicar Recorte';
    applyCropButton.style.width = '100%';
    applyCropButton.style.padding = '6px';
    applyCropButton.style.cursor = 'pointer';
    applyCropButton.style.borderRadius = '4px';
    applyCropButton.style.border = 'none';
    applyCropButton.style.background = '#0088dd';
    applyCropButton.style.color = '#fff';
    applyCropButton.style.fontSize = '11px';
    applyCropButton.onclick = () => {
      if (!editorGame) return;
      const scene = editorGame.scene.getScene('SpriteFrameEditorScene') as SpriteFrameEditorScene;
      if (scene) {
        const left = parseInt(cropLeftInput.value, 10) || 0;
        const right = parseInt(cropRightInput.value, 10) || 0;
        const top = parseInt(cropTopInput.value, 10) || 0;
        const bottom = parseInt(cropBottomInput.value, 10) || 0;
        scene.setCanvasCrop(left, right, top, bottom);
      }
    };
    canvasCropSection.appendChild(applyCropButton);

    toolsPanel.appendChild(canvasCropSection);

    // ---- Configuración del grid ----
    const gridSection = document.createElement('div');
    gridSection.style.marginBottom = '16px';
    gridSection.style.display = 'none';

    const gridLabel = document.createElement('div');
    gridLabel.textContent = 'Grid (Filas x Columnas):';
    gridLabel.style.fontSize = '12px';
    gridLabel.style.marginBottom = '8px';
    gridLabel.style.fontWeight = 'bold';
    gridSection.appendChild(gridLabel);

    const gridRow = document.createElement('div');
    gridRow.style.display = 'flex';
    gridRow.style.gap = '8px';
    gridRow.style.alignItems = 'center';

    const rowsInput = document.createElement('input');
    rowsInput.type = 'number';
    rowsInput.value = '4';
    rowsInput.min = '1';
    rowsInput.max = '20';
    rowsInput.style.width = '50px';
    rowsInput.style.padding = '4px';
    rowsInput.style.borderRadius = '4px';
    rowsInput.style.border = '1px solid #444';
    rowsInput.style.background = '#222';
    rowsInput.style.color = '#fff';

    const xLabel = document.createElement('span');
    xLabel.textContent = 'x';
    xLabel.style.color = '#888';

    const colsInput = document.createElement('input');
    colsInput.type = 'number';
    colsInput.value = '6';
    colsInput.min = '1';
    colsInput.max = '20';
    colsInput.style.width = '50px';
    colsInput.style.padding = '4px';
    colsInput.style.borderRadius = '4px';
    colsInput.style.border = '1px solid #444';
    colsInput.style.background = '#222';
    colsInput.style.color = '#fff';

    const applyGridButton = document.createElement('button');
    applyGridButton.textContent = 'Aplicar';
    applyGridButton.style.padding = '4px 8px';
    applyGridButton.style.cursor = 'pointer';
    applyGridButton.style.borderRadius = '4px';
    applyGridButton.style.border = 'none';
    applyGridButton.style.background = '#444';
    applyGridButton.style.color = '#fff';
    applyGridButton.onclick = () => {
      if (!editorGame) return;
      const scene = editorGame.scene.getScene('SpriteFrameEditorScene') as SpriteFrameEditorScene;
      if (scene) {
        const rows = parseInt(rowsInput.value, 10) || 4;
        const cols = parseInt(colsInput.value, 10) || 6;
        scene.setGrid(rows, cols);
      }
    };

    gridRow.appendChild(rowsInput);
    gridRow.appendChild(xLabel);
    gridRow.appendChild(colsInput);
    gridRow.appendChild(applyGridButton);
    gridSection.appendChild(gridRow);

    const frameDimLabel = document.createElement('div');
    frameDimLabel.style.fontSize = '10px';
    frameDimLabel.style.color = '#888';
    frameDimLabel.style.marginTop = '4px';
    frameDimLabel.textContent = 'Frame: -';
    gridSection.appendChild(frameDimLabel);

    toolsPanel.appendChild(gridSection);

    // ---- Leyenda de colores ----
    const legendSection = document.createElement('div');
    legendSection.style.marginBottom = '16px';
    legendSection.style.display = 'none';

    const legendLabel = document.createElement('div');
    legendLabel.textContent = 'Direcciones:';
    legendLabel.style.fontSize = '12px';
    legendLabel.style.marginBottom = '8px';
    legendLabel.style.fontWeight = 'bold';
    legendSection.appendChild(legendLabel);

    const legendItems = [
      { color: '#00ff00', name: 'Down (fila 0)' },
      { color: '#0088ff', name: 'Up (fila 1)' },
      { color: '#ffff00', name: 'Left (fila 2)' },
      { color: '#ff4444', name: 'Right (fila 3)' },
    ];

    legendItems.forEach(item => {
      const row = document.createElement('div');
      row.style.display = 'flex';
      row.style.alignItems = 'center';
      row.style.gap = '6px';
      row.style.fontSize = '10px';
      row.style.marginBottom = '2px';

      const swatch = document.createElement('div');
      swatch.style.width = '12px';
      swatch.style.height = '12px';
      swatch.style.borderRadius = '2px';
      swatch.style.background = item.color;

      const label = document.createElement('span');
      label.textContent = item.name;
      label.style.color = '#aaa';

      row.appendChild(swatch);
      row.appendChild(label);
      legendSection.appendChild(row);
    });

    toolsPanel.appendChild(legendSection);

    // ---- Frame seleccionado ----
    const frameSection = document.createElement('div');
    frameSection.style.marginBottom = '16px';
    frameSection.style.display = 'none';

    const frameLabel = document.createElement('div');
    frameLabel.textContent = 'Frame Seleccionado:';
    frameLabel.style.fontSize = '12px';
    frameLabel.style.marginBottom = '8px';
    frameLabel.style.fontWeight = 'bold';
    frameSection.appendChild(frameLabel);

    const frameInfoText = document.createElement('div');
    frameInfoText.style.fontSize = '11px';
    frameInfoText.style.color = '#aaa';
    frameInfoText.style.marginBottom = '8px';
    frameInfoText.textContent = 'Ninguno';
    frameSection.appendChild(frameInfoText);

    // Inputs de offset
    const offsetLabel = document.createElement('div');
    offsetLabel.textContent = 'Offset (X, Y):';
    offsetLabel.style.fontSize = '11px';
    offsetLabel.style.marginBottom = '4px';
    offsetLabel.style.color = '#888';
    frameSection.appendChild(offsetLabel);

    const offsetRow = document.createElement('div');
    offsetRow.style.display = 'flex';
    offsetRow.style.gap = '8px';
    offsetRow.style.marginBottom = '8px';

    const offsetXInput = document.createElement('input');
    offsetXInput.type = 'number';
    offsetXInput.value = '0';
    offsetXInput.style.width = '50px';
    offsetXInput.style.padding = '4px';
    offsetXInput.style.borderRadius = '4px';
    offsetXInput.style.border = '1px solid #444';
    offsetXInput.style.background = '#222';
    offsetXInput.style.color = '#fff';

    const offsetYInput = document.createElement('input');
    offsetYInput.type = 'number';
    offsetYInput.value = '0';
    offsetYInput.style.width = '50px';
    offsetYInput.style.padding = '4px';
    offsetYInput.style.borderRadius = '4px';
    offsetYInput.style.border = '1px solid #444';
    offsetYInput.style.background = '#222';
    offsetYInput.style.color = '#fff';

    const applyOffsetButton = document.createElement('button');
    applyOffsetButton.textContent = 'Aplicar';
    applyOffsetButton.style.padding = '4px 8px';
    applyOffsetButton.style.cursor = 'pointer';
    applyOffsetButton.style.borderRadius = '4px';
    applyOffsetButton.style.border = 'none';
    applyOffsetButton.style.background = '#444';
    applyOffsetButton.style.color = '#fff';
    applyOffsetButton.onclick = () => {
      if (!editorGame || selectedFrameIndex === null) return;
      const scene = editorGame.scene.getScene('SpriteFrameEditorScene') as SpriteFrameEditorScene;
      if (scene) {
        const offsetX = parseInt(offsetXInput.value, 10) || 0;
        const offsetY = parseInt(offsetYInput.value, 10) || 0;
        scene.setFrameOffset(selectedFrameIndex, offsetX, offsetY);
      }
    };

    offsetRow.appendChild(offsetXInput);
    offsetRow.appendChild(offsetYInput);
    offsetRow.appendChild(applyOffsetButton);
    frameSection.appendChild(offsetRow);

    toolsPanel.appendChild(frameSection);

    // ---- Controles de visualización ----
    const viewSection = document.createElement('div');
    viewSection.style.marginBottom = '16px';
    viewSection.style.display = 'none';

    const viewLabel = document.createElement('div');
    viewLabel.textContent = 'Vista:';
    viewLabel.style.fontSize = '12px';
    viewLabel.style.marginBottom = '8px';
    viewLabel.style.fontWeight = 'bold';
    viewSection.appendChild(viewLabel);

    const centerButton = document.createElement('button');
    centerButton.textContent = '🎯 Centrar Vista';
    centerButton.style.width = '100%';
    centerButton.style.padding = '8px';
    centerButton.style.cursor = 'pointer';
    centerButton.style.borderRadius = '4px';
    centerButton.style.border = 'none';
    centerButton.style.background = '#444';
    centerButton.style.color = '#fff';
    centerButton.style.marginBottom = '8px';
    centerButton.onclick = () => {
      if (!editorGame) return;
      const scene = editorGame.scene.getScene('SpriteFrameEditorScene') as SpriteFrameEditorScene;
      if (scene) scene.centerView();
    };
    viewSection.appendChild(centerButton);

    const resetAllButton = document.createElement('button');
    resetAllButton.textContent = '🔄 Resetear Todo';
    resetAllButton.style.width = '100%';
    resetAllButton.style.padding = '8px';
    resetAllButton.style.cursor = 'pointer';
    resetAllButton.style.borderRadius = '4px';
    resetAllButton.style.border = 'none';
    resetAllButton.style.background = '#7f1d1d';
    resetAllButton.style.color = '#fca5a5';
    resetAllButton.onclick = () => {
      if (!editorGame) return;
      if (confirm('Esto eliminará todos los ajustes. ¿Continuar?')) {
        const scene = editorGame.scene.getScene('SpriteFrameEditorScene') as SpriteFrameEditorScene;
        if (scene) scene.resetAllAdjustments();
      }
    };
    viewSection.appendChild(resetAllButton);

    toolsPanel.appendChild(viewSection);

    // ---- Instrucciones ----
    const instructionsSection = document.createElement('div');
    instructionsSection.style.marginTop = 'auto';
    instructionsSection.style.paddingTop = '16px';
    instructionsSection.style.borderTop = '1px solid #333';

    const instructionsText = document.createElement('div');
    instructionsText.style.fontSize = '10px';
    instructionsText.style.color = '#666';
    instructionsText.innerHTML = `
      <strong>Controles:</strong><br/>
      Click izq/medio: Arrastrar cámara<br/>
      Click derecho: Seleccionar frame<br/>
      Rueda: Zoom<br/>
      Flechas: Mover cámara<br/>
      Arrastrar handles: Ajustar bordes
    `;
    instructionsSection.appendChild(instructionsText);
    toolsPanel.appendChild(instructionsSection);

    container.appendChild(toolsPanel);

    // ---- Callbacks de la escena ----
    SpriteFrameEditorScene.onImageLoaded = (width: number, height: number, fileName: string) => {
      imageInfoSection.style.display = 'block';
      canvasCropSection.style.display = 'block';
      gridSection.style.display = 'block';
      legendSection.style.display = 'block';
      viewSection.style.display = 'block';
      imageInfoText.innerHTML = `${fileName}<br/>${width} x ${height} px`;
      resultSizeLabel.textContent = `Tamaño resultante: ${width} x ${height} px`;
    };

    SpriteFrameEditorScene.onCanvasCropChanged = (cropInfo: CanvasCropInfo) => {
      cropLeftInput.value = Math.round(cropInfo.left).toString();
      cropRightInput.value = Math.round(cropInfo.right).toString();
      cropTopInput.value = Math.round(cropInfo.top).toString();
      cropBottomInput.value = Math.round(cropInfo.bottom).toString();
      resultSizeLabel.textContent = `Tamaño resultante: ${Math.round(cropInfo.croppedWidth)} x ${Math.round(cropInfo.croppedHeight)} px`;
    };

    SpriteFrameEditorScene.onConfigChanged = (config: SpriteSheetConfig) => {
      currentConfig = config;
      rowsInput.value = config.rows.toString();
      colsInput.value = config.columns.toString();
      frameDimLabel.textContent = `Frame: ${config.frameWidth} x ${config.frameHeight} px`;
    };

    SpriteFrameEditorScene.onFrameSelected = (frameIndex: number | null, adjustment: FrameAdjustment | null) => {
      selectedFrameIndex = frameIndex;
      if (frameIndex === null || adjustment === null || !currentConfig) {
        frameSection.style.display = 'none';
        return;
      }
      frameSection.style.display = 'block';
      const col = frameIndex % currentConfig.columns;
      const row = Math.floor(frameIndex / currentConfig.columns);
      frameInfoText.textContent = `Frame #${frameIndex} (Fila ${row}, Col ${col})`;
      offsetXInput.value = Math.round(adjustment.offsetX).toString();
      offsetYInput.value = Math.round(adjustment.offsetY).toString();
    };

    // ---- Crear Phaser Game ----
    const editorConfig: Phaser.Types.Core.GameConfig = {
      type: Phaser.AUTO,
      parent: 'sprite-frame-editor-phaser-container',
      width: window.innerWidth,
      height: window.innerHeight,
      backgroundColor: '#1a1a2e',
      pixelArt: true,
      antialias: false,
      scene: [SpriteFrameEditorScene],
    };

    editorGame = new Phaser.Game(editorConfig);

    // Manejar resize de ventana
    const handleResize = () => {
      if (editorGame) {
        editorGame.scale.resize(window.innerWidth, window.innerHeight);
      }
    };
    window.addEventListener('resize', handleResize);

    // Limpiar al cerrar
    const cleanup = () => {
      window.removeEventListener('resize', handleResize);
    };

    // Asociar cleanup al botón de volver
    const originalBackClick = backButton.onclick;
    backButton.onclick = () => {
      cleanup();
      if (originalBackClick) {
        (originalBackClick as () => void)();
      }
    };
  }

  /**
   * Abre el Indexador de Superficies.
   * Permite indexar tiles desde archivos atlas para usarlos en el Map Editor.
   */
  openSurfaceIndexer(): void {
    if (!this.host.hasLoginCallback()) {
      return;
    }

    this.host.contentLayer.innerHTML = '';

    // Contenedor principal del editor (pantalla completa)
    const container = document.createElement('div');
    container.style.background = '#1a1a2e';
    container.style.padding = '0';
    container.style.display = 'flex';
    container.style.width = '100vw';
    container.style.height = '100vh';
    container.style.position = 'fixed';
    container.style.top = '0';
    container.style.left = '0';
    container.style.zIndex = '1000';

    // Panel lateral izquierdo
    const sidebar = document.createElement('div');
    sidebar.style.width = '320px';
    sidebar.style.minWidth = '320px';
    sidebar.style.background = '#16213e';
    sidebar.style.padding = '16px';
    sidebar.style.display = 'flex';
    sidebar.style.flexDirection = 'column';
    sidebar.style.gap = '16px';
    sidebar.style.overflowY = 'auto';
    skinScrollable(sidebar);
    sidebar.style.borderRight = '1px solid #0f3460';

    // Título
    const title = document.createElement('h1');
    title.textContent = 'Surface Indexer';
    title.style.margin = '0';
    title.style.fontSize = '18px';
    title.style.color = '#e94560';
    sidebar.appendChild(title);

    // Sección: Cargar Atlas
    const atlasSection = document.createElement('div');
    atlasSection.style.display = 'flex';
    atlasSection.style.flexDirection = 'column';
    atlasSection.style.gap = '8px';

    const atlasTitle = document.createElement('h2');
    atlasTitle.textContent = 'Atlas';
    atlasTitle.style.margin = '0';
    atlasTitle.style.fontSize = '14px';
    atlasTitle.style.color = '#94a3b8';
    atlasTitle.style.borderBottom = '1px solid #0f3460';
    atlasTitle.style.paddingBottom = '8px';
    atlasSection.appendChild(atlasTitle);

    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/png';
    fileInput.style.display = 'none';

    const loadAtlasBtn = document.createElement('button');
    loadAtlasBtn.textContent = 'Cargar Atlas PNG';
    loadAtlasBtn.style.padding = '8px 12px';
    loadAtlasBtn.style.border = 'none';
    loadAtlasBtn.style.borderRadius = '4px';
    loadAtlasBtn.style.background = '#e94560';
    loadAtlasBtn.style.color = 'white';
    loadAtlasBtn.style.cursor = 'pointer';
    loadAtlasBtn.style.fontWeight = '600';
    loadAtlasBtn.onclick = () => fileInput.click();

    atlasSection.appendChild(fileInput);
    atlasSection.appendChild(loadAtlasBtn);

    // Info del atlas
    const atlasInfo = document.createElement('div');
    atlasInfo.style.background = '#0f3460';
    atlasInfo.style.padding = '10px';
    atlasInfo.style.borderRadius = '4px';
    atlasInfo.style.fontSize = '12px';
    atlasInfo.style.display = 'none';
    atlasInfo.innerHTML = `
      <p style="margin: 4px 0;">Archivo: <span id="atlas-filename" style="color: #e94560; font-weight: 600;">-</span></p>
      <p style="margin: 4px 0;">Tamaño: <span id="atlas-size">-</span></p>
      <p style="margin: 4px 0;">Grid: <span id="atlas-grid">-</span></p>
    `;
    atlasSection.appendChild(atlasInfo);
    sidebar.appendChild(atlasSection);

    // Sección: Tile Seleccionado
    const tileSection = document.createElement('div');
    tileSection.style.display = 'flex';
    tileSection.style.flexDirection = 'column';
    tileSection.style.gap = '8px';

    const tileTitle = document.createElement('h2');
    tileTitle.textContent = 'Tile Seleccionado';
    tileTitle.style.margin = '0';
    tileTitle.style.fontSize = '14px';
    tileTitle.style.color = '#94a3b8';
    tileTitle.style.borderBottom = '1px solid #0f3460';
    tileTitle.style.paddingBottom = '8px';
    tileSection.appendChild(tileTitle);

    const selectedTileInfo = document.createElement('div');
    selectedTileInfo.style.background = '#0f3460';
    selectedTileInfo.style.padding = '10px';
    selectedTileInfo.style.borderRadius = '4px';
    selectedTileInfo.style.fontSize = '12px';
    selectedTileInfo.innerHTML = `
      <p style="margin: 4px 0;">Posición: <span id="selected-coords" style="color: #4ade80; font-weight: 600;">Ninguno</span></p>
      <p style="margin: 4px 0; color: #64748b; font-size: 10px;">💡 Ctrl+arrastrar para selección múltiple</p>
    `;
    tileSection.appendChild(selectedTileInfo);

    // Formulario de superficie
    const surfaceForm = document.createElement('div');
    surfaceForm.style.display = 'none';
    surfaceForm.style.flexDirection = 'column';
    surfaceForm.style.gap = '8px';

    const createInput = (label: string, id: string, placeholder: string) => {
      const group = document.createElement('div');
      group.style.display = 'flex';
      group.style.flexDirection = 'column';
      group.style.gap = '4px';
      const lbl = document.createElement('label');
      lbl.textContent = label;
      lbl.style.fontSize = '12px';
      lbl.style.color = '#94a3b8';
      const input = document.createElement('input');
      input.id = id;
      input.placeholder = placeholder;
      input.style.padding = '8px 10px';
      input.style.border = '1px solid #0f3460';
      input.style.borderRadius = '4px';
      input.style.background = '#1a1a2e';
      input.style.color = '#eee';
      input.style.fontSize = '13px';
      group.appendChild(lbl);
      group.appendChild(input);
      return group;
    };

    surfaceForm.appendChild(createInput('ID (único)', 'surface-id', 'ej: path_grass_1'));
    surfaceForm.appendChild(createInput('Nombre', 'surface-name', 'ej: Camino de hierba 1'));
    surfaceForm.appendChild(createInput('Tipo', 'surface-type', 'ej: path'));
    surfaceForm.appendChild(createInput('Subtipo (opcional)', 'surface-subtype', 'ej: grass'));

    const formButtons = document.createElement('div');
    formButtons.style.display = 'flex';
    formButtons.style.gap = '8px';
    formButtons.style.marginTop = '8px';

    const saveBtn = document.createElement('button');
    saveBtn.textContent = 'Guardar';
    saveBtn.style.flex = '1';
    saveBtn.style.padding = '8px 12px';
    saveBtn.style.border = 'none';
    saveBtn.style.borderRadius = '4px';
    saveBtn.style.background = '#e94560';
    saveBtn.style.color = 'white';
    saveBtn.style.cursor = 'pointer';
    saveBtn.style.fontWeight = '600';

    const deleteBtn = document.createElement('button');
    deleteBtn.textContent = 'Eliminar';
    deleteBtn.style.display = 'none';
    deleteBtn.style.padding = '8px 12px';
    deleteBtn.style.border = 'none';
    deleteBtn.style.borderRadius = '4px';
    deleteBtn.style.background = '#7f1d1d';
    deleteBtn.style.color = '#fca5a5';
    deleteBtn.style.cursor = 'pointer';
    deleteBtn.style.fontWeight = '600';

    formButtons.appendChild(saveBtn);
    formButtons.appendChild(deleteBtn);
    surfaceForm.appendChild(formButtons);

    // Checkbox para conservar datos después de grabar
    const keepDataWrapper = document.createElement('label');
    keepDataWrapper.style.display = 'flex';
    keepDataWrapper.style.alignItems = 'center';
    keepDataWrapper.style.gap = '8px';
    keepDataWrapper.style.fontSize = '11px';
    keepDataWrapper.style.color = '#94a3b8';
    keepDataWrapper.style.marginTop = '8px';
    keepDataWrapper.style.cursor = 'pointer';
    
    const keepDataCheckbox = document.createElement('input');
    keepDataCheckbox.type = 'checkbox';
    keepDataCheckbox.checked = true; // Activado por defecto
    keepDataCheckbox.style.width = '14px';
    keepDataCheckbox.style.height = '14px';
    keepDataWrapper.appendChild(keepDataCheckbox);
    keepDataWrapper.appendChild(document.createTextNode('Conservar datos después de grabar'));
    surfaceForm.appendChild(keepDataWrapper);

    tileSection.appendChild(surfaceForm);
    sidebar.appendChild(tileSection);

    // Sección: Superficies Indexadas
    const surfacesSection = document.createElement('div');
    surfacesSection.style.display = 'flex';
    surfacesSection.style.flexDirection = 'column';
    surfacesSection.style.gap = '8px';
    surfacesSection.style.flex = '1';
    surfacesSection.style.minHeight = '0';

    const surfacesTitle = document.createElement('h2');
    surfacesTitle.innerHTML = 'Superficies Indexadas (<span id="surfaces-count">0</span>)';
    surfacesTitle.style.margin = '0';
    surfacesTitle.style.fontSize = '14px';
    surfacesTitle.style.color = '#94a3b8';
    surfacesTitle.style.borderBottom = '1px solid #0f3460';
    surfacesTitle.style.paddingBottom = '8px';
    surfacesSection.appendChild(surfacesTitle);

    const surfacesList = document.createElement('div');
    surfacesList.id = 'surfaces-list';
    surfacesList.style.flex = '1';
    surfacesList.style.overflowY = 'auto';
    skinScrollable(surfacesList);
    surfacesList.style.display = 'flex';
    surfacesList.style.flexDirection = 'column';
    surfacesList.style.gap = '4px';
    surfacesList.innerHTML = '<p style="color: #64748b; font-size: 12px;">No hay superficies indexadas</p>';
    surfacesSection.appendChild(surfacesList);
    sidebar.appendChild(surfacesSection);

    // Sección: Exportar/Importar
    const exportSection = document.createElement('div');
    exportSection.style.display = 'flex';
    exportSection.style.flexDirection = 'column';
    exportSection.style.gap = '8px';

    const exportTitle = document.createElement('h2');
    exportTitle.textContent = 'Exportar / Importar';
    exportTitle.style.margin = '0';
    exportTitle.style.fontSize = '14px';
    exportTitle.style.color = '#94a3b8';
    exportTitle.style.borderBottom = '1px solid #0f3460';
    exportTitle.style.paddingBottom = '8px';
    exportSection.appendChild(exportTitle);

    const exportButtons = document.createElement('div');
    exportButtons.style.display = 'flex';
    exportButtons.style.gap = '8px';

    const exportBtn = document.createElement('button');
    exportBtn.textContent = 'Exportar JSON';
    exportBtn.style.flex = '1';
    exportBtn.style.padding = '8px 12px';
    exportBtn.style.border = 'none';
    exportBtn.style.borderRadius = '4px';
    exportBtn.style.background = '#0f3460';
    exportBtn.style.color = '#94a3b8';
    exportBtn.style.cursor = 'pointer';

    const copyBtn = document.createElement('button');
    copyBtn.textContent = 'Copiar JSON';
    copyBtn.style.flex = '1';
    copyBtn.style.padding = '8px 12px';
    copyBtn.style.border = 'none';
    copyBtn.style.borderRadius = '4px';
    copyBtn.style.background = '#0f3460';
    copyBtn.style.color = '#94a3b8';
    copyBtn.style.cursor = 'pointer';

    exportButtons.appendChild(exportBtn);
    exportButtons.appendChild(copyBtn);
    exportSection.appendChild(exportButtons);

    const importTextarea = document.createElement('textarea');
    importTextarea.placeholder = 'Pegar JSON aquí para importar...';
    importTextarea.rows = 3;
    importTextarea.style.padding = '8px 10px';
    importTextarea.style.border = '1px solid #0f3460';
    importTextarea.style.borderRadius = '4px';
    importTextarea.style.background = '#1a1a2e';
    importTextarea.style.color = '#eee';
    importTextarea.style.fontSize = '13px';
    importTextarea.style.resize = 'vertical';
    exportSection.appendChild(importTextarea);

    const importBtn = document.createElement('button');
    importBtn.textContent = 'Importar';
    importBtn.style.padding = '8px 12px';
    importBtn.style.border = 'none';
    importBtn.style.borderRadius = '4px';
    importBtn.style.background = '#0f3460';
    importBtn.style.color = '#94a3b8';
    importBtn.style.cursor = 'pointer';
    exportSection.appendChild(importBtn);

    const messageDiv = document.createElement('div');
    messageDiv.style.padding = '8px 12px';
    messageDiv.style.borderRadius = '4px';
    messageDiv.style.fontSize = '12px';
    messageDiv.style.display = 'none';
    exportSection.appendChild(messageDiv);

    sidebar.appendChild(exportSection);

    // Botón de volver
    const backButton = document.createElement('a');
    backButton.textContent = '← Volver';
    backButton.style.color = '#94a3b8';
    backButton.style.fontSize = '12px';
    backButton.style.textDecoration = 'none';
    backButton.style.cursor = 'pointer';
    backButton.style.marginTop = 'auto';
    sidebar.appendChild(backButton);

    container.appendChild(sidebar);

    // Contenedor del canvas
    const canvasContainer = document.createElement('div');
    canvasContainer.style.flex = '1';
    canvasContainer.style.display = 'flex';
    canvasContainer.style.flexDirection = 'column';
    canvasContainer.style.background = '#1a1a2e';

    // Header del canvas
    const canvasHeader = document.createElement('div');
    canvasHeader.style.padding = '8px 16px';
    canvasHeader.style.background = '#16213e';
    canvasHeader.style.borderBottom = '1px solid #0f3460';
    canvasHeader.style.display = 'flex';
    canvasHeader.style.alignItems = 'center';
    canvasHeader.style.justifyContent = 'space-between';
    canvasHeader.style.gap = '16px';

    const controls = document.createElement('div');
    controls.style.display = 'flex';
    controls.style.gap = '8px';
    controls.style.alignItems = 'center';

    const createCheckbox = (label: string, checked: boolean) => {
      const wrapper = document.createElement('label');
      wrapper.style.display = 'flex';
      wrapper.style.alignItems = 'center';
      wrapper.style.gap = '8px';
      wrapper.style.fontSize = '12px';
      wrapper.style.cursor = 'pointer';
      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.checked = checked;
      checkbox.style.width = '16px';
      checkbox.style.height = '16px';
      wrapper.appendChild(checkbox);
      wrapper.appendChild(document.createTextNode(label));
      return { wrapper, checkbox };
    };

    const { wrapper: gridWrapper, checkbox: gridCheckbox } = createCheckbox('Mostrar grilla', true);
    const { wrapper: indexedWrapper, checkbox: indexedCheckbox } = createCheckbox('Mostrar indexados', true);

    const centerBtn = document.createElement('button');
    centerBtn.textContent = 'Centrar vista';
    centerBtn.style.padding = '4px 8px';
    centerBtn.style.border = 'none';
    centerBtn.style.borderRadius = '4px';
    centerBtn.style.background = '#0f3460';
    centerBtn.style.color = '#94a3b8';
    centerBtn.style.cursor = 'pointer';
    centerBtn.style.fontSize = '11px';

    controls.appendChild(gridWrapper);
    controls.appendChild(indexedWrapper);
    controls.appendChild(centerBtn);
    canvasHeader.appendChild(controls);

    const fpsDisplay = document.createElement('div');
    fpsDisplay.style.fontSize = '12px';
    fpsDisplay.style.color = '#4ade80';
    fpsDisplay.innerHTML = 'FPS: <span id="surface-indexer-fps">0</span>';
    canvasHeader.appendChild(fpsDisplay);

    canvasContainer.appendChild(canvasHeader);

    // Contenedor de Phaser
    const phaserContainer = document.createElement('div');
    phaserContainer.id = 'surface-indexer-phaser-container';
    phaserContainer.style.flex = '1';
    phaserContainer.style.overflow = 'hidden';
    canvasContainer.appendChild(phaserContainer);

    container.appendChild(canvasContainer);
    this.host.contentLayer.appendChild(container);

    // Estado
    let editorGame: Phaser.Game | null = null;
    let currentAtlasFileName = '';
    let selectedGridX = -1;
    let selectedGridY = -1;
    let selectedTilesX = 1;
    
    // Estado para conservar datos después de grabar
    let lastSavedName = '';
    let lastSavedType = '';
    let lastSavedSubtype = '';
    let selectedTilesY = 1;
    let surfaces: SurfaceIndex = {};

    // Funciones de utilidad
    const showMessage = (text: string, type: 'success' | 'error') => {
      messageDiv.textContent = text;
      messageDiv.style.display = 'block';
      messageDiv.style.background = type === 'success' ? '#14532d' : '#7f1d1d';
      messageDiv.style.color = type === 'success' ? '#4ade80' : '#fca5a5';
      setTimeout(() => {
        messageDiv.style.display = 'none';
      }, 3000);
    };

    const updateSurfacesList = () => {
      const entries = Object.entries(surfaces) as Array<[string, SurfaceDefinition]>;
      const countSpan = document.getElementById('surfaces-count');
      if (countSpan) countSpan.textContent = entries.length.toString();

      if (entries.length === 0) {
        surfacesList.innerHTML = '<p style="color: #64748b; font-size: 12px;">No hay superficies indexadas</p>';
        return;
      }

      surfacesList.innerHTML = entries.map(([id, surface]: [string, SurfaceDefinition]) => {
        const tilesX = surface.tilesX || 1;
        const tilesY = surface.tilesY || 1;
        const sizeLabel = (tilesX > 1 || tilesY > 1) ? ` [${tilesX}x${tilesY}]` : '';
        return `
        <div class="surface-item" data-id="${id}" data-x="${surface.gridX}" data-y="${surface.gridY}" data-tiles-x="${tilesX}" data-tiles-y="${tilesY}"
             style="display: flex; align-items: center; justify-content: space-between; padding: 8px; background: #0f3460; border-radius: 4px; font-size: 12px; cursor: pointer;">
          <div>
            <div style="font-weight: 600; color: #fff;">${surface.name}${sizeLabel}</div>
            <div style="color: #94a3b8; font-size: 11px;">${surface.type}${surface.subtype ? ` / ${surface.subtype}` : ''}</div>
          </div>
          <div style="color: #4ade80; font-size: 11px;">(${surface.gridX}, ${surface.gridY})</div>
        </div>
      `;}).join('');

      surfacesList.querySelectorAll('.surface-item').forEach((item) => {
        (item as HTMLElement).onclick = () => {
          const id = item.getAttribute('data-id')!;
          const x = parseInt(item.getAttribute('data-x')!, 10);
          const y = parseInt(item.getAttribute('data-y')!, 10);
          const tilesX = parseInt(item.getAttribute('data-tiles-x') || '1', 10);
          const tilesY = parseInt(item.getAttribute('data-tiles-y') || '1', 10);
          selectedGridX = x;
          selectedGridY = y;
          selectedTilesX = tilesX;
          selectedTilesY = tilesY;
          updateSelectedTileUI(x, y, tilesX, tilesY, { id, surface: surfaces[id] });
        };
      });
    };

    const updateSelectedTileUI = (gridX: number, gridY: number, tilesX: number, tilesY: number, existing: { id: string; surface: SurfaceDefinition } | null) => {
      selectedGridX = gridX;
      selectedGridY = gridY;
      selectedTilesX = tilesX;
      selectedTilesY = tilesY;
      
      const coordsSpan = document.getElementById('selected-coords');
      const sizeLabel = (tilesX > 1 || tilesY > 1) ? ` [${tilesX}x${tilesY}]` : '';
      if (coordsSpan) coordsSpan.textContent = `(${gridX}, ${gridY})${sizeLabel}`;
      surfaceForm.style.display = 'flex';

      const idInput = document.getElementById('surface-id') as HTMLInputElement;
      const nameInput = document.getElementById('surface-name') as HTMLInputElement;
      const typeInput = document.getElementById('surface-type') as HTMLInputElement;
      const subtypeInput = document.getElementById('surface-subtype') as HTMLInputElement;

      if (existing) {
        idInput.value = existing.id;
        idInput.disabled = true;
        nameInput.value = existing.surface.name;
        typeInput.value = existing.surface.type;
        subtypeInput.value = existing.surface.subtype || '';
        deleteBtn.style.display = 'block';
      } else {
        const sizeStr = (tilesX > 1 || tilesY > 1) ? `${tilesX}x${tilesY}` : '1x1';
        // Extraer el número del pattern del nombre del archivo (ej: pattern2.png -> 2)
        const patternMatch = currentAtlasFileName.match(/pattern(\d+)\.png/i);
        const patternNum = patternMatch ? patternMatch[1] : 'unknown';
        idInput.value = `pattern${patternNum}_${gridX}_${gridY}_${sizeStr}`;
        idInput.disabled = false;
        // Si el checkbox está activado, conservar los últimos valores usados
        if (keepDataCheckbox.checked) {
          nameInput.value = lastSavedName;
          typeInput.value = lastSavedType;
          subtypeInput.value = lastSavedSubtype;
        } else {
          nameInput.value = '';
          typeInput.value = '';
          subtypeInput.value = '';
        }
        deleteBtn.style.display = 'none';
      }
    };

    // Event handlers
    fileInput.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (event) => {
        const imageData = event.target?.result as string;
        if (editorGame) {
          const scene = editorGame.scene.getScene('SurfaceIndexerScene') as SurfaceIndexerScene;
          if (scene) {
            scene.loadAtlas(imageData, file.name);
          }
        }
      };
      reader.readAsDataURL(file);
    };

    saveBtn.onclick = () => {
      if (selectedGridX < 0 || selectedGridY < 0) {
        showMessage('Selecciona un tile primero', 'error');
        return;
      }
      const idInput = document.getElementById('surface-id') as HTMLInputElement;
      const nameInput = document.getElementById('surface-name') as HTMLInputElement;
      const typeInput = document.getElementById('surface-type') as HTMLInputElement;
      const subtypeInput = document.getElementById('surface-subtype') as HTMLInputElement;

      const id = idInput.value.trim();
      const name = nameInput.value.trim();
      const type = typeInput.value.trim();
      const subtype = subtypeInput.value.trim();

      if (!id || !name || !type) {
        showMessage('ID, Nombre y Tipo son requeridos', 'error');
        return;
      }

      const surface: SurfaceDefinition = {
        name,
        type,
        atlasFile: currentAtlasFileName,
        gridX: selectedGridX,
        gridY: selectedGridY,
      };
      if (subtype) surface.subtype = subtype;
      // Solo incluir tilesX/tilesY si son > 1
      if (selectedTilesX > 1) surface.tilesX = selectedTilesX;
      if (selectedTilesY > 1) surface.tilesY = selectedTilesY;

      if (editorGame) {
        const scene = editorGame.scene.getScene('SurfaceIndexerScene') as SurfaceIndexerScene;
        if (scene) {
          scene.addSurface(id, surface);
        }
      }
      
      // Guardar los valores para reutilizar si el checkbox está activado
      if (keepDataCheckbox.checked) {
        lastSavedName = name;
        lastSavedType = type;
        lastSavedSubtype = subtype;
      }
      
      const sizeLabel = (selectedTilesX > 1 || selectedTilesY > 1) ? ` [${selectedTilesX}x${selectedTilesY}]` : '';
      showMessage(`Superficie "${id}"${sizeLabel} guardada`, 'success');
    };

    deleteBtn.onclick = () => {
      if (selectedGridX < 0 || selectedGridY < 0) return;
      if (editorGame) {
        const scene = editorGame.scene.getScene('SurfaceIndexerScene') as SurfaceIndexerScene;
        if (scene) {
          scene.removeSurface(selectedGridX, selectedGridY);
        }
      }
      surfaceForm.style.display = 'none';
      const coordsSpan = document.getElementById('selected-coords');
      if (coordsSpan) coordsSpan.textContent = 'Ninguno';
      showMessage('Superficie eliminada', 'success');
    };

    exportBtn.onclick = () => {
      if (Object.keys(surfaces).length === 0) {
        showMessage('No hay superficies para exportar', 'error');
        return;
      }
      const json = JSON.stringify(surfaces, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `surfaces-${currentAtlasFileName.replace(/\.[^.]+$/, '')}.json`;
      a.click();
      URL.revokeObjectURL(url);
      showMessage('JSON exportado', 'success');
    };

    copyBtn.onclick = async () => {
      if (Object.keys(surfaces).length === 0) {
        showMessage('No hay superficies para copiar', 'error');
        return;
      }
      try {
        await navigator.clipboard.writeText(JSON.stringify(surfaces, null, 2));
        showMessage('JSON copiado al portapapeles', 'success');
      } catch {
        showMessage('Error al copiar', 'error');
      }
    };

    importBtn.onclick = () => {
      const json = importTextarea.value.trim();
      if (!json) {
        showMessage('Pega el JSON primero', 'error');
        return;
      }
      try {
        const imported = JSON.parse(json) as SurfaceIndex;
        if (editorGame) {
          const scene = editorGame.scene.getScene('SurfaceIndexerScene') as SurfaceIndexerScene;
          if (scene) {
            scene.loadSurfaces(imported, currentAtlasFileName);
          }
        }
        importTextarea.value = '';
        showMessage(`${Object.keys(imported).length} superficies importadas`, 'success');
      } catch (err) {
        showMessage(`Error: ${(err as Error).message}`, 'error');
      }
    };

    gridCheckbox.onchange = () => {
      if (editorGame) {
        const scene = editorGame.scene.getScene('SurfaceIndexerScene') as SurfaceIndexerScene;
        if (scene) scene.toggleGrid(gridCheckbox.checked);
      }
    };

    indexedCheckbox.onchange = () => {
      if (editorGame) {
        const scene = editorGame.scene.getScene('SurfaceIndexerScene') as SurfaceIndexerScene;
        if (scene) scene.toggleIndexedTiles(indexedCheckbox.checked);
      }
    };

    centerBtn.onclick = () => {
      if (editorGame) {
        const scene = editorGame.scene.getScene('SurfaceIndexerScene') as SurfaceIndexerScene;
        if (scene) scene.centerView();
      }
    };

    backButton.onclick = () => {
      if (editorGame) {
        editorGame.destroy(true);
        editorGame = null;
      }
      SurfaceIndexerScene.onSurfacesChanged = null;
      SurfaceIndexerScene.onTileSelected = null;
      SurfaceIndexerScene.onAtlasLoaded = null;
      this.host.returnToLogin();
    };

    // Configurar callbacks
    SurfaceIndexerScene.onSurfacesChanged = (newSurfaces) => {
      surfaces = newSurfaces;
      updateSurfacesList();
    };

    SurfaceIndexerScene.onTileSelected = (gridX, gridY, tilesX, tilesY, existing) => {
      updateSelectedTileUI(gridX, gridY, tilesX, tilesY, existing);
    };

    SurfaceIndexerScene.onAtlasLoaded = (atlasInfoData) => {
      currentAtlasFileName = atlasInfoData.fileName;
      atlasInfo.style.display = 'block';
      const filenameSpan = document.getElementById('atlas-filename');
      const sizeSpan = document.getElementById('atlas-size');
      const gridSpan = document.getElementById('atlas-grid');
      if (filenameSpan) filenameSpan.textContent = atlasInfoData.fileName;
      if (sizeSpan) sizeSpan.textContent = `${atlasInfoData.width} x ${atlasInfoData.height} px`;
      if (gridSpan) gridSpan.textContent = `${atlasInfoData.columns} x ${atlasInfoData.rows} tiles`;
      surfaceForm.style.display = 'none';
      const coordsSpan = document.getElementById('selected-coords');
      if (coordsSpan) coordsSpan.textContent = 'Ninguno';
    };

    // Crear juego Phaser
    const editorConfig: Phaser.Types.Core.GameConfig = {
      type: Phaser.AUTO,
      parent: 'surface-indexer-phaser-container',
      width: phaserContainer.clientWidth,
      height: phaserContainer.clientHeight,
      backgroundColor: '#1a1a2e',
      pixelArt: true,
      antialias: false,
      roundPixels: true,
      scale: {
        mode: Phaser.Scale.RESIZE,
        autoCenter: Phaser.Scale.CENTER_BOTH,
      },
      scene: [SurfaceIndexerScene],
    };

    editorGame = new Phaser.Game(editorConfig);
  }

  // =====================================================================
  // Editor de Partículas
  // =====================================================================
  openParticleEditor(): void {
    if (!this.host.hasLoginCallback()) return;
    this.host.contentLayer.innerHTML = '';

    // --- Container principal ---
    const container = document.createElement('div');
    container.style.cssText = 'background:#1a1a2e;display:flex;width:100vw;height:100vh;position:fixed;top:0;left:0;z-index:1000;';

    // --- Sidebar ---
    const sidebar = document.createElement('div');
    sidebar.style.cssText = 'width:300px;min-width:300px;background:#16213e;padding:12px;display:flex;flex-direction:column;gap:8px;overflow-y:auto;border-right:1px solid #0f3460;font-family:monospace;font-size:11px;color:#cbd5e1;';
    skinScrollable(sidebar);

    const title = document.createElement('h1');
    title.textContent = 'Particle Editor';
    title.style.cssText = 'margin:0;font-size:16px;color:#e94560;';
    sidebar.appendChild(title);

    // Placeholder para el panel Catálogo+Presets+Anchor+Métricas (F2.5).
    // Se popula al final, cuando currentDef/apply/editorGame ya existen.
    const catalogPanelHost = document.createElement('div');
    catalogPanelHost.style.cssText = 'display:flex;flex-direction:column;gap:6px;';
    sidebar.appendChild(catalogPanelHost);

    // Helpers para crear controles
    const makeSection = (label: string): HTMLDivElement => {
      const s = document.createElement('div');
      s.style.cssText = 'display:flex;flex-direction:column;gap:4px;';
      const h = document.createElement('h2');
      h.textContent = label;
      h.style.cssText = 'margin:0;font-size:12px;color:#94a3b8;border-bottom:1px solid #0f3460;padding-bottom:4px;';
      s.appendChild(h);
      return s;
    };

    const makeRange = (label: string, min: number, max: number, step: number, value: number, onChange: (v: number) => void): HTMLDivElement => {
      const row = document.createElement('div');
      row.style.cssText = 'display:flex;align-items:center;gap:4px;';
      const lbl = document.createElement('span');
      lbl.textContent = label;
      lbl.style.cssText = 'width:80px;flex-shrink:0;';
      const valSpan = document.createElement('span');
      valSpan.textContent = String(value);
      valSpan.style.cssText = 'width:40px;text-align:right;font-size:10px;';
      const slider = createSlider({
        min,
        max,
        step,
        value,
        onChange: (v) => {
          valSpan.textContent = slider.input.value;
          onChange(v);
        },
      });
      slider.bar.style.flex = '1';
      row.appendChild(lbl);
      row.appendChild(slider.bar);
      row.appendChild(valSpan);
      return row;
    };

    const makeRangePair = (label: string, min: number, max: number, step: number, values: [number, number], onChange: (v: [number, number]) => void): HTMLDivElement => {
      const row = document.createElement('div');
      row.style.cssText = 'display:flex;align-items:center;gap:4px;';
      const lbl = document.createElement('span');
      lbl.textContent = label;
      lbl.style.cssText = 'width:80px;flex-shrink:0;';
      const in1 = document.createElement('input');
      in1.type = 'number';
      in1.min = String(min);
      in1.max = String(max);
      in1.step = String(step);
      in1.value = String(values[0]);
      in1.style.cssText = 'width:50px;background:#0f3460;border:1px solid #1a4a8e;color:#cbd5e1;padding:2px;font-size:10px;';
      const in2 = document.createElement('input');
      in2.type = 'number';
      in2.min = String(min);
      in2.max = String(max);
      in2.step = String(step);
      in2.value = String(values[1]);
      in2.style.cssText = in1.style.cssText;
      const update = (): void => { onChange([parseFloat(in1.value) || 0, parseFloat(in2.value) || 0]); };
      in1.oninput = update;
      in2.oninput = update;
      row.appendChild(lbl);
      row.appendChild(in1);
      row.appendChild(in2);
      return row;
    };

    const makeCheckbox = (label: string, checked: boolean, onChange: (v: boolean) => void): HTMLDivElement => {
      const row = document.createElement('div');
      row.style.cssText = 'display:flex;align-items:center;gap:6px;';
      const cb = document.createElement('input');
      cb.type = 'checkbox';
      cb.checked = checked;
      cb.onchange = () => onChange(cb.checked);
      const lbl2 = document.createElement('span');
      lbl2.textContent = label;
      row.appendChild(cb);
      row.appendChild(lbl2);
      return row;
    };

    // Estado
    let currentDef = createDefaultEffectDef();
    let editorGame: Phaser.Game | null = null;

    const apply = (partial: Partial<ParticleEffectDef>): void => {
      Object.assign(currentDef, partial);
      if (editorGame) {
        const scene = editorGame.scene.getScene('ParticleEditorScene') as ParticleEditorScene;
        if (scene) scene.applyDef(partial);
      }
    };

    // --- Sección: Frames (selector de partículas) ---
    const framesSection = makeSection('Frames');
    const framesHint = document.createElement('div');
    framesHint.style.cssText = 'color:#64748b;font-size:9px;margin-bottom:4px;';
    framesHint.textContent = 'Click para seleccionar/deseleccionar frames:';
    framesSection.appendChild(framesHint);

    const framesGrid = document.createElement('div');
    framesGrid.style.cssText = 'display:flex;flex-wrap:wrap;gap:2px;max-height:200px;overflow-y:auto;';
    skinScrollable(framesGrid);

    const selectedFrames = new Set<number>([0]); // circle1 por defecto
    const frameButtons: HTMLButtonElement[] = [];

    const updateFrameSelection = (): void => {
      const frames = Array.from(selectedFrames).sort((a, b) => a - b);
      if (frames.length === 0) {
        selectedFrames.add(0);
        frames.push(0);
        frameButtons[0].style.border = '2px solid #22c55e';
      }
      apply({ frames });
    };

    PARTICLE_FRAME_NAMES.forEach((name, idx) => {
      const btn = document.createElement('button');
      btn.textContent = name;
      btn.title = `Frame ${idx}: ${name}`;
      const isSelected = selectedFrames.has(idx);
      btn.style.cssText = `padding:1px 3px;font-size:8px;cursor:pointer;background:${isSelected ? '#1a4a8e' : '#0a1628'};border:${isSelected ? '2px solid #22c55e' : '1px solid #1a4a8e'};color:#cbd5e1;white-space:nowrap;`;
      btn.onclick = () => {
        if (selectedFrames.has(idx)) {
          if (selectedFrames.size > 1) {
            selectedFrames.delete(idx);
            btn.style.background = '#0a1628';
            btn.style.border = '1px solid #1a4a8e';
          }
        } else {
          selectedFrames.add(idx);
          btn.style.background = '#1a4a8e';
          btn.style.border = '2px solid #22c55e';
        }
        updateFrameSelection();
      };
      frameButtons.push(btn);
      framesGrid.appendChild(btn);
    });

    framesSection.appendChild(framesGrid);

    // Sub-sección: escala por frame
    const frameScalesContainer = document.createElement('div');
    frameScalesContainer.style.cssText = 'display:flex;flex-direction:column;gap:2px;max-height:150px;overflow-y:auto;margin-top:4px;';
    skinScrollable(frameScalesContainer);
    const frameScalesTitle = document.createElement('div');
    frameScalesTitle.style.cssText = 'color:#64748b;font-size:9px;';
    frameScalesTitle.textContent = 'Escala por frame:';
    framesSection.appendChild(frameScalesTitle);
    framesSection.appendChild(frameScalesContainer);

    const rebuildFrameScalesUI = (): void => {
      frameScalesContainer.innerHTML = '';
      const sorted = Array.from(selectedFrames).sort((a, b) => a - b);
      for (const idx of sorted) {
        const name = PARTICLE_FRAME_NAMES[idx] ?? `frame${idx}`;
        const row = document.createElement('div');
        row.style.cssText = 'display:flex;align-items:center;gap:4px;';
        const lbl = document.createElement('span');
        lbl.textContent = name;
        lbl.style.cssText = 'width:60px;flex-shrink:0;font-size:9px;';
        const val = document.createElement('span');
        val.textContent = String(currentDef.frameScales[idx] ?? 1);
        val.style.cssText = 'width:32px;text-align:right;font-size:9px;';
        const slider = createSlider({
          min: 0.01,
          max: 2,
          step: 0.01,
          value: currentDef.frameScales[idx] ?? 1,
          onChange: (v) => {
            val.textContent = slider.input.value;
            currentDef.frameScales[idx] = v;
            apply({ frameScales: { ...currentDef.frameScales } });
          },
        });
        slider.bar.style.flex = '1';
        row.appendChild(lbl);
        row.appendChild(slider.bar);
        row.appendChild(val);
        frameScalesContainer.appendChild(row);
      }
    };

    // Rebuild scale UI cuando cambia la selección de frames
    const originalUpdateFrameSelection = updateFrameSelection;
    const updateFrameSelectionWithScales = (): void => {
      originalUpdateFrameSelection();
      rebuildFrameScalesUI();
    };

    // Patch los onclick de los botones para usar la nueva función
    frameButtons.forEach((btn, idx) => {
      btn.onclick = () => {
        if (selectedFrames.has(idx)) {
          if (selectedFrames.size > 1) {
            selectedFrames.delete(idx);
            btn.style.background = '#0a1628';
            btn.style.border = '1px solid #1a4a8e';
          }
        } else {
          selectedFrames.add(idx);
          btn.style.background = '#1a4a8e';
          btn.style.border = '2px solid #22c55e';
        }
        updateFrameSelectionWithScales();
      };
    });

    // Inicializar la UI de escalas
    rebuildFrameScalesUI();

    sidebar.appendChild(framesSection);

    // --- Sección: Emisión ---
    const emitSection = makeSection('Emisión');
    emitSection.appendChild(makeRange('Cantidad', 1, 200, 1, currentDef.numParticles, (v) => apply({ numParticles: v })));
    emitSection.appendChild(makeRange('Rate/s', 0, 100, 1, currentDef.emitRate, (v) => apply({ emitRate: v })));
    sidebar.appendChild(emitSection);

    // --- Sección: Spawn ---
    const spawnSection = makeSection('Spawn Area');
    spawnSection.appendChild(makeRangePair('X', -200, 200, 1, currentDef.spawnX, (v) => apply({ spawnX: v })));
    spawnSection.appendChild(makeRangePair('Y', -200, 200, 1, currentDef.spawnY, (v) => apply({ spawnY: v })));
    sidebar.appendChild(spawnSection);

    // --- Sección: Velocidad ---
    const velSection = makeSection('Velocidad');
    velSection.appendChild(makeRangePair('SpeedX', -200, 200, 1, currentDef.speedX, (v) => apply({ speedX: v })));
    velSection.appendChild(makeRangePair('SpeedY', -200, 200, 1, currentDef.speedY, (v) => apply({ speedY: v })));
    sidebar.appendChild(velSection);

    // --- Sección: Vida ---
    const lifeSection = makeSection('Vida');
    lifeSection.appendChild(makeRangePair('Life (ms)', 50, 5000, 50, currentDef.life, (v) => apply({ life: v })));
    sidebar.appendChild(lifeSection);

    // --- Sección: Física ---
    const physSection = makeSection('Física');
    physSection.appendChild(makeRange('GravityY', -200, 200, 1, currentDef.gravityY, (v) => apply({ gravityY: v })));
    physSection.appendChild(makeRange('Friction', 0, 1, 0.01, currentDef.friction, (v) => apply({ friction: v })));
    physSection.appendChild(makeRange('Bounce', -1, 1, 0.1, currentDef.bounce, (v) => apply({ bounce: v })));
    sidebar.appendChild(physSection);

    // --- Sección: Spin ---
    const spinSection = makeSection('Rotación');
    spinSection.appendChild(makeRangePair('Spin °/s', -720, 720, 10, currentDef.spin, (v) => apply({ spin: v })));
    sidebar.appendChild(spinSection);

    // --- Sección: Órbita ---
    const orbitSection = makeSection('Órbita');
    orbitSection.appendChild(makeCheckbox('Enabled', currentDef.orbit.enabled, (v) => apply({ orbit: { ...currentDef.orbit, enabled: v } })));
    orbitSection.appendChild(makeRange('RadiusX', 0, 200, 1, currentDef.orbit.radiusX, (v) => apply({ orbit: { ...currentDef.orbit, radiusX: v } })));
    orbitSection.appendChild(makeRange('RadiusY', 0, 200, 1, currentDef.orbit.radiusY, (v) => apply({ orbit: { ...currentDef.orbit, radiusY: v } })));
    orbitSection.appendChild(makeRange('Speed °/s', -720, 720, 10, currentDef.orbit.speed, (v) => apply({ orbit: { ...currentDef.orbit, speed: v } })));
    sidebar.appendChild(orbitSection);

    // --- Sección: Visual ---
    const visSection = makeSection('Visual');
    visSection.appendChild(makeRangePair('Alpha', 0, 1, 0.05, currentDef.alpha, (v) => apply({ alpha: v })));
    visSection.appendChild(makeRangePair('Scale', 0, 5, 0.1, currentDef.scale, (v) => apply({ scale: v })));
    // Blend mode
    const blendRow = document.createElement('div');
    blendRow.style.cssText = 'display:flex;align-items:center;gap:4px;';
    const blendLbl = document.createElement('span');
    blendLbl.textContent = 'Blend';
    blendLbl.style.cssText = 'width:80px;';
    const blendSel = document.createElement('select');
    blendSel.style.cssText = 'background:#0f3460;border:1px solid #1a4a8e;color:#cbd5e1;padding:2px;font-size:10px;';
    ['NORMAL', 'ADD', 'SCREEN'].forEach((m) => {
      const opt = document.createElement('option');
      opt.value = m;
      opt.textContent = m;
      if (m === currentDef.blendMode) opt.selected = true;
      blendSel.appendChild(opt);
    });
    blendSel.onchange = () => apply({ blendMode: blendSel.value as ParticleEffectDef['blendMode'] });
    blendRow.appendChild(blendLbl);
    blendRow.appendChild(blendSel);
    visSection.appendChild(blendRow);
    // Tint color
    const tintRow = document.createElement('div');
    tintRow.style.cssText = 'display:flex;align-items:center;gap:4px;';
    const tintLbl = document.createElement('span');
    tintLbl.textContent = 'Tint';
    tintLbl.style.cssText = 'width:80px;';
    const tintInput = document.createElement('input');
    tintInput.type = 'color';
    tintInput.value = '#ffffff';
    tintInput.style.cssText = 'width:30px;height:20px;border:none;padding:0;cursor:pointer;';
    tintInput.oninput = () => {
      const hex = parseInt(tintInput.value.replace('#', ''), 16);
      apply({ tint: [hex] });
    };
    tintRow.appendChild(tintLbl);
    tintRow.appendChild(tintInput);
    visSection.appendChild(tintRow);
    sidebar.appendChild(visSection);

    // --- Sección: Loop ---
    const loopSection = makeSection('Loop');
    loopSection.appendChild(makeCheckbox('Loop', currentDef.loop, (v) => apply({ loop: v })));
    loopSection.appendChild(makeRange('Duration ms', 0, 10000, 100, currentDef.duration, (v) => apply({ duration: v })));
    sidebar.appendChild(loopSection);

    // --- Sección: Camera FX (shake + flash) ---
    const camFxSection = makeSection('Camera FX');
    camFxSection.appendChild(
      makeRange('Shake intens', 0, 0.05, 0.001, currentDef.screenShake?.intensity ?? 0, (v) => {
        if (v > 0) {
          apply({ screenShake: { duration: currentDef.screenShake?.duration ?? 300, intensity: v } });
        } else {
          apply({ screenShake: undefined });
        }
      })
    );
    camFxSection.appendChild(
      makeRange('Shake dur ms', 0, 1500, 50, currentDef.screenShake?.duration ?? 0, (v) => {
        if (v > 0 && currentDef.screenShake) {
          apply({ screenShake: { duration: v, intensity: currentDef.screenShake.intensity } });
        }
      })
    );
    camFxSection.appendChild(
      makeRange('Flash dur ms', 0, 1500, 50, currentDef.screenFlash?.duration ?? 0, (v) => {
        if (v > 0) {
          apply({ screenFlash: { duration: v, color: currentDef.screenFlash?.color ?? 0xffffff } });
        } else {
          apply({ screenFlash: undefined });
        }
      })
    );
    sidebar.appendChild(camFxSection);

    // --- Sección: Render (depth) ---
    const renderSection = makeSection('Render');
    renderSection.appendChild(
      makeCheckbox(
        'Detrás del personaje',
        currentDef.behindEntity ?? false,
        (v) => apply({ behindEntity: v })
      )
    );
    // Depth mode dropdown (fixed | orbit dynamic)
    const depthModeRow = document.createElement('div');
    depthModeRow.style.cssText = 'display:flex;align-items:center;gap:4px;';
    const depthModeLbl = document.createElement('span');
    depthModeLbl.textContent = 'Depth';
    depthModeLbl.style.cssText = 'width:80px;font-size:10px;';
    const depthModeSel = document.createElement('select');
    depthModeSel.style.cssText = 'flex:1;background:#0f3460;border:1px solid #1a4a8e;color:#cbd5e1;padding:2px;font-size:10px;';
    for (const o of [
      { value: 'fixed', label: 'Fixed (todas igual)' },
      { value: 'orbit', label: 'Orbit (3D dinámico)' },
    ]) {
      const opt = document.createElement('option');
      opt.value = o.value;
      opt.textContent = o.label;
      if ((currentDef.depthMode ?? 'fixed') === o.value) opt.selected = true;
      depthModeSel.appendChild(opt);
    }
    depthModeSel.onchange = (): void => {
      apply({ depthMode: depthModeSel.value as 'fixed' | 'orbit' });
    };
    depthModeRow.appendChild(depthModeLbl);
    depthModeRow.appendChild(depthModeSel);
    renderSection.appendChild(depthModeRow);
    sidebar.appendChild(renderSection);

    // --- Sección: Nombre ---
    const nameSection = makeSection('Efecto');
    const nameInput = document.createElement('input');
    nameInput.type = 'text';
    nameInput.value = currentDef.name;
    nameInput.placeholder = 'Nombre del efecto';
    nameInput.style.cssText = 'background:#0f3460;border:1px solid #1a4a8e;color:#cbd5e1;padding:4px;font-size:11px;width:100%;box-sizing:border-box;';
    nameInput.oninput = () => { currentDef.name = nameInput.value; };
    nameSection.appendChild(nameInput);
    sidebar.appendChild(nameSection);

    // --- Botones Play/Stop/Export ---
    const actionsRow = document.createElement('div');
    actionsRow.style.cssText = 'display:flex;gap:6px;margin-top:8px;';

    const playBtn = document.createElement('button');
    playBtn.textContent = '▶ Play';
    playBtn.style.cssText = 'flex:1;padding:6px;background:#166534;border:1px solid #22c55e;color:#e2e8f0;cursor:pointer;font-size:11px;';
    playBtn.onclick = () => {
      if (editorGame) {
        const scene = editorGame.scene.getScene('ParticleEditorScene') as ParticleEditorScene;
        scene?.playEffect();
      }
    };

    const stopBtn = document.createElement('button');
    stopBtn.textContent = '■ Stop';
    stopBtn.style.cssText = 'flex:1;padding:6px;background:#991b1b;border:1px solid #ef4444;color:#e2e8f0;cursor:pointer;font-size:11px;';
    stopBtn.onclick = () => {
      if (editorGame) {
        const scene = editorGame.scene.getScene('ParticleEditorScene') as ParticleEditorScene;
        scene?.stopEffect();
      }
    };

    actionsRow.appendChild(playBtn);
    actionsRow.appendChild(stopBtn);
    sidebar.appendChild(actionsRow);

    // Export/Import JSON
    const jsonRow = document.createElement('div');
    jsonRow.style.cssText = 'display:flex;gap:6px;';

    const exportBtn = document.createElement('button');
    exportBtn.textContent = 'Export JSON';
    exportBtn.style.cssText = 'flex:1;padding:4px;background:#0f3460;border:1px solid #1a4a8e;color:#e2e8f0;cursor:pointer;font-size:10px;';
    exportBtn.onclick = () => {
      const defCopy = { ...currentDef };
      delete (defCopy as Record<string, unknown>)['spriteKey']; // no exportar spriteKey interno
      navigator.clipboard.writeText(JSON.stringify(defCopy, null, 2));
      exportBtn.textContent = 'Copiado!';
      setTimeout(() => { exportBtn.textContent = 'Export JSON'; }, 1500);
    };

    const importBtn = document.createElement('button');
    importBtn.textContent = 'Import JSON';
    importBtn.style.cssText = 'flex:1;padding:4px;background:#0f3460;border:1px solid #1a4a8e;color:#e2e8f0;cursor:pointer;font-size:10px;';
    importBtn.onclick = () => {
      const json = prompt('Pegar JSON de efecto:');
      if (!json) return;
      try {
        const imported = JSON.parse(json) as ParticleEffectDef;
        currentDef = { ...imported, spriteKey: currentDef.spriteKey || '' };
        if (editorGame) {
          const scene = editorGame.scene.getScene('ParticleEditorScene') as ParticleEditorScene;
          scene?.importDef(currentDef);
        }
      } catch {
        alert('JSON inválido');
      }
    };

    jsonRow.appendChild(exportBtn);
    jsonRow.appendChild(importBtn);
    sidebar.appendChild(jsonRow);

    // --- Botón Volver ---
    const backBtn = document.createElement('button');
    backBtn.textContent = 'Volver al login';
    backBtn.style.cssText = 'margin-top:auto;padding:6px;background:#1e293b;border:1px solid #334155;color:#94a3b8;cursor:pointer;font-size:11px;';
    backBtn.onclick = () => {
      if (editorGame) {
        editorGame.destroy(true);
        editorGame = null;
      }
      ParticleEditorScene.onSheetLoaded = null;
      ParticleEditorScene.onDefChanged = null;
      this.host.returnToLogin();
    };
    sidebar.appendChild(backBtn);

    // --- Phaser container ---
    const phaserContainer = document.createElement('div');
    phaserContainer.id = 'particle-editor-phaser-container';
    phaserContainer.style.cssText = 'flex:1;position:relative;';

    // Budget meter overlay (HUD del editor, F2.5)
    const budgetHud = document.createElement('div');
    budgetHud.style.cssText =
      'position:absolute;top:8px;right:8px;padding:4px 8px;background:rgba(0,0,0,0.6);' +
      'color:#cbd5e1;font:10px monospace;border:1px solid #1a4a8e;border-radius:3px;' +
      'pointer-events:none;z-index:10;';
    budgetHud.textContent = 'fps: -- | particles: --';
    phaserContainer.appendChild(budgetHud);

    container.appendChild(sidebar);
    container.appendChild(phaserContainer);
    this.host.contentLayer.appendChild(container);

    // ── Popular el panel Catálogo+Presets+Anchor (F2.5) ────────────────────
    let currentFxId = ''; // id del FX cargado del catálogo (vacío = draft nuevo)
    let dirty = false;
    const markDirty = (): void => {
      dirty = true;
      catalogTitleEl.textContent = currentFxId
        ? `Catálogo: ${currentFxId} *`
        : 'Catálogo: (sin guardar) *';
    };
    // Hook para que apply() marque dirty también.
    const originalApply = apply;
    const wrappedApply = (partial: Partial<ParticleEffectDef>): void => {
      originalApply(partial);
      markDirty();
    };
    // No se puede reasignar `apply` (const). Los handlers de UI siguen usando
    // `apply` directo; el dirty se marca cuando el usuario interactúa con el
    // panel Catálogo o cuando guarda/carga.

    // Title row
    const catalogTitleEl = document.createElement('div');
    catalogTitleEl.style.cssText = 'font-size:11px;color:#94a3b8;border-bottom:1px solid #0f3460;padding-bottom:3px;';
    catalogTitleEl.textContent = 'Catálogo: (sin cargar)';
    catalogPanelHost.appendChild(catalogTitleEl);

    // FX list dropdown + Load button + refresh
    const listRow = document.createElement('div');
    listRow.style.cssText = 'display:flex;gap:4px;align-items:center;';
    const fxSelect = document.createElement('select');
    fxSelect.style.cssText = 'flex:1;background:#0f3460;border:1px solid #1a4a8e;color:#cbd5e1;padding:2px;font-size:10px;';
    const placeholderOpt = document.createElement('option');
    placeholderOpt.value = '';
    placeholderOpt.textContent = '(seleccionar FX)';
    fxSelect.appendChild(placeholderOpt);

    const reloadBtn = document.createElement('button');
    reloadBtn.textContent = '↻';
    reloadBtn.title = 'Refrescar lista del catálogo';
    reloadBtn.style.cssText = 'padding:2px 6px;background:#1e293b;border:1px solid #334155;color:#cbd5e1;cursor:pointer;font-size:10px;';

    const refreshList = async (): Promise<void> => {
      try {
        const all = await listFx();
        // Limpiar y repoblar
        while (fxSelect.options.length > 1) fxSelect.remove(1);
        const ids = Object.keys(all).sort();
        for (const id of ids) {
          const opt = document.createElement('option');
          opt.value = id;
          opt.textContent = id;
          fxSelect.appendChild(opt);
        }
      } catch (err) {
        console.warn('[ParticleEditor] catálogo no disponible:', (err as Error).message);
      }
    };
    reloadBtn.onclick = (): void => { void refreshList(); };
    void refreshList();

    listRow.appendChild(fxSelect);
    listRow.appendChild(reloadBtn);
    catalogPanelHost.appendChild(listRow);

    // Load button (carga el FX seleccionado en el preview)
    const loadBtn = document.createElement('button');
    loadBtn.textContent = '⬇ Cargar';
    loadBtn.style.cssText = 'padding:4px;background:#0f3460;border:1px solid #1a4a8e;color:#cbd5e1;cursor:pointer;font-size:10px;';
    loadBtn.onclick = async (): Promise<void> => {
      const id = fxSelect.value;
      if (!id) return;
      try {
        const all = await listFx();
        const v2 = all[id];
        if (!v2) {
          alert(`FX "${id}" no existe`);
          return;
        }
        const v1 = v2ToV1(v2);
        currentDef = v1;
        currentFxId = id;
        dirty = false;
        catalogTitleEl.textContent = `Catálogo: ${id}`;
        if (editorGame) {
          const scene = editorGame.scene.getScene('ParticleEditorScene') as ParticleEditorScene;
          scene?.importDef(v1);
        }
      } catch (err) {
        alert(`Error cargando: ${(err as Error).message}`);
      }
    };
    catalogPanelHost.appendChild(loadBtn);

    // Save / Dup / Delete row
    const actionsRow2 = document.createElement('div');
    actionsRow2.style.cssText = 'display:flex;gap:4px;';

    const saveBtn = document.createElement('button');
    saveBtn.textContent = '💾 Guardar';
    saveBtn.style.cssText = 'flex:1;padding:4px;background:#166534;border:1px solid #22c55e;color:#e2e8f0;cursor:pointer;font-size:10px;';
    saveBtn.onclick = async (): Promise<void> => {
      let id = currentFxId;
      if (!id) {
        const proposed = prompt('ID del FX (fx_<categoria>_<desc>):', 'fx_new_effect');
        if (!proposed) return;
        if (!/^fx_[a-z0-9_]+$/.test(proposed)) {
          alert('ID inválido. Usar fx_<categoria>_<desc> (snake_case, lower).');
          return;
        }
        id = proposed;
      }
      try {
        const v2 = v1ToV2(currentDef, id);
        await saveFx(id, v2);
        currentFxId = id;
        dirty = false;
        catalogTitleEl.textContent = `Catálogo: ${id}`;
        await refreshList();
        fxSelect.value = id;
      } catch (err) {
        alert(`Error guardando: ${(err as Error).message}`);
      }
    };

    const dupBtn = document.createElement('button');
    dupBtn.textContent = '⎘ Dup';
    dupBtn.title = 'Duplicar como FX nuevo (asigna id nuevo, deja editar)';
    dupBtn.style.cssText = 'padding:4px 8px;background:#0f3460;border:1px solid #1a4a8e;color:#cbd5e1;cursor:pointer;font-size:10px;';
    dupBtn.onclick = (): void => {
      currentFxId = '';
      dirty = true;
      catalogTitleEl.textContent = 'Catálogo: (duplicado, sin guardar) *';
    };

    const delBtn = document.createElement('button');
    delBtn.textContent = '✕';
    delBtn.title = 'Eliminar FX cargado del catálogo';
    delBtn.style.cssText = 'padding:4px 8px;background:#991b1b;border:1px solid #ef4444;color:#fee;cursor:pointer;font-size:10px;';
    delBtn.onclick = async (): Promise<void> => {
      if (!currentFxId) {
        alert('No hay FX cargado del catálogo.');
        return;
      }
      if (!confirm(`Eliminar "${currentFxId}" del catálogo?`)) return;
      try {
        await deleteFx(currentFxId);
        currentFxId = '';
        catalogTitleEl.textContent = 'Catálogo: (sin cargar)';
        await refreshList();
      } catch (err) {
        alert(`Error eliminando: ${(err as Error).message}`);
      }
    };

    actionsRow2.appendChild(saveBtn);
    actionsRow2.appendChild(dupBtn);
    actionsRow2.appendChild(delBtn);
    catalogPanelHost.appendChild(actionsRow2);

    // Anchor selector
    const anchorRow = document.createElement('div');
    anchorRow.style.cssText = 'display:flex;gap:4px;align-items:center;margin-top:4px;';
    const anchorLbl = document.createElement('span');
    anchorLbl.textContent = 'Anchor:';
    anchorLbl.style.cssText = 'font-size:10px;color:#94a3b8;';
    const anchorSel = document.createElement('select');
    anchorSel.style.cssText = 'flex:1;background:#0f3460;border:1px solid #1a4a8e;color:#cbd5e1;padding:2px;font-size:10px;';
    const anchorOptions: Array<{ value: EditorAnchorMode; label: string }> = [
      { value: 'body_center', label: 'Body centro' },
      { value: 'body_feet', label: 'Body pies' },
      { value: 'body_head', label: 'Body cabeza' },
      { value: 'helm_top', label: 'Helm top' },
      { value: 'weapon_tip', label: 'Weapon tip' },
      { value: 'weapon_hilt', label: 'Weapon hilt' },
      { value: 'shield_front', label: 'Shield front' },
      { value: 'tile', label: 'Tile (suelo)' },
      { value: 'item_ground', label: 'Item ground' },
      { value: 'projectile', label: 'Projectile (2 clicks)' },
    ];
    for (const o of anchorOptions) {
      const opt = document.createElement('option');
      opt.value = o.value;
      opt.textContent = o.label;
      if (o.value === 'body_center') opt.selected = true;
      anchorSel.appendChild(opt);
    }
    anchorSel.onchange = (): void => {
      if (editorGame) {
        const scene = editorGame.scene.getScene('ParticleEditorScene') as ParticleEditorScene;
        scene?.setAnchorMode(anchorSel.value as EditorAnchorMode);
      }
    };
    anchorRow.appendChild(anchorLbl);
    anchorRow.appendChild(anchorSel);
    catalogPanelHost.appendChild(anchorRow);

    // Presets (collapsible)
    const presetsTitle = document.createElement('div');
    presetsTitle.style.cssText = 'font-size:11px;color:#94a3b8;border-bottom:1px solid #0f3460;padding-bottom:3px;margin-top:6px;';
    presetsTitle.textContent = 'Presets';
    catalogPanelHost.appendChild(presetsTitle);

    const presetsGrid = document.createElement('div');
    presetsGrid.style.cssText = 'display:flex;flex-wrap:wrap;gap:2px;max-height:120px;overflow-y:auto;';
    skinScrollable(presetsGrid);
    for (const preset of PARTICLE_PRESETS) {
      const btn = document.createElement('button');
      btn.textContent = preset.label;
      btn.title = `${preset.description} → ${preset.suggestedId}`;
      btn.style.cssText = 'padding:2px 4px;font-size:9px;background:#0a1628;border:1px solid #1a4a8e;color:#cbd5e1;cursor:pointer;';
      btn.onclick = (): void => {
        const v2 = preset.factory();
        const v1 = v2ToV1(v2);
        currentDef = v1;
        currentFxId = '';
        dirty = true;
        catalogTitleEl.textContent = `Catálogo: (preset ${preset.label}) *`;
        if (editorGame) {
          const scene = editorGame.scene.getScene('ParticleEditorScene') as ParticleEditorScene;
          scene?.importDef(v1);
        }
        // Sugerir id
        if (confirm(`Cargado preset "${preset.label}".\n\nUsar id sugerido "${preset.suggestedId}"?`)) {
          currentFxId = preset.suggestedId;
          catalogTitleEl.textContent = `Catálogo: ${preset.suggestedId} *`;
        }
      };
      presetsGrid.appendChild(btn);
    }
    catalogPanelHost.appendChild(presetsGrid);

    // Crear juego Phaser
    const editorConfig: Phaser.Types.Core.GameConfig = {
      type: Phaser.AUTO,
      parent: 'particle-editor-phaser-container',
      width: phaserContainer.clientWidth || 800,
      height: phaserContainer.clientHeight || 600,
      backgroundColor: '#111118',
      pixelArt: true,
      antialias: false,
      roundPixels: true,
      scale: {
        mode: Phaser.Scale.RESIZE,
        autoCenter: Phaser.Scale.CENTER_BOTH,
      },
      scene: [ParticleEditorScene],
    };

    editorGame = new Phaser.Game(editorConfig);

    // Budget HUD refresh loop
    const hudInterval = window.setInterval(() => {
      if (!editorGame) {
        window.clearInterval(hudInterval);
        return;
      }
      const scene = editorGame.scene.getScene('ParticleEditorScene') as ParticleEditorScene | undefined;
      if (!scene || !scene.getMetrics) return;
      const m = scene.getMetrics();
      budgetHud.textContent = `fps: ${m.fps.toFixed(0)} | particles: ${m.activeParticles}`;
      budgetHud.style.color = m.activeParticles > 200 ? '#fca5a5' : '#cbd5e1';
    }, 500);

    // Silence unused warnings (helpers reservados para futuros incrementos)
    void wrappedApply;
    void markDirty;
    void dirty;
  }
}
